// =============================================================================
// SIREPPAR - Página de Detalle de Expediente
// =============================================================================

import { useMemo, useState, useEffect } from 'react';
import { useParams, Link, useSearchParams, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import Modal from '../../components/common/Modal';
import RichTextEditor, { MarkdownDisplay } from '../../components/common/RichTextEditor';
import TextoColapsable from '../../components/common/TextoColapsable';
import { PREVIEW_OBSERVACION } from '../../utils/limites';
import { expedienteService, usuarioService, documentoService, interaccionService, partidoService, api, reconocimientoService } from '../../services/api';
import ResumenFinancieroExpediente from './expediente/ResumenFinancieroExpediente';
import ReconocimientoFasePanel from './ReconocimientoFasePanel';
import { dimensionPorDefecto } from '../../utils/etapaVerificacion';
import { accionesReconocimiento, filtrarAccionesPorRol, getFaseActual, ETAPAS_RECONOCIMIENTO, etapaIndexDeFase, faseLabel, faseDescripcion, getPlazoAfiliados, textoPlazoAfiliados, esEtapaAfiliados, esEtapaAutoridades, alcanzoEtapaAfiliados, alcanzoEtapaAutoridades, esTramoProvisorio, type ReconAccion } from '../../utils/fasesReconocimiento';
import VersionesPdfList from './expediente/VersionesPdfList';
import { useAuth } from '../../contexts/AuthContext';
import { puedeEscribirInteracciones } from '../../utils/permisosInteracciones';
import SubirDocumentoRespuestaModal from '../../components/common/SubirDocumentoRespuestaModal';
import { tipoDocLabels, DOCS_OBLIGATORIOS_BASE, DOCS_OBLIGATORIOS_EXTRA, obligatoriosPorPartido, DOCS_OBLIGATORIOS_RECONOCIMIENTO, DOCS_OBLIGATORIOS_RECONOCIMIENTO_AFILIADOS, DOCS_OBLIGATORIOS_RECONOCIMIENTO_AUTORIDADES, DOCS_OBLIGATORIOS_ALIANZA, TIPOS_CANDIDATURA, TIPOS_RESPUESTA_AGRUPACION } from '../../utils/tiposDocumento';
import AdjuntosInforme, { MAX_MB_TOTAL_OBSERVACION, superaTotalAdjuntos } from '../../components/common/AdjuntosInforme';
import { bloqueVerificacionDocumental, aplicarVerificacionDocumental, type ItemVerificacion } from '../../utils/verificacionDocumental';
import { conEncabezadoInforme } from '../../utils/informeEncabezado';
import { accionPendienteSecretario } from '../../utils/accionPendienteSecretario';
import PanelInformes from './expediente/PanelInformes';
import TableroDocumentacion from './expediente/TableroDocumentacion';
import type { RequisitosAvance, Requisito } from './expediente/PanelRequisitosAvance';
import { TIPO_TRAMITE_LABELS } from '../../utils/tiposExpediente';
import { sanitizarTextoLive } from '../../utils/sanitizarTexto';
import { tramiteIniciado, AVISO_TRAMITE_SIN_INICIAR } from '../../utils/tramiteIniciado';
import ModalInformeSecretario from '../../components/common/ModalInformeSecretario';
import ModalInformeTep from '../../components/common/ModalInformeTep';
import ModalPublicarAtributos from '../../components/common/ModalPublicarAtributos';
import ModalInformeSimple, { type InformeSimpleConfig } from '../../components/common/ModalInformeSimple';
import {
  textoInformeInicioTramite,
  textoInformeElevarSecretarioAtributos,
  textoPublicacionAtributos,
} from '../../utils/textosInformesAtributos';
import ModalResolucionTep, { type ResolucionTepConfig } from '../../components/common/ModalResolucionTep';
import ModalAprobarReempSecretario from '../../components/common/ModalAprobarReempSecretario';
import ModalOpcionesAccion, { type OpcionAccion } from '../../components/common/ModalOpcionesAccion';
import PanelIntegrantesAlianza from './expediente/PanelIntegrantesAlianza';
import { useAgrupaciones } from '../../hooks/useAgrupaciones';
import { puedeUsarConsultas } from '../../utils/consultas';
import { usePuedeCrearConsulta } from '../../hooks/usePuedeCrearConsulta';
import DatosAlianzaTramite from './expediente/DatosAlianzaTramite';
import PanelSeccionesDocumentacion from './expediente/PanelSeccionesDocumentacion';
import ResumenAfiliadosExpediente from './expediente/ResumenAfiliadosExpediente';
import { alianzaGestionService, seccionesDocumentoService, observacionCheckService } from '../../services/api';
import {
  getNoPresentado,
  sufijoBadgeNoPresentado,
  textoInformeNoPresentado,
  textoInformeNoPresentadoAfiliados,
  textoInformeNoPresentadoAutoridades,
  textoInformeTepNoPresentado,
  type SubFaseNoPresentado,
} from '../../utils/noPresentado';
import {
  textoInformeSecretarioReemp,
  textoInformeTepReemp,
  textoTepApruebaReemp,
  textoTepRechazaReemp,
  textoTepAplazaReemp,
  type SeccionInforme,
} from '../../utils/reempadronamiento';
import {
  textoInformeTepAlianza,
  textoTepApruebaAlianza,
  textoTepRechazaAlianza,
  textoTepAplazaAlianza,
} from '../../utils/resolucionAlianza';
import { etiquetaSeccion, GRUPOS_TIPOS_DOCUMENTO } from '../../utils/tiposDocumento';

// Configuración del modal de resolución del TEP por endpoint (reutiliza el mismo
// modal de carga de acta para atributos y documentación / provisorio).
const RESOLUCIONES_TEP: Record<string, ResolucionTepConfig> = {
  'tep-aprueba-atributos': {
    endpoint: 'tep-aprueba-atributos', titulo: 'TEP aprueba atributos', requiereMotivo: false, tono: 'ok',
    successMsg: 'Aprobación del TEP registrada', confirmMsg: '', confirmLabel: 'Registrar aprobación',
  },
  'tep-rechaza-atributos': {
    endpoint: 'tep-rechaza-atributos', titulo: 'TEP rechaza atributos', requiereMotivo: true, tono: 'bad',
    successMsg: 'Rechazo del TEP registrado', confirmMsg: '', confirmLabel: 'Registrar rechazo',
  },
  'tep-otorga-documentacion': {
    endpoint: 'tep-otorga-documentacion', titulo: 'TEP otorga reconocimiento provisorio', requiereMotivo: false, tono: 'ok',
    successMsg: 'Reconocimiento provisorio otorgado', confirmMsg: '¿Registrar que el TEP otorgó el reconocimiento provisorio?', confirmLabel: 'Registrar otorgamiento',
    motivoInicial: 'El Tribunal Electoral Provincial otorga el reconocimiento provisorio a la agrupación, conforme a la documentación presentada (Art. 18/19, Ley XII N° 23).',
  },
  'tep-deniega-documentacion': {
    endpoint: 'tep-deniega-documentacion', titulo: 'TEP deniega documentación', requiereMotivo: false, tono: 'bad',
    successMsg: 'Denegación del TEP registrada', confirmMsg: '', confirmLabel: 'Registrar denegación',
    motivoInicial: 'El Tribunal Electoral Provincial deniega la documentación presentada por la agrupación, conforme a los fundamentos que constan en el acta.',
  },
  // Último paso del trámite: el TEP resuelve el reconocimiento DEFINITIVO.
  'tep-aprueba-definitivo': {
    endpoint: 'tep-aprueba-definitivo', titulo: 'TEP otorga el reconocimiento definitivo', requiereMotivo: false, tono: 'ok',
    successMsg: 'Reconocimiento definitivo otorgado',
    confirmMsg:
      '¿Registrar que el TEP otorgó el RECONOCIMIENTO DEFINITIVO?\n\n' +
      'El expediente y el partido quedan VIGENTES, y se le registran al partido la fecha y el acta ' +
      'de su reconocimiento. Se notifica al partido.',
    confirmLabel: 'Registrar otorgamiento',
    motivoInicial: 'El Tribunal Electoral Provincial otorga el reconocimiento definitivo a la agrupación, que queda inscripta como partido político en el Registro de Partidos Políticos de la Provincia del Chubut (Ley XII N° 23).',
  },
  'tep-rechaza-definitivo': {
    endpoint: 'tep-rechaza-definitivo', titulo: 'TEP rechaza el reconocimiento definitivo', requiereMotivo: true, tono: 'bad',
    successMsg: 'Rechazo del TEP registrado',
    confirmMsg:
      '¿Registrar que el TEP rechazó el reconocimiento definitivo?\n\n' +
      'El partido conserva el reconocimiento provisorio que ya tenía.',
    confirmLabel: 'Registrar rechazo',
    motivoInicial: 'El Tribunal Electoral Provincial rechaza el reconocimiento definitivo de la agrupación, conforme a los fundamentos que constan en el acta.',
  },
};

// Las tres salidas del TEP en reempadronamiento, detrás del botón único
// "Resolución TEP". Cada una explica su consecuencia: es lo que un botón en la
// barra del encabezado no alcanza a decir (sobre todo el aplazamiento, que no
// cierra el trámite sino que lo devuelve a análisis).
const OPCIONES_RESOLUCION_TEP_REEMP: OpcionAccion[] = [
  {
    key: 'tep_aprueba_reemp',
    label: 'El TEP aprueba',
    descripcion: 'Otorga el reconocimiento. El expediente y el partido quedan vigentes.',
    tono: 'ok',
  },
  {
    key: 'tep_rechaza_reemp',
    label: 'El TEP rechaza',
    descripcion: 'El expediente queda rechazado. El partido no cambia de estado.',
    tono: 'bad',
  },
  {
    key: 'tep_aplaza_reemp',
    label: 'El TEP aplaza',
    descripcion:
      'El expediente vuelve a análisis para que la supervisora curse la observación con lo que el ' +
      'Tribunal determinó. El plazo del aplazo corre desde esa observación.',
    tono: 'warn',
  },
];

// Las tres salidas del TEP en la constitución de alianza. Se declaran aparte de
// las de reempadronamiento y no se comparten: son las mismas TRES DECISIONES pero
// con consecuencias distintas —acá lo que queda vigente es la alianza, no un
// partido, y el aplazo devuelve el expediente a la SEP para que observe a varias
// agrupaciones a la vez—, y el texto de cada opción es justamente lo que el
// Secretario lee antes de elegir.
const OPCIONES_RESOLUCION_TEP_ALIANZA: OpcionAccion[] = [
  {
    key: 'tep_aprueba_alianza',
    label: 'El TEP aprueba',
    descripcion: 'Oficializa la alianza. El expediente y la alianza quedan vigentes.',
    tono: 'ok',
  },
  {
    key: 'tep_rechaza_alianza',
    label: 'El TEP rechaza',
    descripcion: 'El expediente queda rechazado. La alianza no cambia de estado.',
    tono: 'bad',
  },
  {
    key: 'tep_aplaza_alianza',
    label: 'El TEP aplaza',
    descripcion:
      'El expediente vuelve a análisis para que la supervisora curse la observación con lo que el ' +
      'Tribunal determinó. El plazo del aplazo corre desde esa observación.',
    tono: 'warn',
  },
];

// Las DOS salidas del TEP en el circuito "dado por no presentado". Se declaran
// aparte de las de reempadronamiento y alianza aunque compartan el modal: acá el
// Tribunal no aprueba nada —el expediente llegó por no haberse subsanado—, así
// que la lista tiene dos opciones y no tres, y lo que cada una explica es
// distinto: el rechazo cierra el expediente SIN tocar al partido, y el aplazo
// reabre el plazo vencido (a diferencia del aplazo de los otros dos circuitos,
// que espera una observación nueva).
const OPCIONES_RESOLUCION_TEP_NO_PRESENTADO: OpcionAccion[] = [
  {
    key: 'tep_rechaza_no_presentado',
    label: 'El TEP rechaza',
    descripcion:
      'El expediente queda rechazado. El partido no cambia de estado: declarar su caducidad es un ' +
      'acto aparte.',
    tono: 'bad',
  },
  {
    key: 'tep_aplaza_no_presentado',
    label: 'El TEP aplaza',
    descripcion: 'El expediente vuelve a su etapa anterior y se reabre el plazo para que el partido subsane.',
    tono: 'warn',
  },
];

// =============================================================================
// Tipos - Coincidentes con el backend
// =============================================================================

interface Expediente {
  id: string;
  numero: string;
  tipo: string;
  asunto: string;
  descripcion?: string;
  estado: string;
  fechaCreacion: string;
  fechaPresentacion?: string;
  fechaVencimiento?: string;
  fechaResolucion?: string;
  resolucionNumero?: string;
  resolucionTexto?: string;
  datosFormulario?: any;
  partido?: { 
    id: string; 
    numero?: number; 
    denominacion: string; 
    sigla?: string;
    tipo?: string;
    // Necesaria para nombrar la ciudad en los informes de un partido MUNICIPAL.
    localidad?: string | null;
  };
  asignado?: { 
    id: string; 
    nombre: string; 
    apellido: string;
  };
  creador?: { 
    id: string; 
    nombre: string; 
    apellido: string;
    dni?: string;
    email?: string;
    telefono?: string;
  };
  documentos?: Array<{
    id: string;
    tipo: string;
    titulo: string;
    nombreOriginal: string;
    creadoEn: string;
    estadoRevisionAdmisibilidad?: string;
    estadoRevisionAnalisis?: string;
    observacionRespuestaId?: string | null;
    intimacionRespuestaId?: string | null;
    /** Versión vigente del documento. Una superada no se muestra ni traba nada. */
    esVersionActual?: boolean;
    activo?: boolean;
  }>;
  movimientos?: Array<{
    id: string;
    accion: string;
    estadoAnterior?: string;
    estadoNuevo?: string;
    comentario?: string;
    fecha: string;
    usuario?: { nombre: string; apellido: string };
  }>;
  observaciones?: Array<{
    id: string;
    texto: string;
    estado: string;
    subsanada: boolean;
    creadoEn: string;
    plazoDias?: number;
    fechaLimite?: string;
    destinatario?: string;
    articulo?: string;
    respuestaTexto?: string;
    respuestaFecha?: string;
    tipo?: string;
    // Etapa en la que se cursó. En RECONOCIMIENTO es el único dato que distingue
    // una intimación (atributos) de una observación (documentación/afiliados),
    // porque el `tipo` se guarda siempre como OBSERVACION.
    etapaPlazo?: string;
    adjuntos?: Array<{ id: string; nombreOriginal: string; mimeType: string }>;
  }>;
}

// =============================================================================
// Constantes
// =============================================================================

const estadoConfig: Record<string, { color: string; bg: string; label: string }> = {
  BORRADOR: { color: 'text-gray-800', bg: 'bg-gray-100', label: 'Borrador' },
  PRESENTADO: { color: 'text-blue-800', bg: 'bg-blue-100', label: 'Presentado' },
  EN_ANALISIS: { color: 'text-yellow-800', bg: 'bg-yellow-100', label: 'En Análisis' },
  EN_ADMISIBILIDAD: { color: 'text-purple-800', bg: 'bg-purple-100', label: 'En Control de Admisibilidad' },
  OBSERVADO: { color: 'text-orange-800', bg: 'bg-orange-100', label: 'Observado' },
  APROBADO: { color: 'text-green-800', bg: 'bg-green-100', label: 'Aprobado' },
  RECHAZADO: { color: 'text-red-800', bg: 'bg-red-100', label: 'Rechazado' },
  ARCHIVADO: { color: 'text-purple-800', bg: 'bg-purple-100', label: 'Archivado' },
  // Legacy: nombre anterior de EN_SECRETARIO. Se conserva para movimientos viejos.
  PENDIENTE_RESOLUCION: { color: 'text-indigo-800', bg: 'bg-indigo-100', label: 'En Secretario' },
  EN_SECRETARIO: { color: 'text-indigo-800', bg: 'bg-indigo-100', label: 'En Secretario' },
  INTIMADO: { color: 'text-orange-800', bg: 'bg-orange-100', label: 'Intimado' },
  // Rojo, no gris: NO_PRESENTADO no es un expediente archivado, es uno que entró al
  // circuito de resolución por no haber subsanado en plazo.
  NO_PRESENTADO: { color: 'text-red-800', bg: 'bg-red-100', label: 'No Presentado' },
  EN_TEP: { color: 'text-cyan-800', bg: 'bg-cyan-100', label: 'En el TEP' },
  CADUCO: { color: 'text-gray-600', bg: 'bg-gray-200', label: 'Caduco' },
  EXTINGUIDO: { color: 'text-red-800', bg: 'bg-red-100', label: 'Extinguido' },
  VIGENTE: { color: 'text-green-800', bg: 'bg-green-100', label: 'Vigente' },
  REC_PROVISORIO: { color: 'text-teal-800', bg: 'bg-teal-100', label: 'Reconocimiento provisorio' },
};

const tipoLabels = TIPO_TRAMITE_LABELS;

// Ambito territorial de la agrupacion (`Partido.tipo`). Es ORTOGONAL a la clase:
// una confederacion provincial tiene tipo PROVINCIAL y clase CONFEDERACION.
// Apoderado de la agrupación tal como lo devuelve GET /partidos/:id/autoridades.
// `usuario` es la cuenta de sistema vinculada por DNI: es a ESE correo al que llegan
// las notificaciones del trámite, y puede no coincidir con el `email` declarado.
interface ApoderadoPartido {
  id: string;
  dni: string;
  nombre: string;
  apellido: string;
  tipo?: string | null;
  email?: string | null;
  telefono?: string | null;
  activo?: boolean;
  esPromotor?: boolean;
  esIniciador?: boolean;
  fechaDesignacion?: string | null;
  usuario?: { id: string; email: string; rol?: string; estado?: string } | null;
}

const ambitoPartidoLabels: Record<string, string> = {
  MUNICIPAL: 'Municipal',
  DISTRITO: 'Distrital',
  PROVINCIAL: 'Provincial',
  NACIONAL: 'Nacional',
};

// Transiciones permitidas según el estado actual
const TIPOS_SIN_ADMISIBILIDAD = ['RENDICION_ANUAL', 'RENDICION_CAMPANA'];

// El comentario de un movimiento puede traer anexada la lista de destinatarios
// notificados por mail, con el formato: "<texto>\n\n📧 Notificaciones: ✅ a@b, ❌ c@d".
// Separamos el texto de la lista para poder mostrarla de forma prolija en el historial.
const parseMovimientoComentario = (raw?: string) => {
  if (!raw) return { texto: '', notificaciones: [] as Array<{ ok: boolean; email: string }> };
  const marker = '📧 Notificaciones:';
  const idx = raw.indexOf(marker);
  if (idx === -1) return { texto: raw.trim(), notificaciones: [] };
  const texto = raw.slice(0, idx).trim();
  const notificaciones = raw
    .slice(idx + marker.length)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => ({
      ok: s.startsWith('✅'),
      email: s.replace(/^✅|^❌/, '').trim(),
    }));
  return { texto, notificaciones };
};

const getAccionesDisponibles = (
  estado: string,
  rol: string,
  opts: {
    tipo?: string;
    tieneRespuestaPendiente?: boolean;
    plazoVencidoSinResponder?: boolean;
    // Subfase del circuito "no presentado" (ver utils/noPresentado.ts). Define qué
    // botones se ofrecen mientras el expediente está en NO_PRESENTADO.
    subFaseNoPresentado?: SubFaseNoPresentado | null;
  } = {},
) => {
  const acciones: Array<{ key: string; label: string; color: string }> = [];

  // MESA_ENTRADAS es un rol interno pero de SOLO LECTURA: puede ver la gestión
  // del expediente, pero no ejecuta ninguna acción/transición de estado.
  const puedeAccionar = ['ADMINISTRADOR', 'SUPERVISOR', 'ANALISTA', 'AUTORIDAD_FIRMANTE'].includes(rol);
  const isExterno = ['APODERADO_FIRMANTE', 'PRESIDENTE_FIRMANTE', 'CARGADOR'].includes(rol);
  // Dar por no presentada una solicitud y elevar el informe es de la supervisora.
  const esSupervision = ['ADMINISTRADOR', 'SUPERVISOR'].includes(rol);
  // Elevar al TEP y registrar lo que el TEP resolvió es del Secretario Electoral.
  // SUPERVISOR es equivalente al Secretario (AUTORIDAD_FIRMANTE): mismas acciones
  // de decisión y todo el circuito. El gate real es ROLES_HEREDADOS del backend.
  const esSecretario = ['ADMINISTRADOR', 'AUTORIDAD_FIRMANTE', 'SUPERVISOR'].includes(rol);
  // El informe al Secretario lo redacta quien analiza el expediente.
  const esRevision = ['ADMINISTRADOR', 'SUPERVISOR', 'ANALISTA'].includes(rol);
  // El reempadronamiento tiene su propio circuito de resolución (informe al
  // Secretario → TEP → aprueba/rechaza), igual que el reconocimiento.
  const esReempadronamiento = opts.tipo === 'REEMPADRONAMIENTO';
  // La constitución de alianza tiene su propio circuito de resolución: la SEP
  // eleva al Secretario, el Secretario eleva al TEP y el TEP resuelve, sí o sí.
  // El Secretario NO dicta resolución: su única salida hacia adelante es elevar.
  const esAlianza = opts.tipo === 'CONSTITUCION_ALIANZA';

  switch (estado) {
    case 'BORRADOR':
      if (isExterno) acciones.push({ key: 'presentar', label: 'Presentar', color: 'bg-blue-600' });
      break;
    case 'PRESENTADO':
    case 'EN_ADMISIBILIDAD':
      // ALIANZA: un solo botón, el mismo de la bandeja. Aprueba la admisibilidad
      // en bloque y pasa a análisis — el wizard ya validó la documentación del
      // Art. 27 antes de dejar presentar, así que no hay un control formal
      // separado que intimar.
      if (esAlianza) {
        if (esRevision) {
          acciones.push({ key: 'iniciar_analisis_alianza', label: 'Iniciar', color: 'bg-purple-600' });
        }
        break;
      }
      if (estado === 'PRESENTADO') {
        if (puedeAccionar) {
          acciones.push({ key: 'iniciar_admisibilidad', label: 'Iniciar', color: 'bg-purple-600' });
        }
        break;
      }
      if (puedeAccionar) {
        acciones.push({ key: 'aprobar_admisibilidad', label: 'Pasar a análisis', color: 'bg-green-600' });
        acciones.push({ key: 'intimar', label: 'Intimar', color: 'bg-orange-600' });
      }
      break;
    case 'INTIMADO':
      if (puedeAccionar) {
        // MISMA acción que en EN_ADMISIBILIDAD: "Pasar a análisis". Antes acá había
        // un "Aceptar subsanación" aparte, que hacía la misma transición con otro
        // nombre y obligaba a pensar el trámite como dos caminos distintos según si
        // se había intimado o no. El backend ya aceptaba las dos etapas.
        acciones.push({ key: 'aprobar_admisibilidad', label: 'Pasar a análisis', color: 'bg-green-600' });
        acciones.push({ key: 'intimar', label: 'Re-intimar', color: 'bg-orange-600' });
        // Venció el plazo de la intimación y el partido nunca subsanó: la
        // supervisora la tiene por no presentada y eleva el informe al Secretario.
        if (opts.plazoVencidoSinResponder && esSupervision) {
          acciones.push({ key: 'no_presentado', label: 'No presentado', color: 'bg-red-700' });
        }
      }
      break;
    case 'EN_ANALISIS':
      if (esAlianza) {
        if (esRevision) {
          acciones.push({ key: 'observar', label: 'Observar', color: 'bg-orange-600' });
          acciones.push({ key: 'elevar_secretario_alianza', label: 'Elevar al Secretario', color: 'bg-indigo-600' });
        }
        break;
      }
      if (puedeAccionar) acciones.push({ key: 'observar', label: 'Observar', color: 'bg-orange-600' });
      // Reempadronamiento: el informe al Secretario se eleva desde acá (antes
      // era un botón suelto dentro del tab de Observaciones, sin modal).
      if (esReempadronamiento && esRevision) {
        acciones.push({ key: 'informe_secretario_reemp', label: 'Informe al Secretario', color: 'bg-indigo-600' });
      }
      break;
    case 'OBSERVADO':
      if (esAlianza) {
        // Sin "Aceptar subsanación": en alianzas la observación se cierra una por
        // una con su check en la tab Observaciones, porque pueden responder varias
        // agrupaciones y no todas se subsanan a la vez.
        if (esRevision) {
          acciones.push({ key: 'observar', label: 'Re-observar', color: 'bg-orange-600' });
          // ELEVAR TAMBIÉN DESDE OBSERVADO. Si la agrupación nunca contesta, la
          // observación no se puede dar por subsanada —no hay nada que subsanar— y
          // sin este botón el expediente quedaría trabado para siempre. Se eleva con
          // las observaciones sin responder adjuntas y decide el Secretario.
          acciones.push({ key: 'elevar_secretario_alianza', label: 'Elevar al Secretario', color: 'bg-indigo-600' });
        }
        // SIN "No presentado": en alianzas ese circuito se eliminó. El expediente
        // con el plazo vencido se eleva igual, con las observaciones sin responder
        // adjuntas, y si corresponde lo rechaza el TEP. Tener dos botones que
        // hacían el mismo informe con distinta etiqueta obligaba a la supervisora a
        // anticipar la decisión del Tribunal, que no es suya.
        break;
      }
      if (puedeAccionar) {
        // SIN "Aceptar subsanación": cerraba TODAS las observaciones de golpe, que es
        // justo lo que el check por observación vino a evitar —una puede estar
        // subsanada y otra no—. Ahora cada una se cierra con su check en la tab
        // Observaciones, y al cerrarse la última el expediente vuelve solo a
        // EN_ANALISIS (ver resolverObservacion). Mismo criterio que en alianzas.
        //
        // Ojo: el "Aceptar subsanación" de ADMISIBILIDAD sí queda. Ése hace otra
        // transición —INTIMADO → EN_ADMISIBILIDAD— que el check no cubre.
        acciones.push({ key: 'observar', label: 'Re-observar', color: 'bg-orange-600' });
        // ELEVAR TAMBIÉN DESDE OBSERVADO, igual que en alianzas. Si el partido nunca
        // contesta, la observación no se puede dar por subsanada —no hay nada que
        // subsanar— y sin esto el expediente quedaba trabado. Se eleva y decide el
        // Secretario; el informe dice qué quedó sin cumplir.
        if (esReempadronamiento && esRevision) {
          acciones.push({ key: 'informe_secretario_reemp', label: 'Informe al Secretario', color: 'bg-indigo-600' });
        }
        // Solo cuando venció el plazo de la observación y el partido no la respondió
        if (opts.plazoVencidoSinResponder && esSupervision) {
          acciones.push({ key: 'no_presentado', label: 'No presentado', color: 'bg-red-700' });
        }
      }
      break;
    // A resolución del Secretario Electoral. (PENDIENTE_RESOLUCION era el nombre
    // anterior de este mismo estado; se deja para expedientes sin migrar.)
    case 'EN_SECRETARIO':
    case 'PENDIENTE_RESOLUCION':
      // Alianza: el Secretario NO resuelve, sólo ELEVA. La constitución de alianza
      // la dicta el Tribunal en todos los casos, así que acá no hay un botón verde
      // de aprobación: la única salida hacia adelante es el TEP, y la única hacia
      // atrás es devolver el expediente a la SEP.
      if (esAlianza) {
        if (esSecretario) {
          acciones.push({ key: 'elevar_tep_alianza', label: 'Elevar al TEP', color: 'bg-cyan-700' });
          acciones.push({ key: 'devolver_sep_alianza', label: 'Volver atrás', color: 'bg-amber-600' });
        }
        break;
      }
      // Reempadronamiento: el Secretario RESUELVE cuando el expediente está
      // correcto —aprueba y otorga el reconocimiento con su propia resolución— y
      // solo eleva al TEP los casos que no lo están. El resto de los trámites
      // mantiene Aprobar/Rechazar directo.
      //
      // "Aprobar" va primero y en verde porque es el camino normal. NO se
      // deshabilita por lo que falte: si el expediente está correcto lo dice el
      // Secretario, que leyó el informe de la SEP (el informe declara los
      // faltantes, no los bloquea). Si no está de acuerdo, sus salidas son
      // "Elevar al TEP" o "Volver atrás".
      if (esReempadronamiento) {
        if (esSecretario) {
          acciones.push({ key: 'aprobar_reemp', label: 'Aprobar', color: 'bg-green-600' });
          acciones.push({ key: 'elevar_tep_reemp', label: 'Elevar al TEP', color: 'bg-cyan-700' });
          // Si el Secretario no está de acuerdo, lo devuelve al analista con un
          // informe a la SEP en vez de elevarlo.
          acciones.push({ key: 'devolver_sep_reemp', label: 'Volver atrás', color: 'bg-amber-600' });
        }
        break;
      }
      if (puedeAccionar) acciones.push({ key: 'aprobar', label: 'Aprobar', color: 'bg-green-600' });
      if (puedeAccionar) acciones.push({ key: 'rechazar', label: 'Rechazar', color: 'bg-red-600' });
      break;
    // A resolución del TEP: se registra el acta.
    case 'EN_TEP':
      // Alianza: mismo criterio de un solo botón que reempadronamiento. Las tres
      // salidas viven adentro del modal de opciones, porque ninguna es "la normal"
      // y el aplazo —que devuelve el expediente a análisis— no cabe en la etiqueta
      // de un botón de la barra.
      if (esAlianza) {
        if (esSecretario) {
          acciones.push({ key: 'resolucion_tep_alianza', label: 'Resolución TEP', color: 'bg-cyan-700' });
          // Deshacer la elevación (el TEP todavía no resolvió): vuelve al Secretario.
          acciones.push({ key: 'devolver_sep_alianza', label: 'Volver atrás', color: 'bg-amber-600' });
        }
        break;
      }
      if (esReempadronamiento && esSecretario) {
        // UN SOLO botón con las tres salidas adentro (aprueba / rechaza / aplaza).
        // La tarea es una —registrar lo que el TEP resolvió—; tres botones sueltos
        // se leían como tres tareas pendientes, y ninguno alcanzaba a explicar su
        // consecuencia (el aplazamiento devuelve el expediente a análisis, y eso
        // no cabe en la etiqueta de un botón).
        acciones.push({ key: 'resolucion_tep_reemp', label: 'Resolución TEP', color: 'bg-cyan-700' });
        // Deshacer la elevación (el TEP todavía no resolvió).
        acciones.push({ key: 'devolver_sep_reemp', label: 'Volver atrás', color: 'bg-amber-600' });
      }
      break;
    // NO_PRESENTADO ya no es terminal: es el circuito de resolución que se abrió
    // porque el partido nunca subsanó. Los botones dependen de quién tiene la
    // pelota (subfase), no del estado.
    case 'NO_PRESENTADO':
      // ALIANZA: sin acciones. El circuito NO_PRESENTADO se eliminó de alianzas y
      // el backend ya no acepta ese estado en ninguno de sus endpoints (ni
      // devolver-sep ni elevar-tep). Se corta acá a propósito en lugar de dejar
      // caer el expediente en las ramas genéricas de abajo: ésas apuntan al
      // circuito /no-presentado, que resuelve por fuera de las reglas de la
      // alianza y daría botones que fallan al pulsarlos.
      if (esAlianza) break;
      if (opts.subFaseNoPresentado === 'EN_SECRETARIO' && esSecretario) {
        acciones.push({ key: 'elevar_tep_no_presentado', label: 'Elevar al TEP', color: 'bg-cyan-700' });
        // El Secretario no comparte el criterio: devuelve a la SEP y el expediente
        // vuelve a su etapa anterior, cerrando el circuito.
        acciones.push({ key: 'devolver_sep_no_presentado', label: 'Volver atrás', color: 'bg-amber-600' });
      }
      if (opts.subFaseNoPresentado === 'EN_TEP' && esSecretario) {
        // Mismo criterio que reempadronamiento y alianza: UN solo botón con las
        // salidas adentro. La tarea es una —registrar lo que el TEP resolvió— y dos
        // botones sueltos se leían como dos pendientes distintos, sin que ninguna
        // etiqueta alcanzara a decir su consecuencia.
        acciones.push({ key: 'resolucion_tep_no_presentado', label: 'Resolución TEP', color: 'bg-cyan-700' });
        acciones.push({ key: 'devolver_sep_no_presentado', label: 'Volver atrás', color: 'bg-amber-600' });
      }
      break;
    case 'APROBADO':
    case 'RECHAZADO':
      // Un rechazo de alianza no es una puerta cerrada: el Secretario puede
      // reabrirlo para que la SEP observe y vuelva a analizar. Sin notificar.
      if (esAlianza) {
        if (esSecretario) {
          acciones.push({ key: 'devolver_sep_alianza', label: 'Volver atrás', color: 'bg-amber-600' });
        }
        break;
      }
      if (puedeAccionar) acciones.push({ key: 'archivar', label: 'Archivar', color: 'bg-gray-600' });
      break;
    // Alianza reconocida: no quedan acciones sobre el expediente.
    case 'VIGENTE':
      break;
  }

  return acciones;
};

// =============================================================================
// Componente
// =============================================================================

interface AdjuntoRespuesta {
  id: string;
  tipo: string;
  titulo: string;
  estadoRevision: string;
  nombreOriginal?: string;
}

function RespuestaObservacion({ expedienteId, observacionId, partidoId, onSuccess, textoInicial, corregirHref, modoSEP, nombrePartido, tiposPermitidos, labelOverrides, esAlianza }: {
  expedienteId: string;
  observacionId: string;
  partidoId?: string;
  onSuccess: () => void;
  textoInicial?: string;
  corregirHref?: string;
  modoSEP?: boolean;
  nombrePartido?: string;
  tiposPermitidos?: string[];
  labelOverrides?: Record<string, string>;
  // Constitución de alianza: la respuesta va por su propio endpoint, que ACUMULA
  // (no pisa) lo que ya contestaron las otras agrupaciones de la alianza.
  esAlianza?: boolean;
}) {
  // En modo SEP el supervisor carga la respuesta en nombre del partido (llegó
  // por fuera del sistema). El prefijo queda fijo y no se puede borrar; el
  // supervisor sólo agrega el detalle.
  const prefijoSEP = `Respuesta cargada por la SEP en nombre del partido (${nombrePartido || 'partido'}).`;
  const [texto, setTexto] = useState(textoInicial ?? '');
  // Si viene texto pre-cargado (desde re-presentación), arrancamos con el formulario abierto.
  const [mostrarFormulario, setMostrarFormulario] = useState(!!textoInicial);
  const [adjuntos, setAdjuntos] = useState<AdjuntoRespuesta[]>([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showConfirmSinAdjuntos, setShowConfirmSinAdjuntos] = useState(false);
  const [enviando, setEnviando] = useState(false);

  const verDocumento = async (docId: string) => {
    try {
      const response = await api.get(`/documentos/${docId}/url`);
      const url = response.data.data?.url || response.data.url;
      if (url) window.open(url, '_blank');
    } catch {
      toast.error('No se pudo abrir el documento');
    }
  };

  const removeAdjunto = (docId: string) => {
    setAdjuntos(adjuntos.filter(a => a.id !== docId));
  };

  const handleEnviar = async () => {
    const detalle = texto.trim();
    // En modo SEP el prefijo basta como respuesta; en modo partido exigimos texto.
    if (!modoSEP && !detalle) return;
    // Recordatorio: el caso típico es que se escriba la respuesta y se olvide
    // adjuntar el documento requerido. Confirmamos antes de enviar sin adjuntos.
    if (adjuntos.length === 0) {
      setShowConfirmSinAdjuntos(true);
      return;
    }
    await enviarRespuesta();
  };

  const enviarRespuesta = async () => {
    setShowConfirmSinAdjuntos(false);
    const detalle = texto.trim();
    const textoFinal = modoSEP
      ? `${prefijoSEP}${detalle ? ' ' + detalle : ''}`
      : detalle;
    setEnviando(true);
    try {
      if (esAlianza) {
        await alianzaGestionService.responderObservacion(
          observacionId,
          textoFinal,
          adjuntos.map(a => a.id),
        );
      } else {
        await api.post(`/expedientes/${expedienteId}/observaciones/${observacionId}/responder`, {
          texto: textoFinal,
          documentoIds: adjuntos.map(a => a.id),
        });
      }
      toast.success('Respuesta enviada correctamente');
      setTexto('');
      setAdjuntos([]);
      onSuccess();
    } catch (err: any) {
      toast.error(err.response?.data?.error?.message || 'Error al enviar respuesta');
    } finally {
      setEnviando(false);
    }
  };

  if (!mostrarFormulario) {
    return (
      <div className="pl-8">
        <div className="flex flex-wrap gap-2">
          {corregirHref && (
            <Link
              to={corregirHref}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Corregir rendición
            </Link>
          )}
          <button
            type="button"
            onClick={() => setMostrarFormulario(true)}
            className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg ${
              corregirHref
                ? 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M3 14h18m-7 4h7M3 6h18" />
            </svg>
            {modoSEP ? 'Responder en nombre del partido' : corregirHref ? 'Solo responder' : 'Responder'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pl-8">
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium text-gray-700">
            {modoSEP ? 'Registrar respuesta del partido (recibida fuera del sistema):' : 'Escribir respuesta:'}
          </p>
          {!textoInicial && (
            <button
              type="button"
              onClick={() => { setMostrarFormulario(false); setTexto(''); setAdjuntos([]); }}
              className="text-xs text-gray-500 hover:text-gray-700 underline"
            >
              Cancelar
            </button>
          )}
        </div>
        {modoSEP && (
          <div className="bg-gray-100 border border-gray-300 rounded-lg p-2 text-sm text-gray-600 italic">
            {prefijoSEP}
          </div>
        )}
        <RichTextEditor
          value={texto}
          onChange={setTexto}
          placeholder={modoSEP ? 'Detalle (opcional): aclaraciones sobre la respuesta recibida...' : 'Describa las correcciones realizadas...'}
          minHeight={120}
        />
        <div>
          <label className="block text-sm font-medium text-gray-600 mb-2">Adjuntar documentos (opcional)</label>
          <div className="space-y-2">
            {adjuntos.map((adj) => (
              <div key={adj.id} className="flex items-center gap-2 p-2 bg-white rounded border text-sm">
                <span className="inline-flex items-center px-2 py-0.5 rounded bg-blue-50 text-blue-700 text-xs font-medium flex-shrink-0">
                  {tipoDocLabels[adj.tipo] || adj.tipo}
                </span>
                <button
                  type="button"
                  onClick={() => verDocumento(adj.id)}
                  className="flex-1 text-left text-blue-700 hover:text-blue-900 hover:underline truncate"
                  title={adj.titulo}
                >
                  {adj.titulo}
                </button>
                <button
                  type="button"
                  onClick={() => removeAdjunto(adj.id)}
                  className="text-red-500 hover:text-red-700 flex-shrink-0"
                  title="Quitar de la respuesta (el archivo queda guardado en Documentos)"
                >
                  ✕
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => setShowUploadModal(true)}
              className="inline-flex items-center gap-2 px-3 py-2 border border-dashed rounded-lg hover:bg-white text-sm text-gray-600"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
              </svg>
              Subir documento
            </button>
          </div>
        </div>
        <div className="flex justify-end">
          <button
            onClick={handleEnviar}
            disabled={(!modoSEP && !texto.trim()) || enviando}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
          >
            {enviando ? 'Enviando...' : modoSEP ? 'Registrar respuesta' : 'Enviar Respuesta'}
          </button>
        </div>
      </div>

      <SubirDocumentoRespuestaModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        expedienteId={expedienteId}
        partidoId={partidoId}
        tiposPermitidos={tiposPermitidos}
        labelOverrides={labelOverrides}
        onUploaded={(doc) => setAdjuntos((prev) => [...prev, doc])}
      />

      {showConfirmSinAdjuntos && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-bold mb-2 text-gray-900">¿No deseás adjuntar la documentación solicitada?</h3>
            <p className="text-sm text-gray-600 mb-5">
              Estás por enviar la respuesta sin adjuntar ningún documento. Si la observación
              requería documentación, podés agregarla antes de continuar.
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowConfirmSinAdjuntos(false)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm font-medium"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={enviarRespuesta}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
              >
                Continuar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


const TimelineEstado = ({ estadoActual, tipo }: { estadoActual: string; tipo?: string }) => {
  // El reempadronamiento no "finaliza": termina con el partido VIGENTE (o CADUCO
  // si el TEP lo rechaza). El resto de los trámites cierra en Aprobado/Rechazado.
  const esReempadronamiento = tipo === 'REEMPADRONAMIENTO';
  const esCandidatura = tipo === 'CANDIDATURA';
  const pasoFinal = esReempadronamiento
    ? { key: 'VIGENTE', label: 'Vigente' }
    : esCandidatura
      ? { key: 'EN_TEP', label: 'En el TEP' }
      : { key: 'APROBADO', label: 'Finalizado' };
  const pasos = esCandidatura
    ? [
        { key: 'PRESENTADO', label: 'Presentado' },
        { key: 'EN_ANALISIS', label: 'En Análisis' },
        { key: 'OBSERVADO', label: 'Observado' },
        { key: 'EN_SECRETARIO', label: 'En Secretario' },
        pasoFinal,
      ]
    : [
        { key: 'PRESENTADO', label: 'Presentado' },
        { key: 'EN_ADMISIBILIDAD', label: 'Admisibilidad' },
        { key: 'INTIMADO', label: 'Intimado' },
        { key: 'EN_ANALISIS', label: 'En Análisis' },
        { key: 'OBSERVADO', label: 'Observado' },
        { key: 'EN_SECRETARIO', label: 'Resolución' },
        pasoFinal,
      ];

  // Estados que no son un paso propio pero caen dentro de uno.
  const estadoParaStepper =
    // EN_TEP y NO_PRESENTADO viven en la etapa de Resolución, igual que en la
    // bandeja /gestion: el expediente sigue esperando que alguien resuelva.
    (estadoActual === 'EN_TEP' && !esCandidatura) || estadoActual === 'NO_PRESENTADO' ? 'EN_SECRETARIO' :
    // Nombre anterior de EN_SECRETARIO, para expedientes sin migrar.
    estadoActual === 'PENDIENTE_RESOLUCION' ? 'EN_SECRETARIO' :
    // Cualquier cierre (bueno o malo) se pinta en el último paso.
    ['ARCHIVADO', 'RECHAZADO', 'APROBADO', 'VIGENTE', 'CADUCO', 'EXTINGUIDO'].includes(estadoActual)
      ? pasoFinal.key
      : estadoActual;
  const idxActual = pasos.findIndex(p => p.key === estadoParaStepper);
  const esRechazado = ['RECHAZADO', 'CADUCO', 'EXTINGUIDO'].includes(estadoActual);
  // Trámite cerrado con éxito: el último paso también va en verde, no en naranja
  // (el naranja significa "acá está parado, falta resolver").
  const completado = esReempadronamiento || esCandidatura
    ? estadoActual === 'VIGENTE'
    : estadoActual === 'APROBADO';
  return (
    <div>
      <div className="flex items-center">
        {pasos.map((paso, idx) => (
          <div key={paso.key} className="flex items-center flex-1 last:flex-none">
            <div className="flex flex-col items-center">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium border-2 ${
                esRechazado && idx === idxActual ? 'bg-red-100 border-red-500 text-red-700' :
                idx < idxActual || (completado && idx === idxActual) ? 'bg-green-500 border-green-500 text-white' :
                idx === idxActual ? 'bg-orange-100 border-orange-500 text-orange-700' :
                'bg-gray-50 border-gray-300 text-gray-400'
              }`}>
                {idx < idxActual || (completado && idx === idxActual) ? '✓' : idx + 1}
              </div>
              <span className={`text-xs mt-1 whitespace-nowrap ${
                completado && idx === idxActual ? 'font-medium text-green-700' :
                idx === idxActual ? 'font-medium text-orange-700' :
                idx < idxActual ? 'text-green-600' : 'text-gray-400'
              }`}>{paso.label}</span>
            </div>
            {idx < pasos.length - 1 && (
              <div className={`flex-1 h-0.5 mx-1 mb-4 ${idx < idxActual ? 'bg-green-400' : 'bg-gray-200'}`} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// Plazos para responder una observación o intimación, en TODOS los trámites: 1 a 15
// días, más 30 y 60.
//
// El tramo de 1 a 15 es el día a día: el piso es 1 porque cuando falta un solo
// documento no tiene sentido dar dos semanas. Los dos saltos de arriba son los casos
// que no se resuelven con papeleo: 30 días cuando el partido tiene que reunir un
// órgano para subsanar (es el plazo de OBSERVACIONES_DOC_REUNION en el backend) y 60
// cuando hace falta convocar una asamblea o reformar la carta orgánica.
//
// No hay valores intermedios entre 15 y 30, ni entre 30 y 60, a propósito: son
// plazos institucionales, no una escala continua.
const PLAZOS_OBSERVACION = [...Array.from({ length: 15 }, (_, i) => i + 1), 30, 60];
export default function ExpedienteDetailPage() {
  const { id } = useParams<{ id: string }>();
  const verSimbolo = async (indice: number) => {
    try {
      const response = await api.get(`/expedientes/${id}/simbolos/${indice}`, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(response.data);
      window.open(blobUrl, '_blank');
    } catch {
      toast.error('No se pudo abrir el símbolo');
    }
  };
  const verDniIniciador = async (lado: 'frente' | 'dorso') => {
    try {
      const response = await api.get(`/expedientes/${id}/iniciador/dni-${lado}`, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(response.data);
      window.open(blobUrl, '_blank');
    } catch {
      toast.error('No se pudo abrir el documento del iniciador');
    }
  };
  const { user } = useAuth();
  const queryClient = useQueryClient();
  // #389: agrupación activa, para el botón "Hacer una consulta" en expedientes de alianza.
  const agrupaciones = useAgrupaciones();
  // Iniciar consultas: apoderados firmantes e iniciadores (lo decide el backend).
  const { puedeCrear: puedeCrearConsulta } = usePuedeCrearConsulta();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const focusParam = searchParams.get('focus');
  const setTab = (tab: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    next.delete('focus');
    setSearchParams(next, { replace: true });
  };
  const [obsTexto, setObsTexto] = useState('');
  const [obsOrigen, setObsOrigen] = useState<string | undefined>(undefined);
  const location = useLocation();
  const [obsPlazoDias, setObsPlazoDias] = useState(15);
  const [obsRenovarPlazo, setObsRenovarPlazo] = useState(false);
  // 0 en el desplegable = "Sin plazo": observación normal, sin fecha límite.
  const obsSinPlazo = obsPlazoDias === 0;
  const [obsAdjuntos, setObsAdjuntos] = useState<File[]>([]);

  // Modales
  const [showAsignarModal, setShowAsignarModal] = useState(false);
  const [showChecklistObs, setShowChecklistObs] = useState(false);
  const [checklistObsText, setChecklistObsText] = useState('');
  const [showObservarModal, setShowObservarModal] = useState(false);
  const [showAprobarModal, setShowAprobarModal] = useState(false);
  const [showRechazarModal, setShowRechazarModal] = useState(false);
  const [showSubsanarModal, setShowSubsanarModal] = useState(false);
  // Modal de "Aceptar subsanación": exige que la supervisora haya revisado las
  // respuestas del partido antes de cambiar de estado. tipo = etapa de revisión.
  // Modal de "Pasar a análisis". Reemplazó al de "Aceptar subsanación", que hacía la
  // misma transición con otro nombre.
  const [pasarAnalisisAbierto, setPasarAnalisisAbierto] = useState(false);
  const [showInformeSecretario, setShowInformeSecretario] = useState(false);
  const [showInformeTep, setShowInformeTep] = useState(false);
  const [showPublicarAtributos, setShowPublicarAtributos] = useState(false);
  const [informeSimpleConfig, setInformeSimpleConfig] = useState<InformeSimpleConfig | null>(null);
  const [resolucionTepConfig, setResolucionTepConfig] = useState<ResolucionTepConfig | null>(null);
  const [aprobarReempAbierto, setAprobarReempAbierto] = useState(false);
  // Lo que se refresca después de registrar una resolución (TEP o Secretario).
  const refrescarTrasResolucion = () => {
    queryClient.invalidateQueries({ queryKey: ['expediente', id] });
    queryClient.invalidateQueries({ queryKey: ['gestion-expedientes'] });
    // El partido pasa a VIGENTE al aprobarse: hay que refrescar el
    // expediente del apoderado, que es de donde salen el banner de
    // felicitaciones y el gating del menú externo.
    queryClient.invalidateQueries({ queryKey: ['mi-expediente'] });
    queryClient.invalidateQueries({ queryKey: ['partidos'] });
    // Las de alianzas. Se invalidan SIEMPRE, aunque el expediente sea de otro
    // trámite: una key que no tiene query montada es un no-op, y preguntar por
    // el tipo acá sólo agrega una rama que se puede olvidar de actualizar.
    //
    // 'mis-alianzas' es la que importa de verdad: sin ella, el banner del
    // portal externo y el cartel de felicitaciones de la alianza siguen
    // mostrando el estado anterior después de que el TEP la oficializó.
    queryClient.invalidateQueries({ queryKey: ['alianza-expediente', id] });
    queryClient.invalidateQueries({ queryKey: ['bandeja-alianzas'] });
    queryClient.invalidateQueries({ queryKey: ['bandeja-alianzas-contadores'] });
    queryClient.invalidateQueries({ queryKey: ['mis-alianzas'] });
    queryClient.invalidateQueries({ queryKey: ['dashboard-candidatura-estado'] });
  };

  // Chooser de las tres salidas del TEP en reempadronamiento (ver 'resolucion_tep_reemp').
  const [opcionesTepReemp, setOpcionesTepReemp] = useState(false);
  // El mismo chooser para alianzas. Es un booleano APARTE y no uno compartido con
  // el de reempadronamiento porque cada uno alimenta su propia lista de opciones:
  // con una sola bandera, el modal tendría que decidir qué opciones mostrar a
  // partir del tipo de expediente y el chooser dejaría de ser un componente tonto.
  const [opcionesTepAlianza, setOpcionesTepAlianza] = useState(false);
  // Y el del circuito de "no presentado", por la misma razón: su lista es la única
  // de dos opciones y con consecuencias propias.
  const [opcionesTepNoPresentado, setOpcionesTepNoPresentado] = useState(false);
  const [subsanarTexto, setSubsanarTexto] = useState('');
  const [subsanarArchivos, setSubsanarArchivos] = useState<Array<{ file: File; documentoId: string }>>([]);

  // Formularios
  const [analistaId, setAnalistaId] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [resolucionNumero, setResolucionNumero] = useState('');
  const [resolucionTexto, setResolucionTexto] = useState('');
  const [motivoRechazo, setMotivoRechazo] = useState('');

  // Query expediente
  const { data: expediente, isLoading, error } = useQuery({
    queryKey: ['expediente', id],
    queryFn: async () => {
      const response = await expedienteService.getById(id!, { include: 'all' });
      return response.data.data as Expediente;
    },
    enabled: !!id,
  });

  // Para rendiciones la tab por defecto es "financiero"; para los otros tipos, "docs"
  const esRendicion = expediente?.tipo === 'RENDICION_ANUAL' || expediente?.tipo === 'RENDICION_CAMPANA';
  // Provisional: en Reconocimiento se muestra la pestaña "Atributos" y se oculta "Documentación".
  // TODO: reemplazar por el estado real de atributos cuando exista el flujo del acta del TEP.
  const atributosPendientes = expediente?.tipo === 'RECONOCIMIENTO';

  // --- Visibilidad de Afiliados / Adherentes para el partido (externo) --------
  // El partido no ve estas pestañas hasta que el trámite llega a la etapa de
  // documentación: Adherentes desde EN_ANALISIS y Afiliados desde REC_PROVISORIO.
  // En RECONOCIMIENTO el circuito corre por fase, así que también la miramos.
  const ESTADOS_DESDE_ANALISIS = [
    'EN_ANALISIS', 'OBSERVADO', 'EN_SECRETARIO', 'PENDIENTE_RESOLUCION', 'APROBADO',
    'REC_PROVISORIO', 'VIGENTE', 'CADUCO', 'EXTINGUIDO',
  ];
  const ESTADOS_DESDE_PROVISORIO = ['REC_PROVISORIO', 'VIGENTE', 'CADUCO', 'EXTINGUIDO'];
  const faseGating = expediente?.tipo === 'RECONOCIMIENTO' ? getFaseActual(expediente.datosFormulario) : null;

  // --- ALIANZA: detalle propio ---------------------------------------------
  // El expediente genérico no trae integrantes, órganos ni apoderados de la
  // alianza: eso vive en `partidos` y sus tablas satélite. Se pide aparte y se
  // inyecta en las tabs que lo necesitan (Datos, Documentación, Autoridades).
  const esAlianzaExp = expediente?.tipo === 'CONSTITUCION_ALIANZA';
  const esCandidaturaExp = expediente?.tipo === 'CANDIDATURA';

  // Secciones de documentación del expediente (`expediente_tipos_documento`).
  //
  // Se pide para CUALQUIER trámite, no sólo alianzas: un expediente sin filas
  // sembradas devuelve [] y la pantalla cae al tablero de siempre, así que no hace
  // falta preguntar por el tipo. Eso además hace que el reemplazo sea gradual — un
  // reempadronamiento todavía sin sembrar sigue viéndose como antes.
  const { data: seccionesExp = [] } = useQuery({
    queryKey: ['secciones-tipo-docs', id],
    queryFn: () => seccionesDocumentoService.listar(id!),
    enabled: !!id,
  });
  const { data: alianzaDetalle } = useQuery({
    queryKey: ['alianza-expediente', id],
    queryFn: () => alianzaGestionService.detalle(id!),
    enabled: !!id && esAlianzaExp,
  });
  const integrantesAlianza = alianzaDetalle?.integrantes ?? [];
  // Datos que alimentan las partes resolutivas precargadas del acta del TEP (qué
  // partidos integran la alianza y para qué elección). Vivía adentro del modal de
  // resolución propio de alianzas; al pasar al modal genérico ModalResolucionTep
  // —que sólo recibe un string ya armado— hay que componerlo acá.
  //
  // Se calcula en cada render y NO se memoiza: el modal precarga el texto una sola
  // vez, al abrirse con una config nueva, así que un objeto nuevo por render no
  // pisa lo que el Secretario ya editó.
  const datosTextoAlianza = {
    denominacion: alianzaDetalle?.alianza?.denominacion || expediente?.partido?.denominacion || '',
    integrantes: integrantesAlianza.map((i: any) => i?.denominacion || '').filter(Boolean),
    fechaActo: alianzaDetalle?.alianza?.convocatoria?.fechaActo ?? null,
    ambitoTipo: alianzaDetalle?.alianza?.convocatoria?.ambitoTipo ?? null,
  };
  // Los integrantes fuera de SIREPPAR (y los distritales) exigen corroboración
  // manual contra el registro antes de que el expediente pueda elevarse.
  const integrantesSinVerificar = integrantesAlianza.filter(
    (i: any) => i.requiereVerificacion && !i.verificadoEn,
  );
  // Los distritales SÍ están en SIREPPAR, así que no hay nada que "marcar como
  // verificado": lo que corresponde es cotejar su vigencia contra la Justicia
  // Electoral Nacional. Va como recordatorio aparte, sin bloquear nada.
  const integrantesDistritales = integrantesAlianza.filter(
    (i: any) => i.tipo === 'DISTRITO' && i.enSireppar,
  );
  const esUsuarioInterno = ['ADMINISTRADOR', 'SUPERVISOR', 'ANALISTA', 'MESA_ENTRADAS', 'AUTORIDAD_FIRMANTE'].includes(user?.rol || '');
  const puedeVerAdherentes = esUsuarioInterno
    || ESTADOS_DESDE_ANALISIS.includes(expediente?.estado || '')
    || !!faseGating?.startsWith('DOCUMENTACION_');
  const FASES_CON_AFILIADOS = [
    'DOCUMENTACION_APROBADA', 'AFILIADOS_PENDIENTES', 'AUTORIDADES_PENDIENTES',
    'DEFINITIVO_EN_TEP', 'DEFINITIVO_RECHAZADO', 'RECONOCIMIENTO_DEFINITIVO',
  ];
  // Una ALIANZA no tiene afiliados propios: los afiliados son de cada partido
  // integrante. Se corta acá y no sólo en el botón, así la tab, su contenido y el
  // `?tab=afiliados` de la URL quedan cerrados con una sola condición.
  const puedeVerAfiliados = !esAlianzaExp && !esCandidaturaExp && (
    esUsuarioInterno
    || ESTADOS_DESDE_PROVISORIO.includes(expediente?.estado || '')
    || (!!faseGating && FASES_CON_AFILIADOS.includes(faseGating))
  );

  const tabSolicitada =
    (tabParam === 'afiliados' && !puedeVerAfiliados)
    || (tabParam === 'adherentes' && !puedeVerAdherentes)
    // 'autoridades' era la tab de alianzas; se sacó y no tiene contenido, así que
    // entrar por URL dejaría el panel en blanco.
    || tabParam === 'autoridades'
      ? '' : tabParam;
  // En la etapa de afiliados (provisorio → definitivo) lo que importa es cuántas
  // afiliaciones lleva reunidas el partido, así que se entra por esa pestaña.
  // Cuando la pelota es del Secretario (elevar al TEP, registrar el acta, resolver
  // una devolución) se entra directo por la tab Informes: es lo que necesita ver
  // para decidir. Tiene prioridad sobre la de afiliados.
  const pendienteDelSecretario = expediente
    ? !!accionPendienteSecretario({
        tipo: expediente.tipo,
        estado: expediente.estado,
        datosFormulario: expediente.datosFormulario,
      })
    : false;
  const tabPorDefecto = esRendicion
    ? 'financiero'
    : pendienteDelSecretario
      ? 'informes'
      : esEtapaAfiliados(faseGating) && puedeVerAfiliados
        ? 'afiliados'
        : atributosPendientes ? 'atributos' : 'docs';
  const activeTab = tabSolicitada || tabPorDefecto;

  // Scroll a la primera observación pendiente cuando se llega desde el banner
  // de alertas (?tab=obs&focus=primera).
  useEffect(() => {
    if (focusParam !== 'primera' || activeTab !== 'obs' || !expediente) return;
    const t = setTimeout(() => {
      const el = document.querySelector('[data-observacion-pendiente="true"]');
      if (el && 'scrollIntoView' in el) {
        (el as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
    return () => clearTimeout(t);
  }, [focusParam, activeTab, expediente]);

  // Vuelta desde /documentos: el link de retorno trae #doc-<TIPO>, así que se
  // vuelve a la MISMA fila del tablero y no al principio de la página.
  useEffect(() => {
    if (activeTab !== 'docs' || !location.hash.startsWith('#doc-')) return;
    const t = setTimeout(() => {
      const el = document.getElementById(location.hash.slice(1));
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 250);
    return () => clearTimeout(t);
  }, [activeTab, location.hash, expediente]);

  // Cuando el partido viene de re-presentar una rendición, pasamos
  // ?precargarRespuesta=1 en la URL → precargamos un texto formal en la
  // primera observación pendiente y scrolleamos al campo.
  const precargarRespuesta = searchParams.get('precargarRespuesta') === '1';
  const TEXTO_RESPUESTA_PRECARGADO =
    'Se ha subsanado la observación realizada. Se generó una nueva versión de la rendición que refleja las correcciones efectuadas.';

  // Query resumen de documentos del expediente
  const { data: docsResumen } = useQuery({
    queryKey: ['expediente-docs-resumen', id],
    queryFn: async () => {
      const response = await expedienteService.getDocumentosResumen(id!);
      return response.data.data as { PENDIENTE: number; APROBADO: number; RECHAZADO: number };
    },
    enabled: !!id,
  });

  const { data: interacciones } = useQuery({
    queryKey: ['interacciones-expediente', id],
    queryFn: () => interaccionService.list({ expedienteId: id! }),
    enabled: !!id && activeTab === 'inter',
    select: (data) => data.data || [],
  });

  const { data: afiliadosResumen } = useQuery({
    queryKey: ['afiliados-resumen', expediente?.partido?.id],
    queryFn: () => api.get(`/afiliados/partido/${expediente!.partido!.id}/resumen`),
    // También en 'docs': el renglón fijo del 4‰ del tablero de documentación.
    enabled: !!expediente?.partido?.id && (activeTab === 'afiliados' || activeTab === 'docs'),
    select: (data) => data.data,
  });

  // Estado de los dos documentos del padrón de afiliados (Excel + PDF firmado):
  // habilita el botón "Autoridades pendientes" y alimenta la card de la pestaña.
  const { data: estadoAfiliadosDocs } = useQuery({
    queryKey: ['recon-estado-afiliados', id],
    queryFn: () => api.get(`/reconocimiento/${id}/estado-afiliados`),
    enabled: !!id && esEtapaAfiliados(faseGating) && esUsuarioInterno,
    select: (data) => data.data.data as {
      faltanPresentar: string[];
      faltanVerificar: string[];
      completo: boolean;
    },
  });

  // Estado de los tres documentos de la etapa de autoridades definitivas + cuántas
  // autoridades y apoderados presentó el partido: habilita "Elevar al TEP
  // (definitivo)" y alimenta la card de la pestaña.
  const { data: estadoAutoridadesDocs } = useQuery({
    queryKey: ['recon-estado-autoridades', id],
    queryFn: () => api.get(`/reconocimiento/${id}/estado-autoridades`),
    enabled: !!id && esEtapaAutoridades(faseGating) && esUsuarioInterno,
    select: (data) => data.data.data as {
      faltanPresentar: string[];
      faltanVerificar: string[];
      completo: boolean;
      autoridadesCargadas: boolean;
      cantidadAutoridades: number;
      cantidadApoderados: number;
    },
  });

  // Plazo de 4 meses del provisorio. Es UNO SOLO para las dos etapas del tramo
  // (afiliados y autoridades definitivas); el nombre quedó de la primera.
  const plazoAfiliados = getPlazoAfiliados(expediente?.datosFormulario);

  // "No presentado" en el tramo del provisorio: se habilita por DOS caminos, y
  // basta con uno (mismo criterio que el gate del backend, routes/noPresentado):
  //   a) venció el plazo de 4 meses del provisorio, o
  //   b) se le observó algo y venció ese plazo sin que respondiera — puede pasar
  //      bastante antes de los 4 meses.
  const hayObsVencidaSinResponder = (expediente?.observaciones || []).some(
    (o) => o.estado === 'PENDIENTE' && !o.subsanada && !!o.fechaLimite && new Date(o.fechaLimite) < new Date(),
  );
  const plazoCuatroMesesVencido = !!plazoAfiliados?.vencido;
  const noPresentadoHabilitado = plazoCuatroMesesVencido || hayObsVencidaSinResponder;

  const { data: adherentesResumen } = useQuery({
    queryKey: ['adherentes-resumen', expediente?.partido?.id],
    queryFn: () => api.get(`/adherentes/partido/${expediente!.partido!.id}/resumen`),
    enabled: !!expediente?.partido?.id && activeTab === 'adherentes',
    select: (data) => data.data,
  });
  // Query analistas para asignación
  const { data: analistas } = useQuery({
    queryKey: ['usuarios-analistas'],
    queryFn: async () => {
      const response = await usuarioService.list({ rol: 'ANALISTA', pageSize: 100 });
      return response.data.data || [];
    },
    enabled: showAsignarModal,
  });

  const { data: documentosChecklist } = useQuery({
    queryKey: ['documentos-checklist', id],
    queryFn: () => documentoService.list({ expedienteId: id!, pageSize: 200 }),
    enabled: !!id,
    select: (data) => data.data.data || [],
  });

  const { data: autoridadesDniData } = useQuery({
    queryKey: ['autoridades-dni', id],
    queryFn: async () => {
      const response = await expedienteService.getAutoridadesDni(id!);
      return response.data.data as any[];
    },
    enabled: !!id,
  });

  const { data: apoderadosDniData } = useQuery({
    queryKey: ['apoderados-dni', expediente?.partido?.id],
    queryFn: async () => {
      const response = await partidoService.getApoderadosDni(expediente!.partido!.id);
      return response.data.data as any[];
    },
    enabled: !!expediente?.partido?.id,
  });

  // Apoderados de la agrupación para la tab "Datos del Trámite". No se reusa
  // `apoderadosDniData` (esa consulta trae los ARCHIVOS de DNI, no los datos de
  // contacto) ni el panel de alianzas (ahí los apoderados vienen de la alianza).
  const { data: apoderadosPartido } = useQuery({
    queryKey: ['apoderados-partido', expediente?.partido?.id],
    queryFn: async () => {
      const response = await partidoService.getAutoridades(expediente!.partido!.id);
      return (response.data.data?.apoderados ?? []) as ApoderadoPartido[];
    },
    enabled: !!expediente?.partido?.id && !esAlianzaExp && activeTab === 'data',
  });

  // DNI del iniciador: es la documentación obligatoria de la etapa de ATRIBUTOS
  // del reconocimiento (se revisa en admisibilidad, competencia de la SEP).
  const { data: iniciadorDniData } = useQuery({
    queryKey: ['iniciador-dni', expediente?.partido?.id],
    queryFn: async () => {
      const response = await partidoService.getIniciadorDni(expediente!.partido!.id);
      return response.data.data as any[];
    },
    enabled: !!expediente?.partido?.id && expediente?.tipo === 'RECONOCIMIENTO',
  });

  // Etapa de verificación activa de este expediente. El checklist y los DNIs
  // muestran el estado de la dimensión correspondiente (admisibilidad/análisis).
  // Se mira la ÚLTIMA etapa alcanzada, no sólo la que está en curso: un trámite ya
  // elevado al Secretario o resuelto por el TEP pasó por análisis, y es ese estado
  // el que vale. Antes esto sólo miraba EN_ANALISIS/OBSERVADO, así que en cuanto el
  // expediente avanzaba volvía a mostrar el estado de admisibilidad (viejo) y no
  // coincidía con /documentos. La regla es compartida: utils/etapaVerificacion.
  const etapaActivaExp: 'ADMISIBILIDAD' | 'ANALISIS' = dimensionPorDefecto({
    tipo: expediente?.tipo,
    estado: expediente?.estado,
    fase: getFaseActual(expediente?.datosFormulario),
  });
  const proyDoc = (d: any) => ({
    ...d,
    estadoRevision: (etapaActivaExp === 'ANALISIS' ? d.estadoRevisionAnalisis : d.estadoRevisionAdmisibilidad) ?? d.estadoRevision ?? 'PENDIENTE',
    observacion: etapaActivaExp === 'ANALISIS' ? d.observacionAnalisis : d.observacionAdmisibilidad,
    revisadoPorNombre: etapaActivaExp === 'ANALISIS' ? d.analisisRevisadoPor : d.admisibilidadRevisadoPor,
    revisadoEn: etapaActivaExp === 'ANALISIS' ? d.analisisRevisadoEn : d.admisibilidadRevisadoEn,
  });
  const docsChecklist = useMemo(() => (documentosChecklist || []).map(proyDoc), [documentosChecklist, etapaActivaExp]);
  const autoridadesDni = useMemo(() => (autoridadesDniData || []).map((o: any) => ({
    ...o,
    autoridades: (o.autoridades || []).map((a: any) => ({
      ...a,
      dniFrente: (a.dniFrente || []).map(proyDoc),
      dniDorso: (a.dniDorso || []).map(proyDoc),
    })),
  })), [autoridadesDniData, etapaActivaExp]);
  const apoderadosDni = useMemo(() => (apoderadosDniData || []).map((ap: any) => ({
    ...ap,
    dniFrente: (ap.dniFrente || []).map(proyDoc),
    dniDorso: (ap.dniDorso || []).map(proyDoc),
  })), [apoderadosDniData, etapaActivaExp]);

  // Cálculo unificado de problemas de documentación (obligatorios + DNIs autoridades/apoderados)
  // La lista de obligatorios depende del ámbito del partido (DISTRITO suma documentos) y
  // de su clase (CONFEDERACION suma los cuatro del art. 8). Fuente de verdad:
  // tiposDocumento.ts, que espeja el backend.
  const OBLIGATORIOS_TIPOS: Array<{ tipo: string; label: string }> = useMemo(() => {
    const partido = expediente?.partido as any;
    // La constitución de alianza pide los documentos del Art. 27, NO los del
    // reempadronamiento. `obligatoriosPorPartido` mira tipo y clase del partido y
    // devolvería la lista de reempadronamiento (carta orgánica, padrón, cuenta
    // bancaria…), que en una alianza no existe.
    const tipos = expediente?.tipo === 'CONSTITUCION_ALIANZA'
      ? DOCS_OBLIGATORIOS_ALIANZA
      : obligatoriosPorPartido(partido?.tipo, partido?.clase);
    return tipos.map((tipo) => ({ tipo, label: tipoDocLabels[tipo] || tipo }));
  }, [expediente?.tipo, (expediente?.partido as any)?.tipo, (expediente?.partido as any)?.clase]);


  // ===========================================================================
  // Estado de la documentación para el cuerpo de los informes (Secretario / TEP)
  // ===========================================================================
  // Qué documentación es obligatoria depende del trámite Y de la etapa:
  //   - RECONOCIMIENTO, etapa ATRIBUTOS: el DNI del iniciador (frente y dorso).
  //   - RECONOCIMIENTO, etapa DOCUMENTACIÓN: los documentos del Art. 18/19, más
  //     el DNI de las autoridades promotoras y apoderados promotores SI están
  //     cargados (el wizard no los exige, así que su ausencia no se informa).
  //   - REEMPADRONAMIENTO: los obligatorios según el tipo de partido, más el DNI
  //     de cada autoridad y apoderado (ahí sí son obligatorios).
  // Nunca se miran "Otros documentos", emblemas/símbolos ni Documentación TEP.
  const itemsVerificacion = useMemo<ItemVerificacion[]>(() => {
    if (!expediente) return [];
    const esRecon = expediente.tipo === 'RECONOCIMIENTO';
    const fase = esRecon ? getFaseActual(expediente.datosFormulario) : null;
    const esEtapaAtributos = !!fase && fase.startsWith('ATRIBUTOS');

    // RECONOCIMIENTO se verifica siempre en la dimensión de ADMISIBILIDAD (igual
    // que DocumentosPage); el resto, en la dimensión de la etapa activa.
    const enAnalisis = !esRecon && etapaActivaExp === 'ANALISIS';
    const estadoDe = (d: any): string =>
      (enAnalisis ? d?.estadoRevisionAnalisis : d?.estadoRevisionAdmisibilidad)
        ?? d?.estadoRevision ?? 'PENDIENTE';
    const motivoDe = (d: any): string | undefined =>
      (enAnalisis ? d?.observacionAnalisis : d?.observacionAdmisibilidad) || d?.observacion || undefined;

    const items: ItemVerificacion[] = [];

    // --- Un tipo de documento obligatorio ---
    // Se queda con el mejor archivo del tipo: aprobado > sin verificar > rechazado.
    const evaluarTipo = (tipo: string, label: string) => {
      const files = (documentosChecklist || []).filter((d: any) => d.tipo === tipo);
      if (files.length === 0) { items.push({ label, estado: 'SIN_PRESENTAR' }); return; }
      if (files.some((d: any) => estadoDe(d) === 'APROBADO')) { items.push({ label, estado: 'APROBADA' }); return; }
      if (files.some((d: any) => estadoDe(d) === 'PENDIENTE')) { items.push({ label, estado: 'SIN_VERIFICAR' }); return; }
      const ultimoRechazo = [...files].reverse().find((d: any) => estadoDe(d) === 'RECHAZADO');
      items.push({ label, estado: 'RECHAZADA', motivo: motivoDe(ultimoRechazo) });
    };

    // --- Un lado del DNI de una persona ---
    // exigido=false (promotoras del reconocimiento): si no hay archivo no se
    // informa nada, porque el wizard no obliga a cargarlo.
    const evaluarLadoDni = (archivos: any[] | undefined, label: string, exigido: boolean) => {
      if (!archivos || archivos.length === 0) {
        if (exigido) items.push({ label, estado: 'SIN_PRESENTAR' });
        return;
      }
      if (archivos.some((d: any) => estadoDe(d) === 'APROBADO')) { items.push({ label, estado: 'APROBADA' }); return; }
      if (archivos.some((d: any) => estadoDe(d) === 'PENDIENTE')) { items.push({ label, estado: 'SIN_VERIFICAR' }); return; }
      const ultimoRechazo = [...archivos].reverse().find((d: any) => estadoDe(d) === 'RECHAZADO');
      items.push({ label, estado: 'RECHAZADA', motivo: motivoDe(ultimoRechazo) });
    };

    const evaluarPersona = (persona: any, prefijo: string, exigido: boolean) => {
      const nombre = `${persona.nombre || ''} ${persona.apellido || ''}`.trim();
      const quien = `${prefijo} ${nombre}${persona.dni ? ` (DNI ${persona.dni})` : ''}`.trim();
      evaluarLadoDni(persona.dniFrente, `${quien} — DNI frente`, exigido);
      evaluarLadoDni(persona.dniDorso, `${quien} — DNI dorso`, exigido);
    };

    const evaluarAutoridades = (exigido: boolean) => {
      (autoridadesDniData || []).forEach((organo: any) => {
        const nombreOrgano = organo.denominacionPersonalizada || organo.denominacion || organo.tipoOrgano || 'Órgano';
        (organo.autoridades || []).forEach((aut: any) => {
          const cargo = aut.mandato?.cargo || 'Autoridad';
          evaluarPersona(aut, `${nombreOrgano} — ${cargo}`, exigido);
        });
      });
    };
    const evaluarApoderados = (exigido: boolean) => {
      (apoderadosDniData || [])
        .filter((ap: any) => ap.activo !== false)
        .forEach((ap: any) => evaluarPersona(ap, 'Apoderado', exigido));
    };

    if (esRecon && esEtapaAtributos) {
      // Etapa de atributos: lo exigible es el DNI del iniciador.
      const iniciadores = iniciadorDniData || [];
      if (iniciadores.length === 0) {
        items.push({ label: 'DNI del iniciador — frente', estado: 'SIN_PRESENTAR' });
        items.push({ label: 'DNI del iniciador — dorso', estado: 'SIN_PRESENTAR' });
      } else {
        iniciadores.forEach((ini: any) => evaluarPersona(ini, 'Iniciador', true));
      }
      return items;
    }

    if (esRecon) {
      // Art. 18/19 + los obligatorios de cada etapa posterior YA ALCANZADA (padrón
      // del 4‰, actas de las internas). Se suman al mismo checklist y no vuelven a
      // salir: son los que el Secretario y el TEP revisan para el definitivo, y sin
      // esto no entraban nunca en la verificación documental ni en la advertencia
      // que se inyecta a los informes.
      // La fase se lee acá y no de `faseRecon`: esa constante se define más abajo,
      // fuera del alcance de este useMemo.
      const faseDelExpediente = getFaseActual(expediente?.datosFormulario);
      const obligatoriosRecon = [
        ...DOCS_OBLIGATORIOS_RECONOCIMIENTO,
        ...(alcanzoEtapaAfiliados(faseDelExpediente) ? DOCS_OBLIGATORIOS_RECONOCIMIENTO_AFILIADOS : []),
        ...(alcanzoEtapaAutoridades(faseDelExpediente) ? DOCS_OBLIGATORIOS_RECONOCIMIENTO_AUTORIDADES : []),
      ];
      obligatoriosRecon.forEach((tipo) => evaluarTipo(tipo, tipoDocLabels[tipo] || tipo));
      evaluarAutoridades(false);
      evaluarApoderados(false);
      return items;
    }

    // Reempadronamiento (y el resto de los trámites que usan este checklist).
    OBLIGATORIOS_TIPOS.forEach(({ tipo, label }) => evaluarTipo(tipo, label));
    evaluarAutoridades(true);
    evaluarApoderados(true);
    return items;
  }, [expediente, documentosChecklist, autoridadesDniData, apoderadosDniData,
      iniciadorDniData, OBLIGATORIOS_TIPOS, etapaActivaExp]);

  const advertenciaVerificacion = bloqueVerificacionDocumental(itemsVerificacion);
  // Los textos precargados de los informes se arman con la advertencia ya puesta.
  const conAdvertencia = (texto: string) => aplicarVerificacionDocumental(texto, advertenciaVerificacion);
  // Encabezado editable (destinatario + partido) al inicio del cuerpo. El PDF ya no
  // lo imprime fijo, así que quien firma puede corregirlo desde el modal.
  //
  // `conVerificacion` deja fuera el bloque de verificación documental, que lista por
  // etiqueta cada documento rechazado, no presentado o sin verificar, con su motivo.
  // En RECONOCIMIENTO ese detalle es el informe (por eso sigue siendo el default);
  // en REEMPADRONAMIENTO se decidió que no: el informe dice que el análisis está
  // cerrado y que la decisión es del Secretario, y lo puntual se adjunta a mano
  // tildándolo en el panel lateral. Además el inventario se contradecía con el
  // cuerpo nuevo, que afirma haber analizado toda la documentación.
  const informeAlSecretario = (texto: string, opts?: { conVerificacion?: boolean }) =>
    conEncabezadoInforme(opts?.conVerificacion === false ? texto : conAdvertencia(texto), {
      destinatario: 'SECRETARIO',
      nombrePartido: expediente?.partido?.denominacion,
    });
  const informeAlTep = (texto: string) =>
    conEncabezadoInforme(conAdvertencia(texto), {
      destinatario: 'TEP',
      nombrePartido: expediente?.partido?.denominacion,
    });
  // Devolución del Secretario: va dirigida a la SEP, y sin el bloque de advertencia
  // documental (el Secretario escribe sus propios motivos, no un resumen del control).
  const informeALaSep = (texto: string) =>
    conEncabezadoInforme(texto, {
      destinatario: 'SEP',
      nombrePartido: expediente?.partido?.denominacion,
    });

  const { problemasObligatorios, problemasDni, problemasOtros, dniPendientesRevision, otrosPendientesRevision, totalProblemas, textoObs } = useMemo(() => {
    const docs = docsChecklist;
    // ¿La sección de este tipo tiene su CHECK en la dimensión activa?
    //
    // Es el corte de la observación automática, igual que en /documentos. La regla
    // anterior —"con un archivo aprobado del tipo, no se lista nada más"— se rompe
    // donde el tipo tiene N archivos: la constancia de un integrante aprobada
    // silenciaba el rechazo de la de otro. `null` = sin secciones sembradas, y ahí se
    // conserva la regla vieja.
    const seccionAprobada = (tipo: string): boolean | null => {
      const grupo = Object.entries(GRUPOS_TIPOS_DOCUMENTO).find(([, t]) => (t as string[]).includes(tipo))?.[0];
      const filas = (seccionesExp as SeccionInforme[]).filter((x) =>
        grupo ? GRUPOS_TIPOS_DOCUMENTO[grupo].includes(x.tipo) : x.tipo === tipo);
      if (filas.length === 0) return null;
      return filas.every((f) =>
        (etapaActivaExp === 'ADMISIBILIDAD' ? f.admisibilidad : f.analisis).estado === 'APROBADO');
    };

    const getBestObligatorio = (tipo: string) => {
      const matches = docs.filter((d: any) => d.tipo === tipo);
      if (!matches.length) return undefined;
      const priority: Record<string, number> = { APROBADO: 0, PENDIENTE: 1, RECHAZADO: 2 };
      return matches.sort((a: any, b: any) => (priority[a.estadoRevision] ?? 3) - (priority[b.estadoRevision] ?? 3))[0];
    };
    const problemasObligatorios: Array<{ tipo: string; label: string; estado: 'FALTANTE' | 'RECHAZADO'; motivo?: string }> = [];
    for (const { tipo, label } of OBLIGATORIOS_TIPOS) {
      const archivos = docs.filter((d: any) => d.tipo === tipo);
      if (archivos.length === 0) {
        problemasObligatorios.push({ tipo, label, estado: 'FALTANTE', motivo: undefined });
        continue;
      }
      const check = seccionAprobada(tipo);
      // Sección tildada: no hay nada que observar, tenga los archivos que tenga.
      if (check === true) continue;
      if (check === null) {
        // Sin secciones sembradas: regla anterior (gana el mejor estado del tipo).
        const doc = getBestObligatorio(tipo);
        if (doc?.estadoRevision === 'RECHAZADO') {
          problemasObligatorios.push({ tipo, label, estado: 'RECHAZADO', motivo: doc.observacion || undefined });
        }
        continue;
      }
      // Sección sin tildar: un renglón por cada archivo rechazado, con su motivo. Se
      // agrupa por título para no repetir un rechazo ya superado por otra carga del
      // mismo objeto.
      const porTitulo = new Map<string, any[]>();
      for (const f of archivos) {
        const clave = (f.titulo || f.nombreOriginal || '').trim() || f.id;
        porTitulo.set(clave, [...(porTitulo.get(clave) ?? []), f]);
      }
      for (const [clave, grupo] of porTitulo) {
        const ultimo = [...grupo].sort((a, b) =>
          new Date(a.creadoEn).getTime() - new Date(b.creadoEn).getTime()).slice(-1)[0];
        if (ultimo.estadoRevision !== 'RECHAZADO') continue;
        problemasObligatorios.push({
          tipo,
          label: clave && clave !== label ? `${label} — ${clave}` : label,
          estado: 'RECHAZADO',
          motivo: ultimo.observacion || undefined,
        });
      }
    }

    // Selecciona el mejor documento por lado (APROBADO > PENDIENTE > primero por creadoEn desc).
    // Solo considera "problema" cuando no hay doc o el mejor disponible quedó RECHAZADO.
    const evaluarLado = (arr: any[] | undefined): { estado: 'OK' | 'PENDIENTE' | 'FALTANTE' | 'RECHAZADO'; motivo?: string } => {
      if (!arr || arr.length === 0) return { estado: 'FALTANTE' };
      const mejor = arr.find((d: any) => d.estadoRevision === 'APROBADO')
        ?? arr.find((d: any) => d.estadoRevision === 'PENDIENTE')
        ?? arr[0];
      if (mejor.estadoRevision === 'APROBADO') return { estado: 'OK' };
      if (mejor.estadoRevision === 'PENDIENTE') return { estado: 'PENDIENTE' };
      return { estado: 'RECHAZADO', motivo: mejor.observacion || undefined };
    };

    const problemasDni: Array<{ label: string; estado: 'FALTANTE' | 'RECHAZADO'; motivo?: string }> = [];
    // Contadores para el resumen del checklist: lados (frente/dorso) por persona que
    // todavía no están verificados. "pendientes de revisión" = cargados pero sin revisar.
    let dniPendientesRevision = 0;
    const registrarLado = (arr: any[] | undefined, label: string) => {
      const r = evaluarLado(arr);
      if (r.estado === 'FALTANTE' || r.estado === 'RECHAZADO') {
        problemasDni.push({ label, estado: r.estado, motivo: r.motivo });
      } else if (r.estado === 'PENDIENTE') {
        dniPendientesRevision++;
      }
    };

    // Con la sección de DNI tildada no se observa nada de ese grupo. Adentro cada
    // persona y cada lado siguen evaluándose por separado: ahí "hay uno aprobado" NO
    // es el bug de cardinalidad —el frente de una persona es un solo objeto— y listar
    // un rechazo ya reemplazado sería pedir corregir algo ya corregido.
    const checkDniAutoridades = seccionAprobada('DNI_FRENTE_AUTORIDAD');
    const checkDniApoderados = seccionAprobada('DNI_FRENTE_APODERADO');

    (checkDniAutoridades === true ? [] : autoridadesDni).forEach((organo: any) => {
      const nombreOrgano = organo.denominacionPersonalizada || organo.denominacion || organo.tipoOrgano || 'Órgano';
      (organo.autoridades || []).forEach((aut: any) => {
        const cargo = aut.mandato?.cargo || 'Autoridad';
        const nombreCompleto = `${cargo} ${aut.nombre || ''} ${aut.apellido || ''}`.trim();
        const personaLabel = `${nombreOrgano} — ${nombreCompleto}${aut.dni ? ` (DNI ${aut.dni})` : ''}`;
        registrarLado(aut.dniFrente, `DNI FRENTE - ${personaLabel}`);
        registrarLado(aut.dniDorso, `DNI DORSO - ${personaLabel}`);
      });
    });

    (checkDniApoderados === true ? [] : apoderadosDni)
      .filter((ap: any) => ap.activo !== false)
      .forEach((ap: any) => {
        const nombreCompleto = `${ap.nombre || ''} ${ap.apellido || ''}`.trim();
        const personaLabel = `Apoderado ${nombreCompleto}${ap.dni ? ` (DNI ${ap.dni})` : ''}`;
        registrarLado(ap.dniFrente, `DNI FRENTE - ${personaLabel}`);
        registrarLado(ap.dniDorso, `DNI DORSO - ${personaLabel}`);
      });

    // Otros documentos (no obligatorios, no DNIs, no Notas del sistema): se agrupan
    // por tipo. Mismo criterio que la sección "Otros documentos" de DocumentosPage:
    // un pendiente dentro del grupo manda → el tipo queda pendiente aunque ya haya
    // algún aprobado. Si no hay pendientes ni aprobados, el grupo está rechazado y se
    // observa con el motivo del último rechazo.
    const OBL_SET = new Set(OBLIGATORIOS_TIPOS.map(o => o.tipo));
    const problemasOtros: Array<{ label: string; motivo?: string }> = [];
    let otrosPendientesRevision = 0;
    const enAnalisisExp = etapaActivaExp === 'ANALISIS';
    const otrosReales = docs.filter((d: any) =>
      d && d.tipo !== 'NOTA' && !OBL_SET.has(d.tipo) &&
      !(typeof d.tipo === 'string' && d.tipo.startsWith('DNI_')) &&
      // En análisis quedan fuera los rechazados en admisibilidad que nunca llegaron a
      // análisis (siguen PENDIENTE): no están en juego en esta etapa, igual que en la
      // vista de análisis de DocumentosPage. Así no se cuentan como pendientes.
      !(enAnalisisExp && d.estadoRevisionAdmisibilidad === 'RECHAZADO' && d.estadoRevisionAnalisis !== 'RECHAZADO')
    );
    const tiposOtros = Array.from(new Set(otrosReales.map((d: any) => d.tipo)));
    tiposOtros.forEach((tipo) => {
      const files = otrosReales
        .filter((d: any) => d.tipo === tipo)
        .sort((a: any, b: any) => new Date(a.creadoEn).getTime() - new Date(b.creadoEn).getTime());
      const pendientesEnTipo = files.filter((f: any) => f.estadoRevision === 'PENDIENTE').length;
      if (pendientesEnTipo > 0) {
        // Pendiente manda: el tipo queda pendiente. El contador cuenta los documentos
        // pendientes (no el total del tipo ni la cantidad de tipos).
        otrosPendientesRevision += pendientesEnTipo;
        return;
      }
      const check = seccionAprobada(tipo as string);
      if (check === true) return;
      if (check === null) {
        if (files.some((f: any) => f.estadoRevision === 'APROBADO')) return; // verificado, sin problema
        // Sin pendientes ni aprobados: todo rechazado → se observa el último rechazo.
        const lastRejected = [...files].reverse().find((f: any) => f.estadoRevision === 'RECHAZADO');
        if (lastRejected) {
          problemasOtros.push({ label: lastRejected.titulo || lastRejected.tipo, motivo: lastRejected.observacion || undefined });
        }
        return;
      }
      // Sección sin tildar: cada rechazado se informa, no sólo el último.
      files
        .filter((f: any) => f.estadoRevision === 'RECHAZADO')
        .forEach((f: any) => problemasOtros.push({
          label: f.titulo || f.tipo, motivo: f.observacion || undefined,
        }));
    });

    const lineasObl = problemasObligatorios.map((p, i) => {
      const motivo = p.motivo ? ` — ${p.motivo}` : '';
      return `${i + 1}. ${p.label.toUpperCase()}: ${p.estado}${motivo}`;
    });
    const lineasDni = problemasDni.map((p, i) => {
      const motivo = p.motivo ? ` — ${p.motivo}` : '';
      return `${problemasObligatorios.length + i + 1}. ${p.label}: ${p.estado}${motivo}`;
    });
    const lineasOtros = problemasOtros.map((p, i) => {
      const motivo = p.motivo ? ` — ${p.motivo}` : '';
      return `${problemasObligatorios.length + problemasDni.length + i + 1}. ${p.label.toUpperCase()}: RECHAZADO${motivo}`;
    });

    return {
      problemasObligatorios,
      problemasDni,
      problemasOtros,
      dniPendientesRevision,
      otrosPendientesRevision,
      totalProblemas: problemasObligatorios.length + problemasDni.length + problemasOtros.length,
      textoObs: [...lineasObl, ...lineasDni, ...lineasOtros].join('\n'),
    };
  }, [docsChecklist, autoridadesDni, apoderadosDni, OBLIGATORIOS_TIPOS, etapaActivaExp, seccionesExp]);

  // Mutations
  const presentarMutation = useMutation({
    mutationFn: () => expedienteService.presentar(id!),
    onSuccess: () => {
      toast.success('Expediente presentado');
      queryClient.invalidateQueries({ queryKey: ['expediente', id] });
      queryClient.invalidateQueries({ queryKey: ['gestion-expedientes'] });
    },
    onError: (err: any) => toast.error(err.response?.data?.error?.message || 'Error al presentar'),
  });

  const iniciarRevisionMutation = useMutation({
    mutationFn: () => api.post('/expedientes/' + id + '/iniciar-analisis'),
    onSuccess: () => {
      toast.success('Revisión iniciada');
      queryClient.invalidateQueries({ queryKey: ['expediente', id] });
      queryClient.invalidateQueries({ queryKey: ['gestion-expedientes'] });
    },
    onError: (err: any) => toast.error(err.response?.data?.error?.message || 'Error al iniciar revisión'),
  });

  const abrirAdjuntoObservacion = async (observacionId: string, adjuntoId: string) => {
    try {
      window.open(await expedienteService.urlAdjuntoObservacion(id!, observacionId, adjuntoId), '_blank');
    } catch (err: any) {
      toast.error(err.response?.data?.error?.message || 'No se pudo abrir el adjunto');
    }
  };

  const enviarObservacionMutation = useMutation({
    mutationFn: () => {
      const plazo = obsSinPlazo ? undefined : obsPlazoDias;
      const datos = new FormData();
      datos.append('texto', obsTexto);
      if (plazo) datos.append('plazoDias', String(plazo));
      if (obsSinPlazo) datos.append('sinPlazo', 'true');
      if (obsRenovarPlazo) datos.append('renovarPlazo', 'true');
      if (obsOrigen) datos.append('origen', obsOrigen);
      obsAdjuntos.forEach((archivo) => datos.append('archivos', archivo));
      // RECONOCIMIENTO usa su endpoint (no cambia estado a OBSERVADO); el resto, el estándar.
      if (expediente?.tipo === 'RECONOCIMIENTO') {
        return reconocimientoService.observar(id!, datos);
      }
      // ALIANZA: endpoint propio. No es un capricho — la nota cita los Arts. 26/27
      // (no la resolución de reempadronamiento) y el correo va a TODAS las
      // agrupaciones de la alianza, no sólo al apoderado del expediente.
      if (expediente?.tipo === 'CONSTITUCION_ALIANZA') {
        return alianzaGestionService.observar(id!, datos);
      }
      return expedienteService.observar(id!, datos);
    },
    onSuccess: (resp: any) => {
      toast.success(resp?.data?.message || 'Observación enviada');
      setObsTexto('');
      setObsPlazoDias(15);
      setObsAdjuntos([]);
      queryClient.invalidateQueries({ queryKey: ['expediente', id] });
      queryClient.invalidateQueries({ queryKey: ['gestion-expedientes'] });
      queryClient.invalidateQueries({ queryKey: ['alianza-expediente', id] });
      queryClient.invalidateQueries({ queryKey: ['bandeja-alianzas'] });
    },
    onError: (err: any) => toast.error(err.response?.data?.error?.message || 'Error al enviar observación'),
  });

  // ALIANZA: pase de admisibilidad a análisis (botón "Iniciar").
  const iniciarAnalisisAlianzaMutation = useMutation({
    mutationFn: () => alianzaGestionService.iniciarAnalisis(id!),
    onSuccess: () => {
      toast.success('Admisibilidad aprobada — la alianza pasó a análisis');
      queryClient.invalidateQueries({ queryKey: ['expediente', id] });
      queryClient.invalidateQueries({ queryKey: ['alianza-expediente', id] });
      queryClient.invalidateQueries({ queryKey: ['bandeja-alianzas'] });
      queryClient.invalidateQueries({ queryKey: ['gestion-expedientes'] });
    },
    onError: (err: any) =>
      toast.error(err.response?.data?.error?.message || 'No se pudo iniciar el análisis'),
  });

  // ALIANZA: cerrar una observación. Cuando no queda ninguna abierta, el backend
  // devuelve el expediente a EN_ANALISIS (es lo que reabre el botón de elevar).
  const resolverObsAlianzaMutation = useMutation({
    mutationFn: (observacionId: string) => observacionCheckService.resolver(observacionId),
    onSuccess: () => {
      toast.success('Observación resuelta');
      queryClient.invalidateQueries({ queryKey: ['expediente', id] });
      queryClient.invalidateQueries({ queryKey: ['alianza-expediente', id] });
      queryClient.invalidateQueries({ queryKey: ['bandeja-alianzas'] });
      queryClient.invalidateQueries({ queryKey: ['gestion-expedientes'] });
    },
    onError: (err: any) =>
      toast.error(err.response?.data?.error?.message || 'No se pudo resolver la observación'),
  });

  // ALIANZA: destildar el check. Dar por subsanada es un juicio y un juicio se
  // corrige: se tildó de más, se releyó el adjunto y no era, entró un archivo nuevo.
  // Si el expediente había vuelto solo a EN_ANALISIS, el backend lo devuelve a
  // OBSERVADO.
  const reabrirObsAlianzaMutation = useMutation({
    mutationFn: (observacionId: string) => observacionCheckService.reabrir(observacionId),
    onSuccess: () => {
      toast.success('Observación reabierta');
      queryClient.invalidateQueries({ queryKey: ['expediente', id] });
      queryClient.invalidateQueries({ queryKey: ['alianza-expediente', id] });
      queryClient.invalidateQueries({ queryKey: ['bandeja-alianzas'] });
      queryClient.invalidateQueries({ queryKey: ['gestion-expedientes'] });
    },
    onError: (err: any) =>
      toast.error(err.response?.data?.error?.message || 'No se pudo reabrir la observación'),
  });

  // --- RECONOCIMIENTO: acciones de fase (botones top-right) ---
  const [showReconRechazarModal, setShowReconRechazarModal] = useState(false);
  const [reconRechazarEndpoint, setReconRechazarEndpoint] = useState<string | null>(null);
  const [reconMotivo, setReconMotivo] = useState('');
  const [reconModalTitulo, setReconModalTitulo] = useState('Registrar');
  const [reconModalLabel, setReconModalLabel] = useState('Motivo');

  // Archivado forzado del expediente (escape hatch interno: trámite estancado/abandonado)
  const [showArchivarForzadoModal, setShowArchivarForzadoModal] = useState(false);
  const [archivarMotivo, setArchivarMotivo] = useState('');
  const archivarForzadoMutation = useMutation({
    mutationFn: () => expedienteService.archivar(id!, { forzar: true, motivo: archivarMotivo.trim() }),
    onSuccess: () => {
      toast.success('Expediente archivado');
      setShowArchivarForzadoModal(false);
      setArchivarMotivo('');
      queryClient.invalidateQueries({ queryKey: ['expediente', id] });
      queryClient.invalidateQueries({ queryKey: ['gestion-expedientes'] });
    },
    onError: (err: any) => toast.error(err.response?.data?.error?.message || 'Error al archivar el expediente'),
  });

  const reconMutation = useMutation({
    mutationFn: ({ endpoint, body }: { endpoint: string; body?: Record<string, unknown> }) =>
      reconocimientoService.accion(id!, endpoint, body),
    onSuccess: (resp: any) => {
      toast.success(resp?.data?.message || 'Acción realizada');
      // Si la acción devolvió un PDF (p. ej. publicar atributos), se descarga solo.
      const pdfBase64 = resp?.data?.data?.pdfBase64;
      if (pdfBase64) {
        try {
          const bytes = Uint8Array.from(atob(pdfBase64), (c) => c.charCodeAt(0));
          const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
          const link = document.createElement('a');
          link.href = url;
          link.download = resp?.data?.data?.filename || 'documento.pdf';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        } catch { /* descarga best-effort */ }
      }
      setShowReconRechazarModal(false);
      setReconRechazarEndpoint(null);
      setReconMotivo('');
      queryClient.invalidateQueries({ queryKey: ['expediente', id] });
      queryClient.invalidateQueries({ queryKey: ['gestion-expedientes'] });
    },
    onError: (err: any) => toast.error(err.response?.data?.error?.message || 'Error al ejecutar la acción'),
  });

  const handleReconAccion = (accion: ReconAccion) => {
    if (accion.kind === 'obs') {
      setTab('obs');
      return;
    }
    // "No presentado" en la etapa de afiliados: mismo circuito que en
    // admisibilidad/análisis (informe al Secretario → TEP), con el texto de la
    // etapa del provisorio precargado y el plazo de 4 meses ya resuelto.
    if (accion.kind === 'noPresentado') {
      // Fases del TRÁMITE (atributos / documentación): es el mismo circuito y el
      // mismo texto que en reempadronamiento — lo que no se presentó es la
      // documentación del expediente, no el padrón ni las autoridades electas.
      if (accion.key === 'recon_no_presentado_tramite') {
        if (!expediente) return;
        void handleAccion('no_presentado');
        return;
      }
      // Etapa de autoridades definitivas: mismo circuito, otro objeto (no es el
      // padrón lo que falta, son las autoridades electas).
      if (accion.key === 'recon_no_presentado_autoridades') {
        setInformeSimpleConfig({
          basePath: 'no-presentado',
          endpoint: 'elevar-secretario',
          titulo: `Dar por no presentadas las autoridades definitivas — ${expediente!.numero}`,
          textoInicial: textoInformeNoPresentadoAutoridades({
            numero: expediente!.numero,
            estado: expediente!.estado,
            partido: expediente!.partido,
            observaciones: expediente!.observaciones,
            plazoVence: plazoAfiliados?.vence ?? null,
            plazoCuatroMesesVencido,
          }),
          ayuda:
            'El partido no presentó sus autoridades definitivas: venció el plazo de 4 meses del ' +
            'reconocimiento provisorio, o venció el plazo de una observación sin que respondiera. Se eleva el ' +
            'informe al Secretario Electoral, que lo verá en la columna Resolución. Tildá al costado las ' +
            'observaciones que quieras mostrarle: se agregan al final del informe.',
          confirmMsg:
            '¿Tener por NO PRESENTADAS las autoridades definitivas y elevar el informe al Secretario?\n\n' +
            'El expediente pasa a "No Presentado" y queda a resolución del Secretario Electoral.',
          successMsg: 'Autoridades dadas por no presentadas. Informe elevado al Secretario.',
          conHistorial: true,
          // SIN check de aviso al partido: el informe al Secretario es de la vía INTERNA
          // del organismo (SEP → Secretario). Al partido no se le manda ni el PDF ni el
          // aviso de que el expediente avanzó — el paso no le pide nada ni resuelve nada,
          // y el aviso lo dejaba esperando una novedad que todavía no existe. Se le
          // notifica cuando hay una decisión (resolución) o algo que responder
          // (observación/intimación). Vale para TODOS los trámites.
        });
        return;
      }
      setInformeSimpleConfig({
        basePath: 'no-presentado',
        endpoint: 'elevar-secretario',
        titulo: `Dar por no presentado el padrón de afiliados — ${expediente!.numero}`,
        textoInicial: textoInformeNoPresentadoAfiliados({
          numero: expediente!.numero,
          estado: expediente!.estado,
          partido: expediente!.partido,
          observaciones: expediente!.observaciones,
          plazoVence: plazoAfiliados?.vence ?? null,
        }),
        ayuda:
          'El partido no presentó el padrón de afiliados dentro del plazo de 4 meses del reconocimiento ' +
          'provisorio. Se eleva el informe al Secretario Electoral, que lo verá en la columna Resolución. ' +
          'Tildá al costado las observaciones que quieras mostrarle: se agregan al final del informe.',
        confirmMsg:
          '¿Tener por NO PRESENTADO el padrón de afiliados y elevar el informe al Secretario?\n\n' +
          'El expediente pasa a "No Presentado" y queda a resolución del Secretario Electoral.',
        successMsg: 'Padrón dado por no presentado. Informe elevado al Secretario.',
        conHistorial: true,
        // SIN check de aviso al partido: el informe al Secretario es de la vía INTERNA
        // del organismo (SEP → Secretario). Al partido no se le manda ni el PDF ni el
        // aviso de que el expediente avanzó — el paso no le pide nada ni resuelve nada,
        // y el aviso lo dejaba esperando una novedad que todavía no existe. Se le
        // notifica cuando hay una decisión (resolución) o algo que responder
        // (observación/intimación). Vale para TODOS los trámites.
      });
      return;
    }
    // Iniciar trámite: la apertura del expediente es un acto formal y genera su
    // informe firmado. Sin panel de observaciones (todavía no hay nada observado) y
    // con el aviso al partido tildado: es su acuse de que el trámite quedó abierto.
    if (accion.endpoint === 'iniciar-tramite') {
      setInformeSimpleConfig({
        endpoint: 'iniciar-tramite',
        titulo: 'Informe de inicio de trámite',
        ayuda:
          'Se genera el informe de apertura del expediente, firmado por la Dirección de Despacho y '
          + 'Registro. Revisá los datos de quien presentó antes de guardar.',
        confirmMsg:
          '¿Iniciar el trámite? Se guardará el informe de inicio y comenzará la revisión de los atributos.',
        successMsg: 'Trámite iniciado',
        labelGuardar: 'Iniciar trámite',
        // Endpoint migrado a multipart: acepta respaldo (captura, correo, nota escaneada).
        conAdjuntos: true,
        conAvisoPartido: true,
        avisoPartidoPorDefecto: true,
        labelAvisoPartido: 'Enviar el informe de inicio al partido por correo',
        subtextoAvisoPartido: 'Se le adjunta el PDF del informe de inicio. Le llega a TODOS los iniciadores declarados en el trámite, al domicilio electrónico del partido y a quien abrió el expediente.',
        textoInicial: textoInformeInicioTramite({
          numero: expediente!.numero,
          partido: expediente!.partido,
          iniciador: expediente!.creador,
          // Los iniciadores adicionales viven en datosFormulario (los guarda el
          // wizard externo): el informe de inicio es lo que los acredita.
          iniciadoresAdicionales: expediente!.datosFormulario?.iniciadoresAdicionales,
        }),
      });
      return;
    }
    // Elevar los atributos verificados al Secretario, que es quien publica. Lleva el
    // panel de observaciones (para adjuntar antecedentes) y el aviso al partido
    // tildado: el mismo acto lo intima por los Arts. 17/18/19 y le manda la ficha.
    if (accion.endpoint === 'elevar-secretario-atributos') {
      setInformeSimpleConfig({
        endpoint: 'elevar-secretario-atributos',
        titulo: 'Elevar atributos al Secretario',
        ayuda:
          'Redactá el informe al Secretario Electoral, que es quien ordena la publicación. El PDF '
          + 'lista los atributos y anexa los símbolos verificados. Tildá al costado las intimaciones '
          + 'que quieras mostrarle: se agregan al final del informe.',
        confirmMsg:
          '¿Elevar los atributos al Secretario? El expediente pasa a su bandeja para que los publique.',
        successMsg: 'Atributos elevados al Secretario',
        labelGuardar: 'Elevar al Secretario',
        // Endpoint migrado a multipart: acepta respaldo (captura, correo, nota escaneada).
        conAdjuntos: true,
        conHistorial: true,
        // ESTE CHECK SE QUEDA, y es la excepción a la regla de los informes al
        // Secretario. No es un "aviso de que el expediente avanzó": es el acto que
        // INTIMA al partido por los Arts. 17/18/19 y le manda el modelo de ficha de
        // afiliación del Art. 17. Sin él el partido no recibe la intimación y el
        // trámite queda trabado. Arranca DESTILDADO y el subtexto lo advierte.
        conAvisoPartido: true,
        labelAvisoPartido: 'Intimar al partido por correo (Arts. 17/18/19) y enviarle la ficha de afiliación',
        subtextoAvisoPartido:
          '⚠️ Si no lo tildás, el partido NO recibe la intimación. Al tildarlo se le adjunta '
          + 'el informe y el modelo de ficha de afiliación del Art. 17, con las '
          + 'especificaciones de impresión exigidas.',
        textoInicial: textoInformeElevarSecretarioAtributos({
          numero: expediente!.numero,
          partido: expediente!.partido,
        }),
      });
      return;
    }
    // Publicar atributos: abre el modal de edición del cuerpo (borrador / publicar).
    if (accion.endpoint === 'publicar-atributos') {
      setShowPublicarAtributos(true);
      return;
    }
    // Etapa DOCUMENTACIÓN: "Elevar al Secretario" / "Elevar al TEP" abren un modal
    // de informe (texto libre, sin checklist) con borrador + guardar informe.
    if (accion.endpoint === 'elevar-secretario-documentacion') {
      setInformeSimpleConfig({
        endpoint: 'elevar-secretario-documentacion',
        titulo: 'Informe al Secretario (Documentación)',
        ayuda: 'Redactá el informe al Secretario. Se generó tras verificar la totalidad de la documentación. Podés dar formato al texto.',
        confirmMsg: '¿Elevar la documentación al Secretario? Se guardará el informe y el trámite pasará a su revisión.',
        successMsg: 'Documentación elevada al Secretario',
        // Endpoint migrado a multipart: acepta respaldo (captura, correo, nota escaneada).
        conAdjuntos: true,
        conHistorial: true,
        // El aviso al partido arranca DESTILDADO: estos informes son de la vía interna
        // SEP → Secretario → TEP. Si se tilda, al partido le llega solo el aviso de que
        // SIN check de aviso al partido: el informe al Secretario es de la vía INTERNA
        // del organismo (SEP → Secretario). Al partido no se le manda ni el PDF ni el
        // aviso de que el expediente avanzó — el paso no le pide nada ni resuelve nada,
        // y el aviso lo dejaba esperando una novedad que todavía no existe. Se le
        // notifica cuando hay una decisión (resolución) o algo que responder
        // (observación/intimación). Vale para TODOS los trámites.
        textoInicial: informeAlSecretario('Se informa al Señor Secretario Electoral que se ha completado la revisión de la documentación presentada por la agrupación en trámite de reconocimiento (Art. 18/19, Ley XII N° 23), analizando la totalidad de los documentos requeridos.\n\nEn virtud de lo expuesto, se eleva la documentación a su consideración para su revisión.'),
      });
      return;
    }
    if (accion.endpoint === 'elevar-tep-documentacion') {
      setInformeSimpleConfig({
        endpoint: 'elevar-tep-documentacion',
        titulo: 'Informe al TEP (Documentación)',
        ayuda: 'Redactá el informe que se eleva al Tribunal Electoral Provincial. Podés dar formato al texto.',
        confirmMsg: '¿Elevar la documentación al TEP? Se guardará el informe y el trámite pasará a resolución del Tribunal.',
        successMsg: 'Documentación elevada al TEP',
        // Endpoint migrado a multipart: acepta respaldo (captura, correo, nota escaneada).
        conAdjuntos: true,
        conHistorial: true,
        // El aviso al partido arranca DESTILDADO: estos informes son de la vía interna
        // SEP → Secretario → TEP. Si se tilda, al partido le llega solo el aviso de que
        // el expediente avanzó (el informe NO se adjunta: es documentación interna).
        conAvisoPartido: true,
        labelAvisoPartido: 'Avisar al partido por correo que el expediente avanzó',
        subtextoAvisoPartido: 'El informe NO se adjunta: es documentación interna del organismo.',
        textoInicial: informeAlTep('Se eleva al Tribunal Electoral Provincial (TEP) la documentación presentada por la agrupación en trámite de reconocimiento (Art. 18/19, Ley XII N° 23), habiéndose verificado el cumplimiento de los requisitos documentales.\n\nEn virtud de lo expuesto, se solicita al Tribunal se sirva resolver sobre el otorgamiento del reconocimiento provisorio, conforme a derecho.'),
      });
      return;
    }
    // El Secretario devuelve el expediente un paso atrás en vez de elevarlo. El
    // motivo es obligatorio: es el cuerpo del informe que le llega a la SEP.
    if (accion.endpoint === 'devolver-sep') {
      setInformeSimpleConfig({
        endpoint: 'devolver-sep',
        titulo: 'Devolver el expediente a la directora',
        ayuda:
          'Explicá por qué devolvés el expediente. El informe se envia por correo a la SEP ' +
          '(registro@electoralchubut.gob.ar). '+
          '  El partido NO recibe copia.',
        confirmMsg:
          '¿Devolver el expediente a la directora?\n\n' +
          'Vuelve al paso anterior',
        successMsg: 'Expediente devuelto a la SEP',
        conHistorial: true,
        // Endpoint migrado a multipart: acepta respaldo (captura, correo, nota escaneada).
        conAdjuntos: true,
        ayudaAdjuntos: 'Si hace falta, adjuntá lo que respalda la devolución.',
        textoInicial: informeALaSep(
          'Se devuelve el presente expediente a la Secretaría Electoral Permanente, sin elevarlo al ' +
          'Tribunal Electoral Provincial, por los motivos que se exponen a continuación.\n\n' +
          '[Detallar acá las observaciones que motivan la devolución.]\n\n' +
          'Se solicita subsanar lo señalado y elevar nuevamente el informe.',
        ),
      });
      return;
    }
    // Etapa AUTORIDADES definitivas: mismo par de modales que documentación —
    // la SEP informa al Secretario y el Secretario eleva al TEP.
    if (accion.endpoint === 'elevar-secretario-definitivo') {
      setInformeSimpleConfig({
        endpoint: 'elevar-secretario-definitivo',
        titulo: 'Informe al Secretario (Autoridades definitivas)',
        ayuda: 'Redactá el informe al Secretario. Se genera tras verificar las autoridades definitivas presentadas y la documentación de la etapa. Podés dar formato al texto.',
        confirmMsg: '¿Elevar las autoridades definitivas al Secretario? Se guardará el informe y el trámite pasará a su revisión.',
        successMsg: 'Autoridades definitivas elevadas al Secretario',
        // Endpoint migrado a multipart: acepta respaldo (captura, correo, nota escaneada).
        conAdjuntos: true,
        conHistorial: true,
        // El aviso al partido arranca DESTILDADO: estos informes son de la vía interna
        // SEP → Secretario → TEP. Si se tilda, al partido le llega solo el aviso de que
        // SIN check de aviso al partido: el informe al Secretario es de la vía INTERNA
        // del organismo (SEP → Secretario). Al partido no se le manda ni el PDF ni el
        // aviso de que el expediente avanzó — el paso no le pide nada ni resuelve nada,
        // y el aviso lo dejaba esperando una novedad que todavía no existe. Se le
        // notifica cuando hay una decisión (resolución) o algo que responder
        // (observación/intimación). Vale para TODOS los trámites.
        textoInicial: informeAlSecretario('Se informa al Señor Secretario Electoral que la agrupación en trámite de reconocimiento ha presentado sus autoridades partidarias definitivas, electas en sus elecciones internas, junto con la Publicación de llamado a elecciones, el Cronograma de elecciones, el Acta de asamblea donde se proclamaron las autoridades, la Resolución de la Proclamación de Autoridades y el Acta de Designación de Apoderados, habiéndose verificado la totalidad de la documentación.\n\nEn virtud de lo expuesto, se elevan las autoridades definitivas a su consideración para su revisión.'),
      });
      return;
    }
    if (accion.endpoint === 'elevar-tep-definitivo') {
      setInformeSimpleConfig({
        endpoint: 'elevar-tep-definitivo',
        titulo: 'Informe al TEP (Autoridades definitivas)',
        ayuda: 'Redactá el informe que se eleva al Tribunal Electoral Provincial. Podés dar formato al texto.',
        confirmMsg: '¿Elevar el expediente al TEP? Se guardará el informe y el trámite pasará a resolución del Tribunal.',
        successMsg: 'Expediente elevado al TEP para el reconocimiento definitivo',
        // Endpoint migrado a multipart: acepta respaldo (captura, correo, nota escaneada).
        conAdjuntos: true,
        conHistorial: true,
        // El aviso al partido arranca DESTILDADO: estos informes son de la vía interna
        // SEP → Secretario → TEP. Si se tilda, al partido le llega solo el aviso de que
        // el expediente avanzó (el informe NO se adjunta: es documentación interna).
        conAvisoPartido: true,
        labelAvisoPartido: 'Avisar al partido por correo que el expediente avanzó',
        subtextoAvisoPartido: 'El informe NO se adjunta: es documentación interna del organismo.',
        textoInicial: informeAlTep('Se eleva al Tribunal Electoral Provincial (TEP) el expediente de la agrupación en trámite de reconocimiento, habiéndose verificado el padrón de afiliados exigido por la Ley XII N° 23 y las autoridades partidarias definitivas electas en sus elecciones internas.\n\nEn virtud de lo expuesto, se solicita al Tribunal se sirva resolver sobre el otorgamiento del reconocimiento definitivo, conforme a derecho.'),
      });
      return;
    }
    // Informe al Secretario (paso previo a elevar al TEP): abre su modal.
    if (accion.endpoint === 'informe-secretario') {
      setShowInformeSecretario(true);
      return;
    }
    // Elevar atributos al TEP: abre el modal del Informe al TEP (edición de texto,
    // descarga y guardado como documento del expediente).
    if (accion.endpoint === 'elevar-tep-atributos') {
      setShowInformeTep(true);
      return;
    }
    // Resolución del TEP (atributos: aprueba/rechaza; documentación: otorga/deniega
    // el provisorio): abre el mismo modal que obliga a adjuntar el PDF + N° de acta
    // y fecha.
    if (accion.endpoint && RESOLUCIONES_TEP[accion.endpoint]) {
      setResolucionTepConfig(RESOLUCIONES_TEP[accion.endpoint]);
      return;
    }
    if (accion.kind === 'motivo') {
      setReconRechazarEndpoint(accion.endpoint);
      setReconMotivo('');
      setReconModalTitulo(accion.modalTitulo || 'Registrar');
      setReconModalLabel(accion.modalLabel || 'Motivo');
      setShowReconRechazarModal(true);
      return;
    }
    // kind 'endpoint' — confirmaciones para acciones sensibles
    const confirmaciones: Record<string, string> = {
      'iniciar-tramite': '¿Iniciar el trámite? Se registrará el inicio y el expediente quedará Presentado.',
      'elevar-secretario-atributos': '¿Elevar los atributos al Secretario para su análisis?',
      'publicar-atributos': '¿Publicar los atributos para oposición?',
      'elevar-tep-atributos': '¿Elevar los atributos al TEP?',
      'tep-aprueba-atributos': '¿Registrar que el TEP aprobó los atributos? Se habilitará la documentación.',
      'elevar-secretario-documentacion': '¿Elevar la documentación al Secretario para su análisis?',
      'elevar-tep-documentacion': '¿Elevar la documentación al TEP?',
      'tep-otorga-documentacion': '¿Registrar que el TEP otorgó el reconocimiento provisorio?',
      'pasar-autoridades-pendientes':
        '¿Dar por cumplido el requisito de afiliados y habilitar la etapa de autoridades definitivas?\n\n' +
        'Se le avisará al partido que debe convocar elecciones internas y cargar sus autoridades electas.',
      'aceptar-subsanacion': '¿Aceptar la subsanación? Se darán por resueltas las observaciones pendientes.',
      'volver-atras': '¿Volver atrás y reabrir el trámite para su corrección? Se cerrarán las observaciones pendientes.',
    };
    // No se acepta una subsanación de algo que el partido nunca respondió (el
    // botón ya viene deshabilitado; esto cubre cualquier otra vía).
    if (accion.endpoint === 'aceptar-subsanacion' && getSubsanacionInfo('analisis').sinResponder > 0) {
      toast.error('No hay respuestas del partido para dar por subsanadas.');
      return;
    }
    const msg = accion.endpoint ? confirmaciones[accion.endpoint] : undefined;
    if (msg && !confirm(msg)) return;
    if (accion.endpoint) reconMutation.mutate({ endpoint: accion.endpoint });
  };

  // Informe al Secretario "simple" (sin PDF ni modal) de los trámites que todavía
  // no tienen circuito propio: cambio de autoridades, fusión, etc. Reconocimiento
  // y reempadronamiento tienen el suyo, con informe editable en PDF.
  const [informeSecretario, setInformeSecretario] = useState('');
  const enviarSecretarioMutation = useMutation({
    mutationFn: () => api.post('/expedientes/' + id + '/enviar-secretario', { informe: informeSecretario }),
    onSuccess: () => {
      toast.success('Informe enviado al Secretario Electoral');
      setInformeSecretario('');
      queryClient.invalidateQueries({ queryKey: ['expediente', id] });
      queryClient.invalidateQueries({ queryKey: ['gestion-expedientes'] });
    },
    onError: (err: any) => toast.error(err.response?.data?.error?.message || 'Error al enviar informe'),
  });

  // Cargar borrador al montar si esta en analisis
  const { data: borrador } = useQuery({
    queryKey: ['borrador-obs', id],
    queryFn: async () => {
      const r = await api.get('/expedientes/' + id + '/borrador-observacion');
      return r.data.data;
    },
    enabled: !!id && ['EN_ANALISIS', 'EN_ADMISIBILIDAD', 'INTIMADO'].includes(expediente?.estado || '') && ['ADMINISTRADOR', 'SUPERVISOR', 'ANALISTA', 'AUTORIDAD_FIRMANTE'].includes(user?.rol || ''),
  });

  // Setear borrador cuando carga (observación en análisis)
  if (borrador?.texto && expediente?.estado === 'EN_ANALISIS' && !obsTexto) {
    setObsTexto(borrador.texto);
  }

  useEffect(() => {
    const preTexto = (location.state as any)?.contenidoPrecargado;
    if (preTexto && !obsTexto) setObsTexto(preTexto);
    const preOrigen = (location.state as any)?.origenObservacion;
    if (preOrigen && !obsOrigen) setObsOrigen(preOrigen);
  }, []);

  const subsanarMutation = useMutation({
    mutationFn: async () => {
      // Primero subir archivos si hay
      for (const archivo of subsanarArchivos) {
        const formData = new FormData();
        formData.append('archivo', archivo.file);
        formData.append('expedienteId', id!);
        formData.append('tipo', 'SUBSANACION');
        formData.append('titulo', 'Subsanación: ' + archivo.file.name);
        formData.append('documentoSubsanadoId', archivo.documentoId);
        await documentoService.upload(formData);
      }
      // Marcar observaciones como subsanadas
      const obsNoSubsanadas = expediente?.observaciones?.filter(o => !o.subsanada) || [];
      for (const obs of obsNoSubsanadas) {
        await api.post('/expedientes/' + id + '/observaciones/' + obs.id + '/subsanar', { texto: subsanarTexto });
      }
    },
    onSuccess: () => {
      toast.success('Observaciones subsanadas correctamente');
      setShowSubsanarModal(false);
      setSubsanarTexto('');
      setSubsanarArchivos([]);
      queryClient.invalidateQueries({ queryKey: ['expediente', id] });
      queryClient.invalidateQueries({ queryKey: ['gestion-expedientes'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-alertas-internas'] });
    },
    onError: (err: any) => toast.error(err.response?.data?.error?.message || 'Error al subsanar'),
  });

  const volverAnalisisMutation = useMutation({
    mutationFn: () => api.post('/expedientes/' + id + '/volver-analisis'),
    onSuccess: () => {
      toast.success('Expediente vuelto a revisión');
      queryClient.invalidateQueries({ queryKey: ['expediente', id] });
      queryClient.invalidateQueries({ queryKey: ['gestion-expedientes'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-alertas-internas'] });
    },
    onError: (err: any) => toast.error(err.response?.data?.error?.message || 'Error al volver a revisión'),
  });


  const asignarMutation = useMutation({
    mutationFn: () => expedienteService.asignar(id!, analistaId),
    onSuccess: () => {
      toast.success('Expediente asignado');
      setShowAsignarModal(false);
      setAnalistaId('');
      queryClient.invalidateQueries({ queryKey: ['expediente', id] });
      queryClient.invalidateQueries({ queryKey: ['gestion-expedientes'] });
    },
    onError: (err: any) => toast.error(err.response?.data?.error?.message || 'Error al asignar'),
  });

  const observarMutation = useMutation({
    mutationFn: () => {
      const datos = new FormData();
      datos.append('texto', observaciones);
      return expedienteService.observar(id!, datos);
    },
    onSuccess: () => {
      toast.success('Observaciones registradas');
      setShowObservarModal(false);
      setObservaciones('');
      queryClient.invalidateQueries({ queryKey: ['expediente', id] });
      queryClient.invalidateQueries({ queryKey: ['gestion-expedientes'] });
    },
    onError: (err: any) => toast.error(err.response?.data?.error?.message || 'Error al observar'),
  });

  const aprobarMutation = useMutation({
    mutationFn: () => expedienteService.aprobar(id!, { resolucionNumero, resolucionTexto }),
    onSuccess: () => {
      toast.success('Expediente aprobado');
      setShowAprobarModal(false);
      queryClient.invalidateQueries({ queryKey: ['expediente', id] });
      queryClient.invalidateQueries({ queryKey: ['gestion-expedientes'] });
    },
    onError: (err: any) => toast.error(err.response?.data?.error?.message || 'Error al aprobar'),
  });

  const rechazarMutation = useMutation({
    mutationFn: () => expedienteService.rechazar(id!, { motivo: motivoRechazo, resolucionNumero }),
    onSuccess: () => {
      toast.success('Expediente rechazado');
      setShowRechazarModal(false);
      queryClient.invalidateQueries({ queryKey: ['expediente', id] });
      queryClient.invalidateQueries({ queryKey: ['gestion-expedientes'] });
    },
    onError: (err: any) => toast.error(err.response?.data?.error?.message || 'Error al rechazar'),
  });

  const archivarMutation = useMutation({
    mutationFn: () => expedienteService.archivar(id!),
    onSuccess: () => {
      toast.success('Expediente archivado');
      queryClient.invalidateQueries({ queryKey: ['expediente', id] });
      queryClient.invalidateQueries({ queryKey: ['gestion-expedientes'] });
    },
    onError: (err: any) => toast.error(err.response?.data?.error?.message || 'Error al archivar'),
  });

  // ===== Mutaciones de admisibilidad (replicadas desde /gestion) =====
  const invalidateExpediente = () => {
    queryClient.invalidateQueries({ queryKey: ['expediente', id] });
    queryClient.invalidateQueries({ queryKey: ['dashboard-alertas-internas'] });
    queryClient.invalidateQueries({ queryKey: ['gestion-expedientes'] });
  };

  const iniciarAdmisibilidadMutation = useMutation({
    mutationFn: () => api.post(`/admisibilidad/${id}/iniciar`),
    onSuccess: () => { toast.success('Admisibilidad iniciada'); invalidateExpediente(); },
    onError: (e: any) => toast.error(e.response?.data?.error?.message || 'Error'),
  });

  const iniciarAnalisisMutation = useMutation({
    mutationFn: () => api.post(`/expedientes/${id}/iniciar-analisis`),
    onSuccess: () => { toast.success('Análisis iniciado'); invalidateExpediente(); },
    onError: (e: any) => toast.error(e.response?.data?.error?.message || 'Error'),
  });

  const aprobarAdmisibilidadMutation = useMutation({
    mutationFn: () => api.post(`/admisibilidad/${id}/aprobar`),
    onSuccess: () => { toast.success('Admisibilidad aprobada — pasa a Análisis'); invalidateExpediente(); },
    onError: (e: any) => toast.error(e.response?.data?.error?.message || 'Error'),
  });

  const [showIntimarModal, setShowIntimarModal] = useState(false);
  const [textoIntimacion, setTextoIntimacion] = useState('');

  const intimarMutation = useMutation({
    mutationFn: (texto: string) => api.post(`/admisibilidad/${id}/intimar`, { observaciones: texto }),
    onSuccess: () => {
      toast.success('Intimación enviada');
      setShowIntimarModal(false);
      setTextoIntimacion('');
      queryClient.invalidateQueries({ queryKey: ['borrador-obs', id] });
      invalidateExpediente();
    },
    onError: (e: any) => toast.error(e.response?.data?.error?.message || 'Error'),
  });

  // Setear borrador cuando carga (intimación en admisibilidad)
  if (borrador?.texto && ['EN_ADMISIBILIDAD', 'INTIMADO'].includes(expediente?.estado || '') && !textoIntimacion) {
    setTextoIntimacion(borrador.texto);
  }

  const tieneRespuestaPendiente =
    expediente &&
    ['INTIMADO', 'OBSERVADO'].includes(expediente.estado) &&
    !expediente.observaciones?.some(o => o.estado === 'PENDIENTE') &&
    (expediente.observaciones?.some(o => o.estado === 'RESPONDIDA') ?? false);

  // Helpers
  const formatDate = (date?: string) => date ? new Date(date).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';
  const formatDateShort = (date?: string) => date ? new Date(date).toLocaleDateString('es-AR') : '-';

  // Antes de aceptar una subsanación, la supervisora debe haber revisado lo que
  // respondió el partido. Calculamos: (1) adjuntos de las respuestas que siguen
  // PENDIENTE de revisión (bloquean el cambio de estado) y (2) si hay respuestas
  // de simple texto (que no tienen check de "ya lo vi" y conviene recordar mirar).
  const getSubsanacionInfo = (tipo: 'analisis' | 'admisibilidad') => {
    const esAdmis = tipo === 'admisibilidad';
    // RECONOCIMIENTO verifica en una sola dimensión (admisibilidad), incluso en la
    // etapa de documentación: el pendiente se mira SIEMPRE en esa dimensión.
    const esRecon = expediente?.tipo === 'RECONOCIMIENTO';
    const adjuntosPendientes = (expediente?.documentos || []).filter(d => {
      const vinculado = esAdmis ? d.intimacionRespuestaId : d.observacionRespuestaId;
      if (!vinculado) return false;
      const estadoRevision = (esRecon || esAdmis) ? d.estadoRevisionAdmisibilidad : d.estadoRevisionAnalisis;
      return estadoRevision === 'PENDIENTE';
    }).length;
    const tieneRespuestasTexto = (expediente?.observaciones || []).some(o =>
      (esAdmis ? o.tipo === 'INTIMACION' : o.tipo !== 'INTIMACION') &&
      (!!o.respuestaTexto || o.estado === 'RESPONDIDA')
    );
    // Intimaciones/observaciones que el partido NO respondió de NINGUNA forma: sin
    // texto de respuesta, sin documentos adjuntos y sin quedar en RESPONDIDA. Si
    // hay alguna así no hay nada que subsanar: aceptar la subsanación daría por
    // cumplido algo que el partido nunca contestó. El camino es esperar el plazo y,
    // vencido, darlo por no presentado.
    // En RECONOCIMIENTO las observaciones se guardan siempre como OBSERVACION (la
    // etapa se deriva de la fase), así que no se filtran por tipo.
    const deLaEtapa = (o: any) =>
      esRecon ? true : (esAdmis ? o.tipo === 'INTIMACION' : o.tipo !== 'INTIMACION');
    // Solo las PENDIENTE: ese estado es "enviada y esperando al partido".
    // RESPONDIDA ya tiene respuesta y RESUELTA ya se cerró. Mismo criterio que
    // services/subsanacionService.ts en el backend.
    const sinResponder = (expediente?.observaciones || []).filter(o => {
      if (!deLaEtapa(o) || o.subsanada || o.estado !== 'PENDIENTE') return false;
      if (!!o.respuestaTexto) return false;
      const tieneAdjuntos = (expediente?.documentos || []).some(d =>
        (esAdmis ? d.intimacionRespuestaId : d.observacionRespuestaId) === o.id,
      );
      return !tieneAdjuntos;
    }).length;
    return { adjuntosPendientes, tieneRespuestasTexto, sinResponder };
  };

  const isVencido = expediente?.fechaVencimiento && 
    new Date(expediente.fechaVencimiento) < new Date() && 
    !['ARCHIVADO', 'APROBADO', 'RECHAZADO'].includes(expediente.estado);

  // `async` porque la elevación de alianza pide primero el borrador del informe al
  // backend (el análisis por eje se arma con datos que esta pantalla no tiene).
  const handleAccion = async (accion: string) => {
    switch (accion) {
      case 'presentar':
        presentarMutation.mutate();
        break;
      case 'iniciar_revision':
        iniciarRevisionMutation.mutate();
        break;
      case 'observar':
        setTab('obs');
        break;
      case 'aprobar':
        setShowAprobarModal(true);
        break;
      case 'rechazar':
        setShowRechazarModal(true);
        break;
      case 'archivar':
        if (confirm('¿Está seguro de archivar este expediente?')) {
          archivarMutation.mutate();
        }
        break;
      case 'aceptar_admisibilidad':
        if (confirm('¿Aceptar admisibilidad y pasar a análisis?')) {
          iniciarRevisionMutation.mutate();
        }
        break;    
      case 'en_analisis':
        if (confirm('¿Volver el expediente a revisión?')) {
          volverAnalisisMutation.mutate();
        }
        break;
      case 'iniciar_admisibilidad':
        if (expediente && TIPOS_SIN_ADMISIBILIDAD.includes(expediente.tipo)) {
          iniciarAnalisisMutation.mutate();
        } else {
          iniciarAdmisibilidadMutation.mutate();
        }
        break;
      case 'aprobar_admisibilidad':
        // Modal propio, no `confirm()`: el cartel del navegador no puede listar las
        // secciones pendientes de forma legible ni distinguir el aviso del pedido de
        // confirmación, y es lo único que se le muestra antes de aprobar una etapa.
        setPasarAnalisisAbierto(true);
        break;
      case 'intimar':
        setTab('obs');
        break;
      // Dar por no presentada la solicitud: NO archiva. Abre el modal del
      // informe al Secretario (con el historial al costado para elegir qué
      // intimaciones/observaciones mostrarle) y deja el expediente en
      // NO_PRESENTADO · En Secretario, en la columna Resolución de /gestion.
      case 'no_presentado':
        if (!expediente) break;
        setInformeSimpleConfig({
          basePath: 'no-presentado',
          endpoint: 'elevar-secretario',
          titulo: 'Dar por no presentada la solicitud — Informe al Secretario',
          textoInicial: textoInformeNoPresentado(expediente),
          ayuda:
            'Se tiene por no presentada la solicitud y se eleva el informe al Secretario Electoral, ' +
            'que lo verá en la columna Resolución. Tildá al costado las intimaciones u observaciones ' +
            'que quieras mostrarle: se agregan al final del informe, de la más antigua a la más reciente.',
          confirmMsg:
            '¿Tener por NO PRESENTADA la solicitud y elevar el informe al Secretario?\n\n' +
            'El expediente pasa a "No Presentado" y queda a resolución del Secretario Electoral.',
          successMsg: 'Solicitud dada por no presentada. Informe elevado al Secretario.',
          conHistorial: true,
          // SIN check de aviso al partido: el informe al Secretario es de la vía INTERNA
          // del organismo (SEP → Secretario). Al partido no se le manda ni el PDF ni el
          // aviso de que el expediente avanzó — el paso no le pide nada ni resuelve nada,
          // y el aviso lo dejaba esperando una novedad que todavía no existe. Se le
          // notifica cuando hay una decisión (resolución) o algo que responder
          // (observación/intimación). Vale para TODOS los trámites.
        });
        break;
      // El Secretario eleva al TEP: mismo modal, dirigido al TEP.
      case 'elevar_tep_no_presentado':
        if (!expediente) break;
        setInformeSimpleConfig({
          basePath: 'no-presentado',
          endpoint: 'elevar-tep',
          titulo: 'Elevar al TEP — expediente dado por no presentado',
          textoInicial: textoInformeTepNoPresentado(expediente),
          ayuda:
            'Se eleva el expediente al Tribunal Electoral Provincial para que resuelva. Tildá al costado ' +
            'las intimaciones u observaciones que quieras incluir en el informe.',
          confirmMsg: '¿Elevar el expediente al TEP para su resolución?',
          successMsg: 'Expediente elevado al TEP',
          conHistorial: true,
          conAvisoPartido: true,
          labelAvisoPartido: 'Avisar al partido por correo que el expediente avanzó',
          subtextoAvisoPartido: 'El informe NO se adjunta: es documentación interna del organismo. Solo se le avisa del avance.',
        });
        break;
      // ALIANZA: mismo pase que el botón "Iniciar" de la bandeja.
      case 'iniciar_analisis_alianza':
        if (!confirm(
          '¿Iniciar el análisis?\n\n' +
          'Se aprueba la admisibilidad de toda la documentación presentada y el expediente pasa a ' +
          'análisis, con los documentos pendientes de verificar en esa etapa.'
        )) break;
        iniciarAnalisisAlianzaMutation.mutate();
        break;
      // ALIANZA: elevación de la Dirección de Registro al Secretario. El cuerpo
      // arranca con el análisis por eje que arma el backend (composición Art. 26,
      // documentación Art. 27, apoderados, Junta Electoral) y es EDITABLE: es un
      // punto de partida, no un dictamen automático.
      case 'elevar_secretario_alianza': {
        if (!expediente) break;
        // No se eleva con documentos que nadie miró: al Secretario se le eleva lo YA
        // revisado. El botón NO queda en gris —un botón gris no explica nada—: se
        // pulsa y avisa cuántos faltan, igual que el check de aprobación por tipo.
        //
        // El número lo da el BACKEND junto con el borrador, no se cuenta acá: es el
        // mismo que aplica el gate al elevar, y sabe descartar los DNI huérfanos
        // (archivos de una persona cuyo DNI se corrigió, que ya no se ven en
        // /documentos). Contándolos desde el payload aparecían pendientes fantasma.
        let textoInicial = '';
        try {
          const r = await alianzaGestionService.borradorInforme(id!);
          if ((r?.pendientes ?? 0) > 0) {
            const n = r.pendientes as number;
            toast.error(
              `No es posible elevar, ${n === 1
                ? 'queda 1 documento pendiente de aprobación'
                : `quedan ${n} documentos pendientes de aprobación`}`,
            );
            break;
          }
          textoInicial = r?.texto || '';
        } catch (e: any) {
          toast.error(e.response?.data?.error?.message || 'No se pudo armar el borrador del informe');
          break;
        }
        setInformeSimpleConfig({
          basePath: 'alianzas/expedientes',
          endpoint: 'informe-tecnico',
          titulo: 'Elevar la alianza al Secretario',
          // Mismo encabezado que el resto de los informes al Secretario: destinatario
          // + agrupación, editable en el modal. El "Rawson, fecha" y el N° de
          // expediente los imprime el PDF arriba a la derecha.
          //
          // SIN `conAdvertencia`: ese bloque arma un resumen de la verificación
          // documental a partir del checklist genérico, y el cuerpo que trae el
          // backend ya dice qué requisitos del Art. 27 quedaron cumplidos y cuáles no.
          textoInicial: conEncabezadoInforme(textoInicial, {
            destinatario: 'SECRETARIO',
            nombrePartido: expediente.partido?.denominacion,
            etiquetaAgrupacion: 'Alianza',
          }),
          confirmMsg:
            '¿Elevar la alianza al Secretario? El expediente pasa a su bandeja para que la eleve ' +
            'al Tribunal Electoral Provincial, que es quien resuelve.',
          successMsg: 'Alianza elevada al Secretario',
          labelGuardar: 'Elevar al Secretario',
          // Panel lateral con las observaciones cursadas y sus respuestas: al
          // tildarlas se arma el bloque "Antecedentes" al final del informe. Es lo
          // que el Secretario necesita para ver qué se le pidió a la alianza y qué
          // contestó, sin salir del informe. Mismo panel que el resto de los
          // trámites (lo sirve GET /no-presentado/:id/historial, que es genérico
          // para cualquier expediente).
          conHistorial: true,
          // SIN check de aviso al partido: el informe al Secretario es de la vía INTERNA
          // del organismo (SEP → Secretario). Al partido no se le manda ni el PDF ni el
          // aviso de que el expediente avanzó — el paso no le pide nada ni resuelve nada,
          // y el aviso lo dejaba esperando una novedad que todavía no existe. Se le
          // notifica cuando hay una decisión (resolución) o algo que responder
          // (observación/intimación). Vale para TODOS los trámites.
        });
        break;
      }
      // ALIANZA: el Secretario eleva el expediente al TEP, que es quien resuelve.
      // Mismo modal de informe que reempadronamiento; el basePath es lo único que
      // cambia de circuito.
      case 'elevar_tep_alianza':
        if (!expediente) break;
        setInformeSimpleConfig({
          basePath: 'alianzas/expedientes',
          endpoint: 'elevar-tep',
          titulo: 'Elevar al TEP — Constitución de alianza',
          // El cuerpo viene CORTO y con el hueco del medio para escribir: lo que el
          // Tribunal tiene que resolver se redacta caso por caso. Se nombra a la
          // ALIANZA y no al partido: el encabezado del informe también la rotula
          // así (`etiquetaAgrupacion`), porque una alianza no es un partido y es lo
          // primero que se lee.
          textoInicial: conEncabezadoInforme(
            textoInformeTepAlianza({
              numero: expediente.numero,
              denominacion: alianzaDetalle?.alianza?.denominacion
                || expediente.partido?.denominacion
                || '',
            }),
            {
              destinatario: 'TEP',
              nombrePartido: alianzaDetalle?.alianza?.denominacion || expediente.partido?.denominacion,
              etiquetaAgrupacion: 'Alianza',
            },
          ),
          ayuda:
            'Redactá el informe que se eleva al Tribunal Electoral Provincial: el texto viene corto a ' +
            'propósito, con el espacio del medio para detallar qué tiene que resolver el Tribunal. ' +
            'Tildá al costado las observaciones que quieras incluir.',
          confirmMsg:
            '¿Elevar el expediente al TEP para su resolución?\n\n' +
            'El expediente pasa a "En el TEP" y el Secretario ya no resuelve sobre la alianza.',
          successMsg: 'Expediente elevado al TEP',
          conHistorial: true,
          // CON check de aviso, a diferencia del informe al Secretario: acá el
          // expediente sale del organismo y las agrupaciones tienen derecho a saber
          // que su solicitud quedó a resolución del Tribunal. Destildado por
          // defecto, porque el informe en sí es interno.
          conAvisoPartido: true,
          labelAvisoPartido: 'Avisar a las agrupaciones por correo que el expediente avanzó',
          subtextoAvisoPartido:
            'El informe NO se adjunta: es documentación interna del organismo. Si no se tilda, a las ' +
            'agrupaciones no les llega nada.',
        });
        break;
      // Las tres salidas del TEP en alianzas viven detrás de un solo botón, igual
      // que en reempadronamiento.
      case 'resolucion_tep_alianza':
        setOpcionesTepAlianza(true);
        break;
      case 'tep_aprueba_alianza':
        setResolucionTepConfig({
          basePath: 'alianzas/expedientes',
          endpoint: 'tep-aprueba',
          titulo: 'TEP aprueba la constitución de la alianza',
          labelMotivo: 'Texto de la resolución',
          // SIN labelNumero/labelFecha/prefijoNumero: los defaults del modal ya son
          // los del TEP (ACTA), que es el instrumento que corresponde acá.
          requiereMotivo: false,
          motivoInicial: textoTepApruebaAlianza(datosTextoAlianza),
          tono: 'ok',
          confirmLabel: 'Registrar aprobación',
          confirmMsg:
            '¿Registrar que el TEP APROBÓ la constitución de la alianza?\n\n' +
            'El expediente pasa a "Vigente" y la alianza también, con lo que queda habilitada para ' +
            'oficializar listas de candidatos.',
          successMsg: 'Constitución de alianza aprobada',
          conAvisoPartido: true,
          // Tildado por defecto: es el acto que resuelve el trámite y las
          // agrupaciones tienen que recibir el acta, no sólo enterarse de que
          // existe. Se puede destildar si se notifica por cédula.
          avisoPartidoPorDefecto: true,
          labelAvisoPartido: 'Notificar la resolución a las agrupaciones por correo',
        });
        break;
      case 'tep_rechaza_alianza':
        setResolucionTepConfig({
          basePath: 'alianzas/expedientes',
          endpoint: 'tep-rechaza',
          titulo: 'TEP rechaza la constitución de la alianza',
          labelMotivo: 'Texto de la resolución',
          requiereMotivo: true,
          motivoInicial: textoTepRechazaAlianza(datosTextoAlianza),
          tono: 'bad',
          confirmLabel: 'Registrar rechazo',
          confirmMsg:
            '¿Registrar que el TEP RECHAZÓ la constitución de la alianza?\n\n' +
            'El expediente pasa a "Rechazado". La alianza NO cambia de estado: declarar su caducidad ' +
            'es un acto aparte, con su propio expediente.',
          successMsg: 'Rechazo registrado',
          conAvisoPartido: true,
          avisoPartidoPorDefecto: true,
          labelAvisoPartido: 'Notificar el rechazo a las agrupaciones por correo',
        });
        break;
      // Aplazamiento: el expediente vuelve a análisis. El plazo NO arranca con el
      // acta —lo abre la observación que curse la supervisora con lo que el
      // Tribunal determinó—, así que acá no se pide ni se recalcula ninguna fecha.
      case 'tep_aplaza_alianza':
        setResolucionTepConfig({
          basePath: 'alianzas/expedientes',
          endpoint: 'tep-aplaza',
          titulo: 'TEP aplaza la resolución',
          labelMotivo: 'Texto de la resolución',
          requiereMotivo: true,
          motivoInicial: textoTepAplazaAlianza(datosTextoAlianza),
          tono: 'ok',
          confirmLabel: 'Registrar aplazamiento',
          confirmMsg:
            '¿Registrar que el TEP APLAZÓ la resolución?\n\n' +
            'El expediente vuelve a "En análisis" y se le quita el vencimiento. El plazo del aplazo ' +
            'empieza a correr cuando la supervisora curse la observación con lo que el Tribunal ' +
            'determinó.',
          successMsg: 'Aplazamiento registrado',
          conAvisoPartido: true,
          avisoPartidoPorDefecto: true,
          labelAvisoPartido: 'Notificar el aplazamiento y el plazo a las agrupaciones por correo',
        });
        break;
      // ALIANZA: el Secretario devuelve el expediente a la SEP. Disponible también
      // en RECHAZADO, para reabrir el trámite sin notificar a las agrupaciones.
      case 'devolver_sep_alianza': {
        if (!expediente) break;
        // El destino NO es siempre la SEP: desde EN_TEP la devolución sólo DESHACE
        // la elevación y el expediente se queda en el Secretario, que es quien
        // decide si vuelve a elevarlo o lo baja a análisis (ver
        // DESTINO_DEVOLUCION_ALIANZA en el backend). Con el texto fijo, el
        // Secretario confirmaba una devolución a la SEP que no iba a ocurrir.
        const deshaceElevacion = expediente.estado === 'EN_TEP';
        setInformeSimpleConfig({
          basePath: 'alianzas/expedientes',
          endpoint: 'devolver-sep',
          titulo: deshaceElevacion ? 'Deshacer la elevación al TEP' : 'Devolver el expediente a la SEP',
          textoInicial: informeALaSep(
            deshaceElevacion
              ? 'Se deja sin efecto la elevación al Tribunal Electoral Provincial del presente ' +
                'expediente de constitución de alianza, por los motivos que se exponen a ' +
                'continuación.\n\n[Detallar acá los motivos por los que se deshace la elevación.]'
              : 'Se devuelve el presente expediente de constitución de alianza a la Secretaría Electoral ' +
                'Permanente, sin elevarlo al Tribunal Electoral Provincial, por los motivos que se ' +
                'exponen a continuación.\n\n' +
                '[Detallar acá las observaciones que motivan la devolución.]\n\n' +
                'Se solicita subsanar lo señalado y elevar nuevamente el informe.',
          ),
          ayuda:
            'Explicá por qué devolvés el expediente. El informe se envía a la Secretaría Electoral ' +
            'Permanente (registro@electoralchubut.gob.ar), con copia al buzón interno, y queda guardado ' +
            'en el expediente. Las agrupaciones NO reciben copia.',
          confirmMsg: deshaceElevacion
            ? '¿Deshacer la elevación al TEP?\n\n' +
              'El expediente vuelve a tu bandeja, sin pasar por la SEP. Se guardará el informe con los ' +
              'motivos.'
            : '¿Devolver el expediente a la SEP?\n\n' +
              'Vuelve a análisis y la Secretaría Electoral Permanente lo retoma. Se guardará el informe ' +
              'con los motivos.',
          successMsg: deshaceElevacion ? 'Elevación al TEP deshecha' : 'Expediente devuelto a la SEP',
          conHistorial: true,
        });
        break;
      }
      // El Secretario devuelve el expediente a la SEP en vez de elevarlo. Mismo
      // modal para los dos circuitos: solo cambia el basePath.
      case 'devolver_sep_reemp':
      case 'devolver_sep_no_presentado': {
        if (!expediente) break;
        const esNoPresentado = accion === 'devolver_sep_no_presentado';
        setInformeSimpleConfig({
          basePath: esNoPresentado ? 'no-presentado' : 'reempadronamiento',
          endpoint: 'devolver-sep',
          titulo: 'Devolver el expediente a la SEP',
          textoInicial: informeALaSep(
            esNoPresentado
              ? 'Se devuelve el presente expediente a la Secretaría Electoral Permanente, sin tener por ' +
                'no presentada la solicitud, por los motivos que se exponen a continuación.\n\n' +
                '[Detallar acá los motivos de la devolución.]\n\n' +
                'Se hace saber que el plazo de la intimación u observación vencida no se reabre por esta ' +
                'devolución: de corresponder otorgar un nuevo plazo, deberá cursarse una nueva observación.'
              : 'Se devuelve el presente expediente de reempadronamiento a la Secretaría Electoral ' +
                'Permanente, sin elevarlo al Tribunal Electoral Provincial, por los motivos que se exponen ' +
                'a continuación.\n\n[Detallar acá las observaciones que motivan la devolución.]\n\n' +
                'Se solicita subsanar lo señalado y elevar nuevamente el informe.',
          ),
          ayuda:
            'Explicá por qué devolvés el expediente. El informe se envía a la Secretaría Electoral ' +
            'Permanente (registro@electoralchubut.gob.ar), con copia al buzón interno, y queda guardado en el ' +
            'expediente. El partido NO recibe copia.',
          confirmMsg:
            '¿Devolver el expediente a la SEP?\n\n' +
            'Vuelve al paso anterior y la Secretaría Electoral Permanente lo retoma. Se guardará el ' +
            'informe con los motivos.',
          successMsg: 'Expediente devuelto a la SEP',
          conHistorial: true,
        });
        break;
      }
      // Las dos salidas del TEP viven detrás de un solo botón.
      case 'resolucion_tep_no_presentado':
        setOpcionesTepNoPresentado(true);
        break;
      // Resolución del TEP: mismo modal de acta que el resto del sistema.
      case 'tep_rechaza_no_presentado':
        setResolucionTepConfig({
          basePath: 'no-presentado',
          endpoint: 'tep-rechaza',
          titulo: 'TEP rechaza — no presentado',
          requiereMotivo: true,
          tono: 'bad',
          confirmLabel: 'Registrar rechazo',
          // El texto dice qué NO pasa además de qué pasa: el rechazo cierra el
          // expediente pero no declara la caducidad del partido, y quien firma
          // tiene que saberlo antes de confirmar, no después.
          confirmMsg:
            '¿Registrar que el TEP RECHAZÓ la solicitud dada por no presentada?\n\n' +
            'El expediente pasa a "Rechazado". El partido NO cambia de estado: declarar su ' +
            'caducidad es un acto aparte, con su propio expediente.',
          successMsg: 'Rechazo registrado',
          conAvisoPartido: true,
          labelAvisoPartido: 'Notificar el rechazo al partido por correo',
        });
        break;
      case 'tep_aplaza_no_presentado':
        setResolucionTepConfig({
          basePath: 'no-presentado',
          endpoint: 'tep-aplaza',
          titulo: 'TEP aplaza la resolución',
          requiereMotivo: true,
          tono: 'ok',
          confirmLabel: 'Registrar aplazamiento',
          confirmMsg:
            '¿Registrar el APLAZAMIENTO resuelto por el TEP?\n\n' +
            'El expediente vuelve a su etapa anterior y se reabre el plazo para que el partido subsane.',
          successMsg: 'Aplazamiento registrado',
          conAvisoPartido: true,
          labelAvisoPartido: 'Notificar el nuevo plazo al partido por correo',
        });
        break;
      // ---- REEMPADRONAMIENTO: informe al Secretario → TEP → resolución ----
      // Mismos modales que reconocimiento/no presentado: cuerpo editable +
      // panel lateral para adjuntar las intimaciones/observaciones al informe.
      case 'informe_secretario_reemp':
        if (!expediente) break;
        setInformeSimpleConfig({
          basePath: 'reempadronamiento',
          endpoint: 'informe-secretario',
          titulo: 'Informe al Secretario — Reempadronamiento',
          // Cuerpo BREVE y sin inventario: dice que el análisis está cerrado y que
          // la decisión es del Secretario, nada más. El detalle del expediente
          // (tipos de documento, archivos, estado de cada observación) lo tiene él
          // mejor presentado en /documentos y en la tab Observaciones; repetirlo acá
          // enterraba lo único que el informe agrega. Lo puntual se adjunta a mano
          // desde el panel lateral.
          textoInicial: informeAlSecretario(textoInformeSecretarioReemp(expediente), {
            conVerificacion: false,
          }),
          ayuda:
            'Redactá el informe al Secretario Electoral sobre el trámite de reempadronamiento. ' +
            'El texto viene breve a propósito: tildá al costado las intimaciones u observaciones ' +
            'que quieras mostrarle y se agregan al final del informe, con su estado (subsanada o ' +
            'sin subsanar), de la más antigua a la más reciente.',
          confirmMsg:
            '¿Elevar el informe al Secretario?\n\n' +
            'El expediente pasa a "En Secretario" y queda a su resolución.',
          successMsg: 'Informe elevado al Secretario',
          conHistorial: true,
          // SIN check de aviso al partido: el informe al Secretario es de la vía INTERNA
          // del organismo (SEP → Secretario). Al partido no se le manda ni el PDF ni el
          // aviso de que el expediente avanzó — el paso no le pide nada ni resuelve nada,
          // y el aviso lo dejaba esperando una novedad que todavía no existe. Se le
          // notifica cuando hay una decisión (resolución) o algo que responder
          // (observación/intimación). Vale para TODOS los trámites.
        });
        break;
      case 'elevar_tep_reemp':
        if (!expediente) break;
        setInformeSimpleConfig({
          basePath: 'reempadronamiento',
          endpoint: 'elevar-tep',
          titulo: 'Elevar al TEP — Reempadronamiento',
          // El cuerpo viene CORTO y con el hueco del medio para escribir: al TEP
          // solo se elevan los expedientes que no están correctos, y el motivo de
          // la elevación se redacta caso por caso.
          textoInicial: informeAlTep(textoInformeTepReemp(expediente)),
          ayuda:
            'Redactá el informe que se eleva al Tribunal Electoral Provincial: el texto viene corto a ' +
            'propósito, con el espacio del medio para detallar qué tiene que resolver el Tribunal. ' +
            'Tildá al costado las intimaciones u observaciones que quieras incluir.',
          confirmMsg: '¿Elevar el expediente al TEP para su resolución?',
          successMsg: 'Expediente elevado al TEP',
          conHistorial: true,
          conAvisoPartido: true,
          labelAvisoPartido: 'Avisar al partido por correo que el expediente avanzó',
          subtextoAvisoPartido: 'El informe NO se adjunta: es documentación interna del organismo. Solo se le avisa del avance.',
        });
        break;
      // El SECRETARIO aprueba por sí mismo y otorga el reconocimiento. Su modal
      // muestra la resolución (con el certificado) como una hoja editable,
      // ya completa con los datos del partido, y el sistema genera el PDF.
      case 'aprobar_reemp':
        setAprobarReempAbierto(true);
        break;
      // Las tres salidas del TEP viven detrás de un solo botón.
      case 'resolucion_tep_reemp':
        setOpcionesTepReemp(true);
        break;
      case 'tep_aprueba_reemp':
        setResolucionTepConfig({
          basePath: 'reempadronamiento',
          endpoint: 'tep-aprueba',
          titulo: 'TEP aprueba el reempadronamiento',
          labelMotivo: 'Texto de la resolución',
          requiereMotivo: false,
          motivoInicial: textoTepApruebaReemp(),
          tono: 'ok',
          confirmLabel: 'Registrar aprobación',
          confirmMsg:
            '¿Registrar que el TEP APROBÓ el reempadronamiento?\n\n' +
            'El expediente pasa a "Vigente" y el partido también.',
          successMsg: 'Reempadronamiento aprobado',
          conAvisoPartido: true,
          avisoPartidoPorDefecto: true,
          labelAvisoPartido: 'Notificar la aprobación al partido por correo',
        });
        break;
      case 'tep_rechaza_reemp':
        setResolucionTepConfig({
          basePath: 'reempadronamiento',
          endpoint: 'tep-rechaza',
          titulo: 'TEP rechaza el reempadronamiento',
          labelMotivo: 'Texto de la resolución',
          requiereMotivo: true,
          motivoInicial: textoTepRechazaReemp(),
          tono: 'bad',
          confirmLabel: 'Registrar rechazo',
          confirmMsg:
            '¿Registrar que el TEP RECHAZÓ el reempadronamiento?\n\n' +
            'El expediente pasa a "Rechazado". El partido NO cambia de estado: declarar su ' +
            'caducidad es un acto aparte, con su propio expediente.',
          successMsg: 'Rechazo registrado',
          conAvisoPartido: true,
          avisoPartidoPorDefecto: true,
          labelAvisoPartido: 'Notificar el rechazo al partido por correo',
        });
        break;
      // Aplazamiento: el expediente vuelve a análisis. El plazo NO arranca con el
      // acta —lo abre la observación que curse la supervisora con lo que el
      // Tribunal determinó—, así que acá no se pide ni se recalcula ninguna fecha.
      case 'tep_aplaza_reemp':
        setResolucionTepConfig({
          basePath: 'reempadronamiento',
          endpoint: 'tep-aplaza',
          titulo: 'TEP aplaza la resolución',
          labelMotivo: 'Texto de la resolución',
          requiereMotivo: true,
          motivoInicial: textoTepAplazaReemp(),
          tono: 'ok',
          confirmLabel: 'Registrar aplazamiento',
          confirmMsg:
            '¿Registrar que el TEP APLAZÓ la resolución?\n\n' +
            'El expediente vuelve a "En análisis". El plazo del aplazo empieza a correr cuando ' +
            'la supervisora curse la observación con lo que el Tribunal determinó.',
          successMsg: 'Aplazamiento registrado',
          conAvisoPartido: true,
          avisoPartidoPorDefecto: true,
          labelAvisoPartido: 'Notificar el aplazamiento y el plazo al partido por correo',
        });
        break;
      case 'subsanar':
        setShowSubsanarModal(true);
        break;
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (error || !expediente) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900">Expediente no encontrado</h3>
        <Link to="/expedientes" className="text-primary-600 hover:text-primary-700 mt-2 inline-block">
          ← Volver a expedientes
        </Link>
      </div>
    );
  }

  const estadoInfo = estadoConfig[expediente.estado] || estadoConfig.BORRADOR;
  // Habilita el botón "No presentado". La regla es la MISMA que valida el backend
  // en routes/noPresentado.ts, para que el botón no aparezca cuando el endpoint va
  // a rechazar (ni al revés):
  //   - Análisis (OBSERVADO): venció fechaVencimiento y hay una observación de
  //     análisis en PENDIENTE (el partido nunca respondió).
  //   - Admisibilidad (INTIMADO): pasaron los 10 días hábiles desde la última
  //     intimación y sigue en PENDIENTE.
  const plazoVencidoSinResponder = (() => {
    if (expediente.estado === 'OBSERVADO') {
      return !!isVencido &&
        (expediente.observaciones?.some(o => o.tipo !== 'INTIMACION' && o.estado === 'PENDIENTE') ?? false);
    }
    if (expediente.estado === 'INTIMADO') {
      const intimaciones = (expediente.observaciones || [])
        .filter(o => o.tipo === 'INTIMACION')
        .sort((a, b) => new Date(b.creadoEn).getTime() - new Date(a.creadoEn).getTime());
      const ultima = intimaciones[0];
      if (!ultima || ultima.estado !== 'PENDIENTE') return false;
      // Intimación sin plazo: no vence, no hay botón de "no presentado".
      if (!ultima.fechaLimite) return false;
      const diasTranscurridos = Math.floor((Date.now() - new Date(ultima.creadoEn).getTime()) / (1000 * 60 * 60 * 24));
      return 10 - Math.floor((diasTranscurridos * 5) / 7) <= 0;
    }
    return false;
  })();

  const noPresentadoInfo = getNoPresentado(expediente.datosFormulario);

  const acciones = getAccionesDisponibles(expediente.estado, user?.rol || '', {
    tipo: expediente.tipo,
    tieneRespuestaPendiente,
    plazoVencidoSinResponder,
    subFaseNoPresentado: noPresentadoInfo?.subFase ?? null,
  });


const isInterno = ['ADMINISTRADOR', 'SUPERVISOR', 'ANALISTA', 'MESA_ENTRADAS', 'AUTORIDAD_FIRMANTE'].includes(user?.rol || '');
  // MESA_ENTRADAS ve la gestión (isInterno) pero es de SOLO LECTURA: no puede
  // ejecutar acciones (intimar, observar, aprobar, etc.). puedeGestionar habilita
  // las acciones para el resto de los roles internos.
  const puedeGestionar = ['ADMINISTRADOR', 'SUPERVISOR', 'ANALISTA', 'AUTORIDAD_FIRMANTE'].includes(user?.rol || '');
  // Un trámite que todavía nadie inició no se revisa: ni observaciones, ni cambios de
  // estado de documentos, ni aprobación de secciones. El backend lo rechaza con 409
  // (services/tramiteIniciado.ts); acá se usa para no ofrecer la acción.
  const iniciado = tramiteIniciado(expediente.estado);
  // Solo las que el partido tiene sin responder: estado PENDIENTE (no RESPONDIDA por el partido ni RESUELTA/subsanada).
  const obsPendientes = expediente.observaciones?.filter(o => !o.subsanada && o.estado === 'PENDIENTE') || [];

  // --- RECONOCIMIENTO: fase + acciones top-right + visibilidad del panel de observación ---
  const esReconocimiento = expediente.tipo === 'RECONOCIMIENTO';
  const faseRecon = esReconocimiento ? (getFaseActual(expediente.datosFormulario) ?? 'WIZARD_EXTERNO_COMPLETO') : null;
  // "Observado" = hay observaciones activas (pendientes o respondidas, no resueltas).
  const obsActivasRecon = expediente.observaciones?.filter(
    o => o.tipo !== 'INTIMACION' && !o.subsanada && (o.estado === 'PENDIENTE' || o.estado === 'RESPONDIDA'),
  ) || [];
  const hayObsPendienteRecon = obsActivasRecon.length > 0;
  const esSecretario = ['AUTORIDAD_FIRMANTE', 'ADMINISTRADOR', 'SUPERVISOR'].includes(user?.rol || '');
  const esRevisor = ['ANALISTA', 'SUPERVISOR', 'ADMINISTRADOR'].includes(user?.rol || '');
  const reconAcciones = (esReconocimiento && faseRecon
    ? filtrarAccionesPorRol(accionesReconocimiento(faseRecon, hayObsPendienteRecon), { esRevisor, esSecretario })
    : []
  // "No presentado" NO se muestra mientras no corresponda: se OCULTA (no se
  // deshabilita) hasta que venza el plazo de 4 meses del provisorio o el plazo de
  // una observación sin responder. Es una acción grave — no tiene que estar a la
  // vista antes de tiempo.
  ).filter((a) => a.kind !== 'noPresentado' || noPresentadoHabilitado);
  // "Publicar" queda visible pero deshabilitado mientras haya documentos de
  // admisibilidad sin verificar (p. ej. el símbolo pendiente): recién se publica
  // cuando no quedan pendientes en la dimensión de admisibilidad.
  const hayPendientesAdmisibilidad = (expediente.documentos ?? []).some(
    d => d.estadoRevisionAdmisibilidad === 'PENDIENTE',
  );
  // REEMPADRONAMIENTO: "Informe al Secretario" queda visible pero deshabilitado
  // mientras haya documentos que nadie verificó ni una vez en esta etapa. Se mira
  // la MISMA dimensión que tilda la pantalla Documentos con el expediente en
  // análisis (etapaActivaExp); mirar la otra dejaría el botón trabado para
  // siempre. El backend valida lo mismo en /reempadronamiento/:id/informe-secretario.
  const docsSinVerificarEtapa = (expediente.documentos ?? []).filter(d => {
    // Rechazado en admisibilidad y nunca llegó a análisis: fuera de juego acá.
    if (etapaActivaExp === 'ANALISIS' &&
        d.estadoRevisionAdmisibilidad === 'RECHAZADO' &&
        d.estadoRevisionAnalisis !== 'RECHAZADO') return false;
    const estado = etapaActivaExp === 'ANALISIS' ? d.estadoRevisionAnalisis : d.estadoRevisionAdmisibilidad;
    return (estado ?? 'PENDIENTE') === 'PENDIENTE';
  }).length;
  // Tipos obligatorios sin NI UN archivo verificado en la etapa activa: o no se
  // cargó nada, o lo que hay sigue PENDIENTE. Un tipo sin archivos no aparece en
  // el contador de arriba, así que sin esto se podría elevar con documentación
  // obligatoria faltante. Espeja el gate del backend.
  // Documentación exigida por la etapa que el partido todavía no presentó. En la
  // etapa de documentación del reconocimiento son los documentos del Art. 18: sin
  // ellos no hay nada que elevar al Secretario. Si el partido no los presenta, el
  // camino es observarlo y, vencido ese plazo, darlo por no presentado.
  const documentacionSinPresentar = itemsVerificacion.filter(i => i.estado === 'SIN_PRESENTAR');
  // "Aceptar subsanación" no corresponde si hay intimaciones/observaciones que el
  // partido nunca respondió. En RECONOCIMIENTO las respuestas se vinculan como
  // observación (no intimación), así que se consulta esa etapa.
  const subsanacionAnalisis = getSubsanacionInfo('analisis');
  const subsanacionAdmisibilidad = getSubsanacionInfo('admisibilidad');
  const motivoSinResponder = (n: number) =>
    `Hay ${n} ${n === 1 ? 'intimación/observación sin ninguna respuesta del partido' : 'intimaciones/observaciones sin ninguna respuesta del partido'}: ` +
    'no hay nada que subsanar. Esperá el plazo y, si no responde, usá "No presentado".';
  const obligatoriosSinVerificar = OBLIGATORIOS_TIPOS.filter(({ tipo }) =>
    !docsChecklist.some((d: any) => d.tipo === tipo && d.estadoRevision !== 'PENDIENTE'),
  );

  // ===========================================================================
  // Requisitos para avanzar de etapa (REEMPADRONAMIENTO)
  // ===========================================================================
  // FUENTE ÚNICA de los bloqueos: de acá salen tanto el `disabled` + tooltip de
  // los botones del encabezado como el panel de la tab Documentos. Antes el motivo
  // vivía solo en el `title` del botón (había que descubrirlo pasando el mouse por
  // un botón gris) y el panel de la tab mostraba otra cosa: un checklist de tipos
  // de documento que ya está, mejor, en /documentos.
  //
  // Cada entrada espeja un gate REAL del backend:
  //   informe_secretario_reemp     → routes/reempadronamiento.ts (docs sin verificar)
  //   aceptar_subsanacion(_admis)  → services/subsanacionService.ts (nada respondido)
  const bloqueosPorAccion: Record<string, string[]> = {};
  {
    const bloqueosInforme: string[] = [];
    if (docsSinVerificarEtapa > 0) {
      bloqueosInforme.push(`Quedan ${docsSinVerificarEtapa} documento(s) sin verificar. No debe quedar ninguno pendiente.`);
    }
    // La documentación obligatoria que falta YA NO BLOQUEA: el backend dejó de
    // hacerlo (bloquear dejaba sin salida al partido que no presenta y no contesta)
    // y el cuerpo del informe la nombra. Listarla acá como bloqueo dejaba el botón
    // deshabilitado por algo que el servidor sí acepta.
    if (bloqueosInforme.length > 0) bloqueosPorAccion['informe_secretario_reemp'] = bloqueosInforme;
  }

  // Acción de AVANCE de cada estado: la que hace pasar el expediente a la etapa
  // siguiente (no las laterales como "Intimar" u "Observar", que no avanzan nada).
  const ACCION_AVANCE_REEMP: Record<string, { key: string; label: string; nota?: string }> = {
    EN_ADMISIBILIDAD: { key: 'aprobar_admisibilidad', label: 'Pasar a análisis' },
    INTIMADO: {
      key: 'aprobar_admisibilidad', label: 'Pasar a análisis',
      nota: 'Aprueba la admisibilidad de lo que siga pendiente. Las intimaciones sin resolver no son impedimento.',
    },
    EN_ANALISIS: {
      key: 'informe_secretario_reemp', label: 'Informe al Secretario',
      nota: obligatoriosSinVerificar.length > 0
        ? `Falta verificar: ${obligatoriosSinVerificar.map(o => o.label).join(', ')}. No bloquea la elevación — se declara en el informe y resuelve el Secretario.`
        : undefined,
    },
    // Desde OBSERVADO el avance ya no es un botón que cierre todo: cada observación
    // se cierra con su check y el expediente vuelve solo a EN_ANALISIS. Lo que
    // AVANZA el trámite es el informe, que además se puede elevar con observaciones
    // abiertas cuando el partido nunca contestó.
    OBSERVADO: {
      key: 'informe_secretario_reemp', label: 'Informe al Secretario',
      nota: 'Cada observación se cierra con su check en la tab Observaciones. Si el partido no contestó, se puede elevar igual: lo que falta se declara en el informe.'
        + (obligatoriosSinVerificar.length > 0
          ? ` Falta verificar: ${obligatoriosSinVerificar.map(o => o.label).join(', ')}.`
          : ''),
    },
    // El avance normal es que el Secretario APRUEBE: solo se eleva al TEP el
    // expediente que no está correcto.
    EN_SECRETARIO: {
      key: 'aprobar_reemp', label: 'Aprobar el reempadronamiento',
      nota: 'La pelota la tiene el Secretario Electoral: aprueba con su resolución, o eleva al TEP si el expediente no está correcto.',
    },
    PENDIENTE_RESOLUCION: {
      key: 'aprobar_reemp', label: 'Aprobar el reempadronamiento',
      nota: 'La pelota la tiene el Secretario Electoral: aprueba con su resolución, o eleva al TEP si el expediente no está correcto.',
    },
    EN_TEP: { key: 'resolucion_tep_reemp', label: 'Registrar la resolución del TEP', nota: 'El expediente está en el TEP: se registra el acta cuando resuelva (aprueba, rechaza o aplaza).' },
    NO_PRESENTADO: { key: 'elevar_tep_no_presentado', label: 'Elevar al TEP (no presentado)', nota: 'Circuito de "no presentado": resuelve el Secretario y luego el TEP.' },
  };

  const requisitosAvance = ((): RequisitosAvance | null => {
    if (expediente.tipo !== 'REEMPADRONAMIENTO' || !isInterno) return null;
    const avance = ACCION_AVANCE_REEMP[expediente.estado];
    if (!avance) return null;

    const bloqueos = bloqueosPorAccion[avance.key] ?? [];
    const requisitos: Requisito[] = [];

    // --- Requisito: nada pendiente de verificar (bloquea solo el informe) ---
    // Solo el CONTADOR: el detalle de qué documento es y por qué está pendiente se
    // ve en /documentos, que es donde además se verifica.
    const hayPendientes = docsSinVerificarEtapa > 0 || obligatoriosSinVerificar.length > 0;
    if (avance.key === 'informe_secretario_reemp') {
      requisitos.push({
        label: hayPendientes
          ? `${docsSinVerificarEtapa} documento(s) pendientes de verificar`
          : 'No quedan documentos pendientes de verificar',
        estado: hayPendientes ? 'FALTA' : 'OK',
        nota: hayPendientes
          ? 'No se puede elevar el informe con documentación sin verificar.'
          : undefined,
      });
      if (obligatoriosSinVerificar.length > 0) {
        requisitos.push({
          label: `Faltan verificar ${obligatoriosSinVerificar.length} documento(s) obligatorio(s)`,
          estado: 'FALTA',
        });
      }
    } else if (hayPendientes) {
      // En el resto de las etapas los pendientes no bloquean, pero saberlos evita
      // pasar a análisis o aceptar una subsanación sin haber mirado nada.
      requisitos.push({
        label: `${docsSinVerificarEtapa} documento(s) pendientes de verificar`,
        estado: 'INFO',
        nota: 'No bloquea esta acción, pero sí el informe al Secretario más adelante.',
      });
    }

    // El requisito "el partido respondió" se fue con el botón "Aceptar subsanación"
    // de reempadronamiento: ya no hay una acción que dependa de eso. Cada observación
    // se cierra con su check —que sí exige tener los adjuntos revisados— y pasar a
    // análisis no espera respuesta, porque una intimación abierta no es impedimento.

    return {
      accionLabel: avance.label,
      disponibleParaRol: acciones.some(a => a.key === avance.key),
      requisitos,
      bloqueos,
      notaEtapa: avance.nota,
    };
  })();
  // El panel de observación (crear observación) lo usan los analistas en las fases revisables.
  // En AFILIADOS_PENDIENTES también se observa (el padrón presentado puede estar
  // incompleto), pero el estado sigue siendo REC_PROVISORIO: el overlay OBSERVADO
  // no se aplica ahí. Espeja FASES_QUE_ADMITEN_OBSERVACION del backend.
  // TODAS las fases admiten observación, igual que FASES_QUE_ADMITEN_OBSERVACION en
  // el backend: el estado del expediente lo deriva `syncEstadoExpediente` de la fase,
  // y el overlay OBSERVADO/INTIMADO sólo se aplica mientras la fase es revisable, así
  // que observar en cualquier otra no altera el trámite.
  const puedeObservarRecon = esReconocimiento && !!faseRecon && esRevisor;
  // En la etapa de atributos la acción es una INTIMACIÓN; en documentación, una OBSERVACIÓN.
  const esFaseAtributosRecon = faseRecon === 'ATRIBUTOS_PENDIENTES' || faseRecon === 'ATRIBUTOS_RECHAZADOS';
  // Término a usar (tab, textos): en reconocimiento se deriva de la ETAPA (fase),
  // no del estado ni de o.tipo (las obs de reconocimiento se guardan como OBSERVACION).
  // Etapa de atributos → "intimación"; documentación → "observación".
  const usaTerminoIntimacion = esReconocimiento
    ? (!!faseRecon && faseRecon.startsWith('ATRIBUTOS'))
    : ['EN_ADMISIBILIDAD', 'INTIMADO'].includes(expediente.estado);

  // Tipos de documento que el partido puede adjuntar al responder, según la etapa
  // del reconocimiento. En intimación (atributos): solo DNI del apoderado + símbolo.
  // En observación (documentación): obligatorios Art.18 + símbolo + DNI apoderado/autoridad.
  const LABELS_PROMOTOR_RECON: Record<string, string> = {
    DNI_FRENTE_INICIADOR: 'DNI (frente) del Iniciador',
    DNI_DORSO_INICIADOR: 'DNI (dorso) del Iniciador',
    DNI_FRENTE_APODERADO: 'DNI (frente) Apoderado Promotor',
    DNI_DORSO_APODERADO: 'DNI (dorso) Apoderado Promotor',
    DNI_FRENTE_AUTORIDAD: 'DNI (frente) Autoridad Promotora',
    DNI_DORSO_AUTORIDAD: 'DNI (dorso) Autoridad Promotora',
    // Ayuda visual del formato en el selector de la respuesta.
    PLANILLA_ADHERENTES_FIRMAS: 'Planilla de Adherentes con firmas (PDF)',
    LISTADO_ADHERENTES_EXCEL: 'Listado de Adherentes (Excel/ODS)',
  };
  // Candidatura: lo que puede presentar una agrupación (sin resoluciones ni informes de la SEP).
  const tiposRespuestaRecon: string[] | undefined = esCandidaturaExp
    ? TIPOS_RESPUESTA_AGRUPACION
    : !esReconocimiento
    ? undefined
    : usaTerminoIntimacion
      ? ['DNI_FRENTE_INICIADOR', 'DNI_DORSO_INICIADOR', 'SIMBOLO']
      // Etapa de autoridades definitivas: lo que puede corregir el partido son los
      // cinco documentos de esa etapa y los DNI de autoridades/apoderados.
      : esEtapaAutoridades(faseRecon)
      ? [...DOCS_OBLIGATORIOS_RECONOCIMIENTO_AUTORIDADES, 'DNI_FRENTE_AUTORIDAD', 'DNI_DORSO_AUTORIDAD', 'DNI_FRENTE_APODERADO', 'DNI_DORSO_APODERADO', 'OTRO']
      : ['ACTA_CONSTITUTIVA', 'CARTA_ORGANICA', 'BASES_ACCION_POLITICA', 'ACTA_DESIGNACION_AUTORIDADES_PROMOTORAS', 'PLANILLA_ADHERENTES_FIRMAS', 'LISTADO_ADHERENTES_EXCEL', 'SIMBOLO', 'DNI_FRENTE_APODERADO', 'DNI_DORSO_APODERADO', 'DNI_FRENTE_AUTORIDAD', 'DNI_DORSO_AUTORIDAD', 'OTRO'];
  // En la etapa de autoridades definitivas las personas ya NO son promotoras, así
  // que los labels de los DNI no deben decirlo.
  const LABELS_DEFINITIVAS_RECON: Record<string, string> = {
    DNI_FRENTE_APODERADO: 'DNI (frente) del Apoderado',
    DNI_DORSO_APODERADO: 'DNI (dorso) del Apoderado',
    DNI_FRENTE_AUTORIDAD: 'DNI (frente) de la Autoridad',
    DNI_DORSO_AUTORIDAD: 'DNI (dorso) de la Autoridad',
  };
  const labelsRespuestaRecon = !esReconocimiento
    ? undefined
    : esEtapaAutoridades(faseRecon)
      ? LABELS_DEFINITIVAS_RECON
      : LABELS_PROMOTOR_RECON;

  // Contador de la tab Observaciones/Intimaciones.
  //
  // UN SOLO contador, y sólo en rojo: cuántas quedan SIN SUBSANAR. Antes había uno
  // verde con las respondidas, pero "respondida" no es una buena noticia que amerite
  // un contador propio: lo único que hay que mirar de un vistazo es qué falta.
  //
  // El corte es `subsanada`, el mismo check que tilda la Secretaría. Las anteriores
  // a ese campo se cerraban con estado RESUELTA y tienen `subsanada = false` en la
  // base: se excluyen a mano para no contarlas como deuda vieja que no existe.
  const observacionesSinSubsanarCount = expediente.observaciones?.filter(
    o => !o.subsanada && o.estado !== 'RESUELTA',
  ).length || 0;

  // Flecha "atrás": los usuarios internos vuelven a la Bandeja de trabajo
  // (/gestion) en la etapa que corresponde al estado del expediente
  // (admisibilidad / análisis / resolución). Los externos y los expedientes ya
  // cerrados (sin etapa de bandeja) vuelven al Listado.
  const etapaBandeja: Record<string, 'admisibilidad' | 'analisis' | 'resolucion'> = {
    PRESENTADO: 'admisibilidad',
    EN_ADMISIBILIDAD: 'admisibilidad',
    INTIMADO: 'admisibilidad',
    EN_ANALISIS: 'analisis',
    OBSERVADO: 'analisis',
    // El provisorio (etapa de afiliados) se trabaja en la bandeja de Análisis.
    REC_PROVISORIO: 'analisis',
    EN_SECRETARIO: 'resolucion',
    PENDIENTE_RESOLUCION: 'resolucion',  // legacy
  };
  const etapaActual = etapaBandeja[expediente.estado];
  const backHref = isInterno && etapaActual ? `/gestion?tab=${etapaActual}` : '/expedientes';

  return (
    <div className="space-y-6">
     {/* Header del expediente - card unificada */}
<div className="bg-white rounded-lg shadow p-5">
  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
    <div>
      <div className="flex items-center gap-3 mb-2">
        <Link to={backHref} className="text-gray-400 hover:text-gray-600">
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
        </Link>
        <h1 className="text-xl font-bold text-gray-900">{expediente.numero}</h1>
        {/* En RECONOCIMIENTO el `estado` no cambia durante atributos/documentación
            (queda PRESENTADO), así que ahí se muestra la ETAPA general del proceso,
            igual que el stepper.
            EXCEPCIÓN — tramo del provisorio (afiliados, autoridades, definitivo en
            Secretario/TEP): ahí sí se muestra el ESTADO real. La etapa se llama
            "Definitivo" y al lado del número de expediente se leía como si el partido
            ya tuviera el reconocimiento definitivo, cuando todavía tiene el
            provisorio. En qué paso va el trámite ya lo dice la barra de proceso. */}
        <span className={`px-3 py-1 rounded-full text-sm font-medium ${estadoInfo.bg} ${estadoInfo.color}`}>
          {esReconocimiento && faseRecon && !esTramoProvisorio(faseRecon)
            ? ETAPAS_RECONOCIMIENTO[etapaIndexDeFase(faseRecon)]
            : estadoInfo.label}
          {/* En NO_PRESENTADO el estado no dice quién tiene la pelota (Secretario o TEP). */}
          {expediente.estado === 'NO_PRESENTADO' && sufijoBadgeNoPresentado(expediente.datosFormulario)}
        </span>
        {isVencido && (
          <span className="px-2 py-1 rounded bg-red-100 text-red-700 text-xs font-medium">VENCIDO</span>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
      {expediente.partido && (
        <span className="flex items-center gap-1">
            <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
            {expediente.partido.sigla ? `${expediente.partido.sigla} - ` : ''}{expediente.partido.denominacion}
          </span>
        )}
        {/* Ambito territorial de la agrupacion. El tipo de tramite ya lo dice el
            titulo del expediente, asi que este renglon aporta el dato que ahi falta. */}
        {expediente.partido?.tipo && (
          <span className="flex items-center gap-1">
            <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
            {ambitoPartidoLabels[expediente.partido.tipo] || expediente.partido.tipo}
          </span>
        )}
        {expediente.fechaPresentacion && (
          <span className="flex items-center gap-1">
            <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
            {formatDateShort(expediente.fechaPresentacion)}
          </span>
        )}
        {isInterno && expediente.asignado && (
          <span>Asignado a <strong>{expediente.asignado.nombre} {expediente.asignado.apellido}</strong></span>
        )}
      </div>
    </div>
    {acciones.length > 0 && expediente.tipo !== 'RECONOCIMIENTO' && (
      <div className="flex gap-2 shrink-0">
        {acciones
    .map(accion => {
      // Los motivos de bloqueo salen de `bloqueosPorAccion`, la MISMA fuente que
      // alimenta el panel "Para avanzar de etapa" de la tab Documentos: el tooltip
      // y el panel no pueden decir cosas distintas.
      const bloqueos = bloqueosPorAccion[accion.key] ?? [];
      const deshabilitado = bloqueos.length > 0;
      const motivoBloqueo = bloqueos.join(' ');
      return (
      <button key={accion.key} onClick={() => handleAccion(accion.key)} disabled={deshabilitado}
        title={deshabilitado ? motivoBloqueo : undefined}
        className={`px-4 py-2 text-white text-sm font-medium rounded-lg transition whitespace-nowrap ${accion.color} ${deshabilitado ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90'}`}
      >{accion.label}</button>
      );
    })
  }
      </div>
    )}
    {esReconocimiento && (
      <div className="flex gap-2 shrink-0 flex-wrap justify-end">
        {reconAcciones.map(a => {
          // "Elevar a Secretario" (atributos y documentación) queda deshabilitado
          // hasta que no haya documentos pendientes de verificar: al Secretario se le
          // eleva lo YA verificado. La publicación no se chequea acá porque a esa
          // altura la verificación ya pasó (la hizo la SEP antes de elevar).
          const bloqueaPorPendientes =
            (a.endpoint === 'elevar-secretario-atributos' || a.endpoint === 'elevar-secretario-documentacion') &&
            hayPendientesAdmisibilidad;
          // Sin la documentación del Art. 18 presentada no hay informe que elevar.
          const faltaDocumentacion =
            a.endpoint === 'elevar-secretario-documentacion' && documentacionSinPresentar.length > 0;
          // Ni subsanación que aceptar si el partido no respondió nada.
          const sinResponderRecon =
            a.endpoint === 'aceptar-subsanacion' ? subsanacionAnalisis.sinResponder : 0;
          // Etapa de afiliados: no se avanza a autoridades hasta que los DOS
          // documentos del padrón (Excel + PDF firmado) estén presentados y
          // verificados. Espeja el gate del backend.
          const faltaPadronAfiliados =
            a.endpoint === 'pasar-autoridades-pendientes' && estadoAfiliadosDocs
              ? !estadoAfiliadosDocs.completo
              : false;
          // Etapa de autoridades: no se eleva al Secretario hasta que el partido
          // haya enviado el wizard y los TRES documentos estén verificados. Espeja
          // el gate del backend (elevar-secretario-definitivo).
          const faltaAutoridades =
            a.endpoint === 'elevar-secretario-definitivo' && estadoAutoridadesDocs
              ? !estadoAutoridadesDocs.autoridadesCargadas || !estadoAutoridadesDocs.completo
              : false;
          const deshabilitado = bloqueaPorPendientes || faltaDocumentacion || sinResponderRecon > 0 || faltaPadronAfiliados || faltaAutoridades;
          const motivoPadron = !faltaPadronAfiliados || !estadoAfiliadosDocs
            ? undefined
            : estadoAfiliadosDocs.faltanPresentar.length > 0
              ? `El partido todavía no presentó: ${estadoAfiliadosDocs.faltanPresentar.join(', ')}.`
              : `Falta verificar: ${estadoAfiliadosDocs.faltanVerificar.join(', ')}. Verificá los dos documentos del padrón en la pestaña Documentos.`;
          const motivoAutoridades = !faltaAutoridades || !estadoAutoridadesDocs
            ? undefined
            : !estadoAutoridadesDocs.autoridadesCargadas
              ? 'El partido todavía no presentó sus autoridades definitivas.'
              : estadoAutoridadesDocs.faltanPresentar.length > 0
                ? `Falta presentar: ${estadoAutoridadesDocs.faltanPresentar.join(', ')}.`
                : `Falta verificar: ${estadoAutoridadesDocs.faltanVerificar.join(', ')}. Verificá los documentos de la etapa y los DNI en la pestaña Documentos.`;
          const motivo = motivoPadron
            ?? motivoAutoridades
            ?? (sinResponderRecon > 0
            ? motivoSinResponder(sinResponderRecon)
            : faltaDocumentacion
              ? `El partido todavía no presentó: ${documentacionSinPresentar.map(i => i.label).join(', ')}.`
              : bloqueaPorPendientes
                ? 'Verificá todos los documentos pendientes para poder continuar.'
                : undefined);
          return (
            <button key={a.key} onClick={() => handleReconAccion(a)} disabled={deshabilitado}
              title={motivo}
              className={`px-4 py-2 text-white text-sm font-medium rounded-lg transition whitespace-nowrap ${a.color} ${deshabilitado ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90'}`}
            >{a.label}</button>
          );
        })}
        <Link
          to={`/documentos?expedienteId=${expediente.id}${expediente.partido ? `&partidoId=${expediente.partido.id}` : ''}`}
          className="px-4 py-2 bg-white border border-blue-600 text-blue-700 text-sm font-medium rounded-lg hover:bg-blue-50 transition whitespace-nowrap"
        >
          Ver documentación →
        </Link>
      </div>
    )}
  </div>

  {/* Stepper dentro de la misma card.
      RECONOCIMIENTO usa un panel basado en `fase` (atributos/documentación);
      el resto de los trámites usa el stepper basado en `estado`. */}
  <div className="mt-4 pt-4 border-t">
    {expediente.tipo === 'RECONOCIMIENTO'
      ? <ReconocimientoFasePanel expediente={expediente} />
      : <TimelineEstado estadoActual={expediente.estado} tipo={expediente.tipo} />}
  </div>
</div>

      {/* Alerta observaciones/intimaciones pendientes - solo apoderados */}
      {!isInterno && obsPendientes.length > 0 && (() => {
        // En reconocimiento el término se deriva de la etapa (fase); en el resto, de o.tipo.
        const todasIntimacion = esReconocimiento ? usaTerminoIntimacion : obsPendientes.every(o => o.tipo === 'INTIMACION');
        const todasObs = esReconocimiento ? !usaTerminoIntimacion : obsPendientes.every(o => o.tipo !== 'INTIMACION');
        const tipoTexto = todasIntimacion ? 'intimación' : todasObs ? 'observación' : 'comunicación';
        const tipoTextoPlural = todasIntimacion ? 'intimaciones' : todasObs ? 'observaciones' : 'comunicaciones';
        return (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 flex items-center justify-between">
          <div>
            <p className="font-medium text-orange-800">
              {obsPendientes.length === 1
                ? `Tiene una ${tipoTexto} pendiente de respuesta`
                : `Tiene ${obsPendientes.length} ${tipoTextoPlural} pendientes`}
            </p>
            {expediente.fechaVencimiento && (
              <p className="text-sm text-orange-600">Límite: {formatDateShort(expediente.fechaVencimiento)}</p>
            )}
          </div>
        </div>
        );
      })()}


      {/* Sin banner de "expediente vencido": la fecha de vencimiento ya está en
          los datos del expediente y el badge VENCIDO acompaña al estado. */}

      {/* TABS */}
<div className="bg-white rounded-lg shadow">

{/* Barra de navegación */}
<div className="flex border-b overflow-x-auto">

  {(expediente.tipo === 'RENDICION_ANUAL' || expediente.tipo === 'RENDICION_CAMPANA') && (
    <button onClick={() => setTab('financiero')} className={`px-5 py-3 text-sm font-medium whitespace-nowrap flex items-center gap-2 border-b-2 transition-colors ${activeTab === 'financiero' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
      Resumen Financiero
    </button>
  )}

  {/* Documentacion: oculta en Reconocimiento mientras atributos pendientes */}
  {!atributosPendientes && (
  <button onClick={() => setTab('docs')} className={`px-5 py-3 text-sm font-medium whitespace-nowrap flex items-center gap-2 border-b-2 transition-colors ${activeTab === 'docs' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
    Documentación
    {!esRendicion && documentosChecklist && (() => {
  const verificados = OBLIGATORIOS_TIPOS.filter(({ tipo }) => docsChecklist.some((d: any) => d.tipo === tipo && d.estadoRevision === 'APROBADO')).length;
  return <span className="bg-orange-100 text-orange-700 text-xs font-bold px-1.5 py-0.5 rounded-full">{verificados}/{OBLIGATORIOS_TIPOS.length}</span>;
})()}

  </button>
  )}

  <button onClick={() => setTab('obs')} className={`px-5 py-3 text-sm font-medium whitespace-nowrap flex items-center gap-2 border-b-2 transition-colors ${activeTab === 'obs' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
    {usaTerminoIntimacion ? 'Intimaciones' : 'Observaciones'}
    {observacionesSinSubsanarCount > 0 && (
      <span
        className="bg-red-100 text-red-600 text-xs font-bold px-1.5 py-0.5 rounded-full"
        title={usaTerminoIntimacion ? 'Intimaciones sin subsanar' : 'Observaciones sin subsanar'}
      >
        {observacionesSinSubsanarCount}
      </span>
    )}
  </button>

  <button onClick={() => setTab('data')} className={`px-5 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${activeTab === 'data' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
    Datos del Trámite
  </button>

  {atributosPendientes && (
    <button onClick={() => setTab('atributos')} className={`px-5 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${activeTab === 'atributos' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
      Atributos
    </button>
  )}

  {/* Informes y resoluciones de la vía Secretario → TEP. Es la tab que usa
      sobre todo el Secretario, y se abre sola cuando la pelota es suya.
      Está en TODOS los expedientes: reempadronamiento, cambio de autoridades y
      el circuito de no presentado también generan informes al Secretario, al TEP,
      resoluciones del TEP y devoluciones del Secretario a la SEP. */}
  <button onClick={() => setTab('informes')} className={`px-5 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${activeTab === 'informes' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
    Informes
  </button>

{!esRendicion && !esAlianzaExp && puedeVerAfiliados && (
    <button onClick={() => setTab('afiliados')} className={`px-5 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${activeTab === 'afiliados' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
      Afiliados
    </button>
  )}

  {expediente.tipo === 'RECONOCIMIENTO' && puedeVerAdherentes && (
    <button onClick={() => setTab('adherentes')} className={`px-5 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${activeTab === 'adherentes' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
      Adherentes
    </button>
  )}


  {/* Interacciones va anteúltima, pegada al Historial: las dos son registro de lo
      que pasó, no cosas que haya que resolver. */}
  {isInterno && (
    <button onClick={() => setTab('inter')} className={`px-5 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${activeTab === 'inter' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
      Interacciones
    </button>
  )}

  <button onClick={() => setTab('hist')} className={`px-5 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${activeTab === 'hist' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
    Historial
  </button>

</div>

{/* Contenido */}
<div className="p-6">

   {/* TAB: Resumen Financiero (solo rendiciones) */}
{activeTab === 'financiero' && (expediente.tipo === 'RENDICION_ANUAL' || expediente.tipo === 'RENDICION_CAMPANA') && (
  <ResumenFinancieroExpediente expediente={expediente as any} onObservar={() => setTab('obs')} />
)}

   {/* TAB: Documentación (para rendiciones: solo PDFs firmados de cada versión) */}
{activeTab === 'docs' && esRendicion && (
  <VersionesPdfList expediente={expediente as any} />
)}

   {/* TAB: Documentación (para otros tipos: checklist + grilla de docs) */}
{activeTab === 'docs' && !esRendicion && (
  <div className="space-y-4">
    {/* Etapa de autoridades definitivas del provisorio: plazo (el mismo de los 4
        meses), qué presentó el partido y estado de los tres documentos. Va acá
        porque es la pestaña donde la supervisora los verifica. */}
    {esEtapaAutoridades(faseRecon) && (
      <div className={`border rounded-xl p-5 ${plazoAfiliados?.vencido ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className={`text-xs font-bold uppercase tracking-wide mb-1 ${plazoAfiliados?.vencido ? 'text-red-700' : 'text-amber-700'}`}>
              Autoridades definitivas — elecciones internas
            </p>
            {plazoAfiliados ? (
              <>
                <p className="text-sm text-gray-800">
                  <span className={`text-2xl font-bold ${plazoAfiliados.vencido ? 'text-red-700' : 'text-amber-700'}`}>
                    {textoPlazoAfiliados(plazoAfiliados)}
                  </span>
                  <span className="ml-2">— vence el {new Date(plazoAfiliados.vence).toLocaleDateString('es-AR')}</span>
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  4 meses desde la resolución del TEP que otorgó el reconocimiento provisorio
                  {plazoAfiliados.acta ? ` (${plazoAfiliados.acta})` : ''}.
                </p>
              </>
            ) : (
              <p className="text-sm text-gray-700">
                El partido debe convocar a elecciones internas y cargar sus autoridades definitivas.
              </p>
            )}
            {estadoAutoridadesDocs && (
              <p className="text-xs text-gray-700 mt-2">
                {estadoAutoridadesDocs.autoridadesCargadas
                  ? `Presentó ${estadoAutoridadesDocs.cantidadAutoridades} autoridad(es) y ${estadoAutoridadesDocs.cantidadApoderados} apoderado(s).`
                  : 'Todavía no presentó sus autoridades definitivas.'}
              </p>
            )}
          </div>
          {isInterno && estadoAutoridadesDocs && (
            <div className="text-sm">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Documentación de la etapa</p>
              {/* "Revisados" y no "aprobados": un rechazo también es una decisión
                  tomada y no frena la elevación — solo la frena lo PENDIENTE. */}
              {estadoAutoridadesDocs.completo ? (
                <p className="text-green-700 font-medium">✓ Documentos y DNI revisados</p>
              ) : (
                <ul className="space-y-1 text-gray-700 max-h-48 overflow-y-auto">
                  {estadoAutoridadesDocs.faltanPresentar.map(d => (
                    <li key={d}>✗ <span className="font-medium">{d}</span> — sin presentar</li>
                  ))}
                  {estadoAutoridadesDocs.faltanVerificar.map(d => (
                    <li key={d}>◻ <span className="font-medium">{d}</span> — presentado, sin verificar</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>
    )}

    {/* Aviso único de trámite sin iniciar. Reemplaza al viejo cartel de "crear las
        secciones": ésas ahora se siembran al dar de alta el trámite, así que lo que
        queda por avisar no es qué falta configurar sino que la etapa no está
        abierta. */}
    {puedeGestionar && !iniciado && (
      <div className="border border-amber-200 rounded-lg bg-amber-50 p-4">
        <p className="text-sm text-amber-800">{AVISO_TRAMITE_SIN_INICIAR}</p>
      </div>
    )}

    {/* Panel unificado: genera observación con obligatorios + DNIs (autoridades/apoderados) */}
    {puedeGestionar && iniciado && (
      <div className="border rounded-lg p-4 bg-white flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Observación automática</h2>
          {totalProblemas === 0 ? (
            <p className="text-xs text-green-600 font-medium mt-1">Documentación completa — no hay problemas.</p>
          ) : (
            <p className="text-xs text-amber-600 mt-1">
              {problemasObligatorios.length} obligatorio{problemasObligatorios.length === 1 ? '' : 's'} · {problemasDni.length} DNI{problemasDni.length === 1 ? '' : 's'} con problemas
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
        {user?.rol !== 'MESA_ENTRADAS' && (
          <button
            onClick={() => { setChecklistObsText(textoObs); setShowChecklistObs(v => !v); }}
            disabled={totalProblemas === 0}
            className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Generar observación {totalProblemas > 0 && `(${totalProblemas})`}
          </button>
          )}
          <Link
            to={`/documentos?expedienteId=${id}${expediente.partido ? `&partidoId=${expediente.partido.id}` : ''}`}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg"
          >
            Ver todos los documentos →
          </Link>
        </div>
      </div>
    )}
    {showChecklistObs && puedeGestionar && iniciado && (
      <div className="border border-orange-200 rounded-lg bg-orange-50 p-4">
        <div className="flex items-start justify-between mb-2">
          <div>
            <p className="text-sm font-semibold text-orange-800">Observación generada automáticamente</p>
            <p className="text-xs text-orange-600">
              Podés editar el texto antes de copiar. Incluye obligatorios y DNIs (autoridades + apoderados) faltantes o rechazados.
            </p>
          </div>
          <button onClick={() => setShowChecklistObs(false)} className="text-orange-400 hover:text-orange-600 text-lg leading-none">×</button>
        </div>
        <textarea
          value={checklistObsText}
          onChange={e => setChecklistObsText(e.target.value)}
          rows={10}
          className="w-full text-sm text-gray-700 border border-orange-200 rounded-lg p-3 bg-white resize-y"
        />
        <div className="flex flex-wrap justify-end gap-2 mt-2">
          <div className="mr-auto text-xs text-orange-600 self-center">
            {problemasObligatorios.length} obligatorio{problemasObligatorios.length === 1 ? '' : 's'} · {problemasDni.length} DNI{problemasDni.length === 1 ? '' : 's'}
          </div>
          <button onClick={() => setShowChecklistObs(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50">Cerrar</button>
          <button onClick={() => navigator.clipboard.writeText(checklistObsText)} className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium rounded-lg">Copiar texto</button>
        </div>
      </div>
    )}

    {/* REEMPADRONAMIENTO: en vez del viejo checklist de tipos de documento (que
        repetía /documentos), se muestra QUÉ FALTA para avanzar de etapa. Los
        requisitos y los bloqueos son los mismos que deshabilitan los botones del
        encabezado. El resto de los trámites (cambio de autoridades, fusión, etc.)
        sigue con el checklist de siempre. */}
    {/* REEMPADRONAMIENTO (interno): tablero de estado de la documentación. Antes acá
        iba el panel "Para avanzar de etapa", que sólo contaba pendientes; el tablero
        dice qué pasó con CADA documento, que es lo que hay que saber cuando el
        trámite ya está en análisis. `requisitosAvance` sigue siendo el
        discriminador: es no-nulo justamente en reempadronamiento interno.
        El resto de los trámites conserva el checklist de siempre. */}
    {/* ALIANZA: los partidos integrantes van PRIMERO. Es lo que hay que resolver
        antes que nada — mientras quede un integrante fuera de SIREPPAR sin
        verificar, el backend bloquea la elevación al Secretario. */}
    {esAlianzaExp && alianzaDetalle && (
      <PanelIntegrantesAlianza
        expedienteId={id!}
        integrantes={integrantesAlianza}
        puedeVerificar={
          puedeGestionar
          && ['EN_ANALISIS', 'OBSERVADO'].includes(expediente.estado)
          && ['ADMINISTRADOR', 'SUPERVISOR', 'ANALISTA'].includes(user?.rol || '')
        }
      />
    )}

    {/* RESUMEN por sección, de solo lectura. Reemplaza al tablero de documentación
        en TODOS los trámites: el tablero derivaba el estado del tipo contando sus
        archivos, y contar no puede saber cuántos hacen falta cuando el tipo tiene N
        documentos (una constancia por integrante, un DNI por autoridad).
        El check para aprobar NO está acá: vive en /documentos, donde están los
        archivos. Cada fila linkea allá. */}
    {seccionesExp.length > 0 && (
      <>
        <PanelSeccionesDocumentacion
          expedienteId={id!}
          partidoId={expediente.partido?.id}
          secciones={seccionesExp}
          /* La dimensión la decide la etapa que el expediente YA alcanzó, no el
             panel. Estaba clavada en análisis —que es donde resuelve una alianza— y
             un reempadronamiento todavía en admisibilidad mostraba todo "Sin
             revisar" aunque la analista acabara de aprobarlo. */
          dimension={etapaActivaExp}
          tituloSeccion={`${esAlianzaExp ? 'Documentación del Art. 27' : 'Estado de la documentación'}`
            + ` — ${etapaActivaExp === 'ADMISIBILIDAD' ? 'admisibilidad' : 'análisis'}`}
          puedeEditarNota={puedeGestionar}
          /* La nota de la sección es comentario de trabajo de la Secretaría: la
             agrupación no la ve. Lo que sí ve es la OBSERVACIÓN, que es el acto
             formal con plazo, PDF y correo. */
          mostrarNota={isInterno}
        />
        {/* El 4‰ era el último renglón del tablero. No es documentación —no hay
            archivo que aprobar— pero decide si el partido subsiste, así que sigue a
            la vista. En alianzas no corresponde: no tienen afiliados propios. */}
        {puedeVerAfiliados && (
          <ResumenAfiliadosExpediente
            partidoId={expediente.partido?.id}
            afiliados={afiliadosResumen}
          />
        )}
      </>
    )}

    {/* Con secciones sembradas la documentación ya se mostró arriba: ni el tablero
        ni el checklist viejo corresponden (los dos derivan el estado del tipo
        contando archivos).
        El tablero queda como respaldo de lo que TODAVÍA no se sembró, para que el
        reemplazo no dependa de que el backfill haya corrido. Cuando no quede ningún
        expediente sin sembrar, se puede borrar junto con el checklist. */}
    {seccionesExp.length > 0 ? null : requisitosAvance ? (
      <TableroDocumentacion
        expedienteId={id!}
        partidoId={expediente.partido?.id}
        partidoNota={(expediente.partido as any)?.nota}
        docs={docsChecklist}
        tiposObligatorios={OBLIGATORIOS_TIPOS}
        observaciones={expediente.observaciones || []}
        afiliados={afiliadosResumen}
        puedeEditar={puedeGestionar}
      />
    ) : (<>

    {/* Checklist de documentos obligatorios */}
    {(() => {
      const docs = docsChecklist;
      const getDoc = (tipo: string) => {
        const matches = docs.filter((d: any) => d.tipo === tipo);
        if (!matches.length) return undefined;
        const priority: Record<string, number> = { APROBADO: 0, PENDIENTE: 1, RECHAZADO: 2 };
        return matches.sort((a: any, b: any) => (priority[a.estadoRevision] ?? 3) - (priority[b.estadoRevision] ?? 3))[0];
      };
      return (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-700">Checklist rapido de documentos</h3>
          </div>
          <div className="divide-y divide-gray-100 border rounded-lg overflow-hidden">
            {OBLIGATORIOS_TIPOS.map(({ tipo, label }) => {
              const doc = getDoc(tipo);
              const estado = doc?.estadoRevision;
              if (estado === 'APROBADO') return (
                <div key={tipo} className="flex items-center gap-3 px-4 py-3 bg-white">
                  <div className="w-7 h-7 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">{label}</p>
                    {doc?.revisadoPorNombre && <p className="text-xs text-gray-400">Verificado por {doc.revisadoPorNombre}{doc.revisadoEn ? ` · ${new Date(doc.revisadoEn).toLocaleDateString('es-AR')}` : ''}</p>}
                  </div>
                  <span className="text-xs text-gray-400 flex-shrink-0">{doc?.creadoEn ? new Date(doc.creadoEn).toLocaleDateString('es-AR') : ''}</span>
                </div>
              );
              if (estado === 'RECHAZADO') return (
                <div key={tipo} className="flex items-center gap-3 px-4 py-3 bg-red-50">
                  <div className="w-7 h-7 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-red-700">{label}</p>
                    <p className="text-xs text-red-500">Rechazado{doc?.revisadoPorNombre ? ` por ${doc.revisadoPorNombre}` : ''}{doc?.observacion ? ` · Motivo: ${doc.observacion}` : ''}</p>
                  </div>
                  <span className="text-xs text-gray-400 flex-shrink-0">{doc?.creadoEn ? new Date(doc.creadoEn).toLocaleDateString('es-AR') : ''}</span>
                </div>
              );
              if (estado === 'PENDIENTE') return (
                <div key={tipo} className="flex items-center gap-3 px-4 py-3 bg-white">
                  <div className="w-7 h-7 rounded-full bg-yellow-400 flex items-center justify-center flex-shrink-0">
                    <div className="w-2.5 h-2.5 rounded-full bg-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-yellow-800">{label}</p>
                    <p className="text-xs text-yellow-600">Cargado · pendiente de revisión</p>
                  </div>
                  <span className="text-xs text-gray-400 flex-shrink-0">{doc?.creadoEn ? new Date(doc.creadoEn).toLocaleDateString('es-AR') : ''}</span>
                </div>
              );
              return (
                <div key={tipo} className="flex items-center gap-3 px-4 py-3 bg-white">
                  <div className="w-7 h-7 rounded-full border-2 border-dashed border-gray-300 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-400">{label}</p>
                  </div>
                  <span className="text-xs text-gray-400">No cargado</span>
                </div>
              );
            })}

            {/* Resumen DNIs: toda persona (autoridades + apoderados) debe tener frente y dorso verificados */}
            {(() => {
              const problemas = problemasDni.length;
              const pendientes = dniPendientesRevision;
              const ok = problemas === 0 && pendientes === 0;
              const bg = problemas > 0 ? 'bg-red-50' : pendientes > 0 ? 'bg-white' : 'bg-white';
              const icon = problemas > 0
                ? <div className="w-7 h-7 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0"><svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg></div>
                : pendientes > 0
                  ? <div className="w-7 h-7 rounded-full bg-yellow-400 flex items-center justify-center flex-shrink-0"><div className="w-2.5 h-2.5 rounded-full bg-white" /></div>
                  : <div className="w-7 h-7 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0"><svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg></div>;
              return (
                <div className={`flex items-center gap-3 px-4 py-3 ${bg}`}>
                  {icon}
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${problemas > 0 ? 'text-red-700' : pendientes > 0 ? 'text-yellow-800' : 'text-gray-900'}`}>DNIs de autoridades y apoderados</p>
                    {ok ? (
                      <p className="text-xs text-gray-400">Todas las personas tienen DNI (frente y dorso) verificado</p>
                    ) : (
                      <p className={`text-xs ${problemas > 0 ? 'text-red-500' : 'text-yellow-600'}`}>
                        {problemas > 0 && `${problemas} sin cargar / rechazado${problemas === 1 ? '' : 's'}`}
                        {problemas > 0 && pendientes > 0 && ' · '}
                        {pendientes > 0 && `${pendientes} pendiente${pendientes === 1 ? '' : 's'} de revisión`}
                      </p>
                    )}
                  </div>
                  {!ok && <span className="text-xs font-bold text-gray-500 flex-shrink-0">{problemas + pendientes} lado{problemas + pendientes === 1 ? '' : 's'}</span>}
                </div>
              );
            })()}

            {/* Resumen Otros documentos: cargados que no son obligatorios ni DNIs */}
            {(() => {
              const rechazados = problemasOtros.length;
              const pendientes = otrosPendientesRevision;
              const ok = rechazados === 0 && pendientes === 0;
              const bg = rechazados > 0 ? 'bg-red-50' : 'bg-white';
              const icon = rechazados > 0
                ? <div className="w-7 h-7 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0"><svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg></div>
                : pendientes > 0
                  ? <div className="w-7 h-7 rounded-full bg-yellow-400 flex items-center justify-center flex-shrink-0"><div className="w-2.5 h-2.5 rounded-full bg-white" /></div>
                  : <div className="w-7 h-7 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0"><svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg></div>;
              return (
                <div className={`flex items-center gap-3 px-4 py-3 ${bg}`}>
                  {icon}
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${rechazados > 0 ? 'text-red-700' : pendientes > 0 ? 'text-yellow-800' : 'text-gray-900'}`}>Otros documentos</p>
                    {ok ? (
                      <p className="text-xs text-gray-400">Sin otros documentos pendientes</p>
                    ) : (
                      <p className={`text-xs ${rechazados > 0 ? 'text-red-500' : 'text-yellow-600'}`}>
                        {rechazados > 0 && `${rechazados} rechazado${rechazados === 1 ? '' : 's'}`}
                        {rechazados > 0 && pendientes > 0 && ' · '}
                        {pendientes > 0 && `${pendientes} pendiente${pendientes === 1 ? '' : 's'} de revisión`}
                      </p>
                    )}
                  </div>
                  {!ok && <span className="text-xs font-bold text-gray-500 flex-shrink-0">{rechazados + pendientes}</span>}
                </div>
              );
            })()}
          </div>
        </div>
      );
      
    })()}
    </>)}
  </div>
)}

  {/* TAB: Observaciones */}
  {activeTab === 'obs' && (
    <div className="space-y-6">

      {/* Panel de Revisión — internos, en cualquier estado menos BORRADOR.
          Un expediente ya resuelto (vigente, aprobado, rechazado, archivado) también
          se puede observar: si aparece algo que subsanar después de la resolución, la
          Secretaría tiene que poder pedirlo y la agrupación responderlo. El backend
          NO mueve el expediente a OBSERVADO fuera de las etapas de análisis, así que
          observar un trámite terminado no deshace su resolución. */}
      {expediente.estado !== 'BORRADOR' && puedeGestionar && (
        <div className="border border-yellow-200 rounded-lg">
          <div className="px-6 py-4 border-b bg-yellow-50 rounded-t-lg">
            <h2 className="text-base font-medium text-yellow-800">Panel de Revisión</h2>
            {expediente.asignado && (
              <p className="text-sm text-yellow-600 mt-1">Revisando: {expediente.asignado.nombre} {expediente.asignado.apellido}</p>
            )}
          </div>
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{esFaseAtributosRecon ? 'Intimación' : 'Observaciones'}</label>
              <RichTextEditor
                value={obsTexto}
                onChange={val => setObsTexto(sanitizarTextoLive(val))}
                placeholder={esFaseAtributosRecon ? 'Escriba la intimación sobre los atributos del partido...' : 'Escriba las observaciones sobre la documentación...'}
                minHeight={180}
              />
            </div>
            <AdjuntosInforme
              archivos={obsAdjuntos}
              onChange={setObsAdjuntos}
              titulo="Adjuntar documentos (opcional)"
              ayuda="Por ejemplo, una resolución para notificar. Hasta 50 MB por archivo y 50 MB en total. El partido los descarga desde el expediente; los PDF livianos también le llegan por mail."
              maxMb={MAX_MB_TOTAL_OBSERVACION}
              avisoCorreo={false}
            />
            <div className="flex flex-wrap gap-2 items-center">
              {/* Plazo para responder: 1 a 15 días. Antes eran dos opciones fijas
                  (15 y 30) y no había forma de cursar una observación con un plazo
                  más corto, que es lo habitual cuando falta un solo documento. */}
              <select value={obsPlazoDias} onChange={(e) => setObsPlazoDias(Number(e.target.value))} className="px-3 py-2 border rounded-lg text-sm bg-white">
                {PLAZOS_OBSERVACION.map((d) => (
                  <option key={d} value={d}>{d} {d === 1 ? 'día' : 'días'}</option>
                ))}
                <option value={0}>Sin plazo</option>
              </select>
              {!obsSinPlazo && (
                <select value={obsRenovarPlazo ? 'si' : 'no'} onChange={(e) => setObsRenovarPlazo(e.target.value === 'si')} className="px-3 py-2 border rounded-lg text-sm bg-white">
                  <option value="no">No renovar plazo</option>
                  <option value="si">Renovar plazo</option>
                </select>
              )}
              <button onClick={() => {
                  if (superaTotalAdjuntos(obsAdjuntos)) {
                    toast.error(`Los adjuntos suman más de ${MAX_MB_TOTAL_OBSERVACION} MB. Mandá los más pesados en otra observación.`);
                    return;
                  }
                  if (confirm(esFaseAtributosRecon ? '¿Enviar la intimación? Se notificará al partido por email.' : '¿Enviar la observación? Se notificará al apoderado por email.')) enviarObservacionMutation.mutate();
                }}
                disabled={!obsTexto || enviarObservacionMutation.isPending}
                className="px-3 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 text-sm disabled:opacity-50">
                {enviarObservacionMutation.isPending ? 'Enviando...' : (esFaseAtributosRecon ? 'Enviar Intimación' : 'Enviar Observación')}
              </button>
              {/* Reconocimiento y reempadronamiento elevan desde el botón "Informe
                  al Secretario" de arriba, que abre el modal del informe en PDF. */}
              {!esReconocimiento && expediente.tipo !== 'REEMPADRONAMIENTO' && expediente.tipo !== 'CONSTITUCION_ALIANZA' && (
                <button onClick={() => { if (confirm('¿Enviar informe al Secretario Electoral?')) enviarSecretarioMutation.mutate(); }}
                  disabled={enviarSecretarioMutation.isPending}
                  className="px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm disabled:opacity-50">
                  {enviarSecretarioMutation.isPending ? 'Enviando...' : 'Informe al Secretario'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* El "Panel de Intimación" inline se sacó de acá.
          En admisibilidad se dibujaba JUNTO al Panel de Revisión de arriba, así que
          la pestaña quedaba con DOS cuadros de escritura: uno para observar y otro
          para intimar, sobre el mismo expediente y la misma etapa.
          Intimar sigue estando: es el botón "Intimar" / "Re-intimar" del encabezado,
          que abre el modal con el mismo campo y el mismo endpoint. Un acto grave se
          pide desde un botón explícito, no desde un cuadro que ya está abierto.
          En RECONOCIMIENTO este panel ya estaba excluido por el mismo motivo — el
          guard estaba puesto sólo para ese trámite. */}

      {/* Banner de subsanación tras re-presentación */}
      {precargarRespuesta && expediente.observaciones?.some(o => o.estado === 'PENDIENTE' && !o.subsanada) && (
        <div className="bg-amber-50 border-l-4 border-amber-500 rounded-r-lg p-4">
          <p className="text-sm text-amber-900">
            <strong>Falta responder formalmente la observación.</strong>
            <span className="block text-xs text-amber-800 mt-0.5">
              Generaste una nueva versión de la rendición. Para completar la subsanación,
              respondé la observación pendiente con el detalle de las correcciones realizadas.
            </span>
          </p>
        </div>
      )}

      {/* Comunicaciones */}
      {expediente.observaciones && expediente.observaciones.length > 0 ? (() => {
        // Identifico la primera observación PENDIENTE — solo a esa precargo texto
        const primeraPendienteId = expediente.observaciones
          .slice()
          .sort((a, b) => new Date(a.creadoEn).getTime() - new Date(b.creadoEn).getTime())
          .find(o => o.estado === 'PENDIENTE' && !o.subsanada)?.id;
        return (
        <div className="border border-orange-200 rounded-lg">
          <div className="px-4 py-3 border-b border-orange-200 bg-orange-50 rounded-t-lg">
            <h2 className="text-base font-medium text-orange-800">Comunicaciones</h2>
          </div>
          <div className="p-4 space-y-4">
            {expediente.observaciones.map((obs) => {
              const pdfsObservacion = (expediente.documentos ?? [])
                .filter(d => d.tipo === 'NOTA' && d.titulo?.includes(expediente.numero))
                .sort((a, b) => new Date(a.creadoEn).getTime() - new Date(b.creadoEn).getTime());
              const obsOrdenadas = (expediente.observaciones ?? [])
                .slice()
                .sort((a, b) => new Date(a.creadoEn).getTime() - new Date(b.creadoEn).getTime());
              const obsIndex = obsOrdenadas.findIndex(o => o.id === obs.id);
              const pdfObservacion = pdfsObservacion.find((pdf) => {
                const sigObs = obsOrdenadas[obsIndex + 1];
                const fechaPdf = new Date(pdf.creadoEn).getTime();
                const fechaObs = new Date(obs.creadoEn).getTime() - 5000;
                const fechaSigObs = sigObs ? new Date(sigObs.creadoEn).getTime() : Infinity;
                return fechaPdf >= fechaObs && fechaPdf < fechaSigObs;
              });
              // SE PUEDE RESPONDER MÁS DE UNA VEZ, en todos los trámites, mientras la
              // Secretaría no haya tildado el check de subsanada. Responder no cierra
              // nada: RESPONDIDA sólo significa "hay algo para revisar".
              //
              // Antes el corte era `estado === 'PENDIENTE'`, así que el primero en
              // contestar dejaba afuera al resto: la observación pide dos documentos,
              // uno los sube y el otro ya no puede. Las respuestas se acumulan.
              //
              // En ALIANZA responden los firmantes de cualquiera de las agrupaciones
              // (la alianza o un integrante), incluidos presidentes y vicepresidentes.
              const rolFirmanteAlianza = ['APODERADO_FIRMANTE', 'PRESIDENTE_FIRMANTE', 'VICEPRESIDENTE_FIRMANTE']
                .includes(user?.rol || '');
              const isExternoUser = esAlianzaExp
                ? rolFirmanteAlianza
                : ['APODERADO_FIRMANTE', 'PRESIDENTE_FIRMANTE', 'CARGADOR'].includes(user?.rol || '');
              const puedeResponder = isExternoUser
                && ['PENDIENTE', 'RESPONDIDA'].includes(obs.estado)
                && !obs.subsanada;
              // #389: consultar NO es responder: no cambia la observación ni corta el plazo.
              // Lo ven quienes pueden iniciar consultas (apoderados firmantes e
              // iniciadores, según el backend), en cualquier estado o plazo. En un expediente
              // de alianza, solo con la alianza como agrupación activa: el backend exige
              // que el expediente sea de la agrupación de la sesión.
              const puedeConsultar = puedeCrearConsulta && puedeUsarConsultas(user?.rol)
                && (!esAlianzaExp || agrupaciones.activa?.partidoId === expediente.partido?.id);
              // La SEP (roles internos de escritura) puede cargar la respuesta en
              // nombre del partido cuando ésta llegó por fuera del sistema.
              const isInternoEscritura = ['ADMINISTRADOR', 'SUPERVISOR', 'ANALISTA', 'AUTORIDAD_FIRMANTE'].includes(user?.rol || '');
              const puedeResponderSEP = isInternoEscritura
                && ['PENDIENTE', 'RESPONDIDA'].includes(obs.estado)
                && !obs.subsanada;
              const esPrimeraPendiente = obs.id === primeraPendienteId;
              // Adjuntos de ESTA observación que nadie revisó todavía.
              const adjuntosPendientesObs = (expediente.documentos || []).filter(d =>
                (obs.tipo === 'INTIMACION' ? d.intimacionRespuestaId === obs.id : d.observacionRespuestaId === obs.id)
                && (etapaActivaExp === 'ANALISIS' ? d.estadoRevisionAnalisis : d.estadoRevisionAdmisibilidad) === 'PENDIENTE',
              ).length;
              // El check por observación va en TODOS los trámites. Antes era sólo de
              // alianzas y el resto cerraba todas de golpe con "Aceptar subsanación",
              // que no sirve cuando una está subsanada y otra no.
              // SIN `&& !obs.subsanada`: el check queda VISIBLE y TILDADO después de
              // marcarlo. Escondiéndolo, el estado sólo se leía en una etiqueta de
              // texto y no había forma de volver atrás.
              const puedeResolverObs = puedeGestionar
                && ['ADMINISTRADOR', 'SUPERVISOR', 'ANALISTA'].includes(user?.rol || '');
              return (
                <div
                  key={obs.id}
                  className="space-y-3"
                  {...(esPrimeraPendiente ? { 'data-observacion-pendiente': 'true' } : {})}
                >
                  <div className="flex gap-3">
                    <div className="w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-orange-600 text-xs font-bold">S</span>
                    </div>
                    <div className="flex-1 bg-orange-50 border border-orange-200 rounded-lg p-3">
                      <div className="flex justify-between items-start mb-1">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-orange-800">Secretaría Electoral</span>
                          {(() => {
                            // En reconocimiento el tipo se guarda como OBSERVACION, así
                            // que el término real se lee del marcador de etapa que dejó
                            // el backend al cursarla (etapaPlazo). Ojo: NO se puede usar
                            // la fase actual del expediente — un mensaje de la etapa de
                            // atributos es una intimación para siempre, aunque el trámite
                            // ya haya avanzado a documentación o afiliados.
                            // Para los mensajes anteriores al marcador se cae a la fase
                            // actual, que es lo mejor disponible.
                            // Orden de precedencia:
                            //  1. tipo INTIMACION → intimación, sin ambigüedad (la
                            //     cursó el panel de admisibilidad, no el de reconocimiento).
                            //  2. marcador de etapa que dejó el flujo de reconocimiento.
                            //  3. fallback a la fase actual, para mensajes viejos sin marcador.
                            const esIntim = obs.tipo === 'INTIMACION'
                              ? true
                              : !esReconocimiento
                                ? false
                                : obs.etapaPlazo === 'INTIMACION_RECONOCIMIENTO'
                                  ? true
                                  : obs.etapaPlazo === 'OBSERVACION_RECONOCIMIENTO'
                                    ? false
                                    : usaTerminoIntimacion;
                            return (
                              <span className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${
                                esIntim ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {esIntim ? 'Intimación' : 'Observación'}
                              </span>
                            );
                          })()}
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className="text-xs text-gray-400">{formatDateShort(obs.creadoEn)}</span>
                          {/* El check de la observación. Tildarlo es decir "lo que
                              acompañó el partido alcanza", así que no se puede con
                              adjuntos que nadie miró — el backend lo rechaza y acá se
                              avisa antes, diciendo cuántos faltan. Se puede destildar:
                              un juicio se corrige. */}
                          {puedeResolverObs && (
                            <input
                              type="checkbox"
                              checked={!!obs.subsanada}
                              disabled={resolverObsAlianzaMutation.isPending || reabrirObsAlianzaMutation.isPending}
                              onChange={(e) => {
                                if (!e.target.checked) {
                                  reabrirObsAlianzaMutation.mutate(obs.id);
                                  return;
                                }
                                if (adjuntosPendientesObs > 0) {
                                  toast.error(
                                    `No es posible dar por subsanada: quedan ${adjuntosPendientesObs} ` +
                                    `${adjuntosPendientesObs === 1 ? 'documento adjunto pendiente' : 'documentos adjuntos pendientes'} de revisión.`,
                                  );
                                  e.target.checked = false;
                                  return;
                                }
                                resolverObsAlianzaMutation.mutate(obs.id);
                              }}
                              title={obs.subsanada
                                ? 'Destildar: la observación vuelve a quedar abierta'
                                : adjuntosPendientesObs > 0
                                  ? `Quedan ${adjuntosPendientesObs} adjunto(s) sin revisar`
                                  : 'Dar por subsanada'}
                              className="w-5 h-5 rounded border-gray-300 accent-green-600 focus:ring-green-500 cursor-pointer disabled:cursor-wait"
                            />
                          )}
                        </div>
                      </div>
                      {/* Una observación puede traer la transcripción completa de un
                          acta o de una carta orgánica. Pintada de una, empujaba el
                          plazo, la nota en PDF y las respuestas fuera de la pantalla,
                          así que se muestra el arranque y el resto va detrás de
                          "Leer más". No se recorta nada: el texto entero ya está acá. */}
                      <TextoColapsable
                        texto={obs.texto}
                        tope={PREVIEW_OBSERVACION}
                        bloque
                        render={(fragmento) => <MarkdownDisplay content={fragmento} />}
                      />
                      {obs.plazoDias && obs.fechaLimite && (
                        <div className="mt-1 flex gap-3 text-xs">
                          <span className="text-orange-600 font-medium">Plazo: {obs.plazoDias}d</span>
                          <span className="text-orange-600">Límite: {formatDateShort(obs.fechaLimite)}</span>
                          {obs.destinatario && obs.fechaLimite && (
                            <span className={`font-medium ${obs.destinatario === 'SEP' ? 'text-blue-600' : obs.destinatario === 'PARTIDO' ? 'text-orange-600' : 'text-purple-600'}`}>
                              {obs.destinatario === 'SEP'
                                ? `Pendiente de resolución SEP (${Math.max(0, Math.ceil((new Date(obs.fechaLimite).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))} días restantes)`
                                : obs.destinatario === 'PARTIDO'
                                ? `Esperando respuesta del partido (${Math.max(0, Math.ceil((new Date(obs.fechaLimite).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))} días restantes)`
                                : `Esperando respuesta del afiliado (${Math.max(0, Math.ceil((new Date(obs.fechaLimite).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))} días restantes)`}
                            </span>
                          )}
                        </div>
                      )}
                      {!obs.fechaLimite && (
                        <div className="mt-1 text-xs">
                          <span className="text-orange-600 font-medium">Sin plazo</span>
                        </div>
                      )}
                      {pdfObservacion && (
                        <button onClick={() => documentoService.download(pdfObservacion.id)}
                          className="mt-1 inline-flex items-center gap-1 text-xs text-orange-600 hover:text-orange-800 underline">
                          Ver nota PDF
                        </button>
                      )}
                      {obs.adjuntos && obs.adjuntos.length > 0 && (
                        <ul className="mt-2 space-y-1" aria-label="Archivos adjuntos de la observación">
                          {obs.adjuntos.map((adj) => (
                            <li key={adj.id}>
                              <button type="button" onClick={() => abrirAdjuntoObservacion(obs.id, adj.id)}
                                className="inline-flex items-center gap-1 text-xs text-orange-600 hover:text-orange-800 underline break-all text-left">
                                📎 {adj.nombreOriginal}
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                  {obs.respuestaTexto && (
                    <div className="flex gap-3 pl-8">
                      <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-blue-600 text-xs font-bold">A</span>
                      </div>
                      <div className="flex-1 bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <div className="flex justify-between items-start mb-1">
                          <span className="text-xs font-medium text-blue-800">Apoderado</span>
                          <span className="text-xs text-gray-500">{obs.respuestaFecha ? formatDateShort(obs.respuestaFecha) : ''}</span>
                        </div>
                        <MarkdownDisplay content={obs.respuestaTexto || ''} />
                        {(() => {
                          // El VÍNCULO del adjunto sigue el tipo de observación
                          // (intimación → intimacionRespuestaId; observación →
                          // observacionRespuestaId). La DIMENSIÓN de verificación que
                          // se muestra debe ser la MISMA que usa la pantalla Documentos
                          // (etapaActivaExp), para no mostrar "pendiente" algo que ya
                          // se verificó allí.
                          const esIntimacion = obs.tipo === 'INTIMACION';
                          const vinculados = (expediente.documentos?.filter(d =>
                            esIntimacion ? d.intimacionRespuestaId === obs.id : d.observacionRespuestaId === obs.id
                          ) || []).map(d => ({
                            ...d,
                            // RECONOCIMIENTO verifica en una sola dimensión (admisibilidad),
                            // aun en la etapa de documentación: se mira SIEMPRE esa dimensión.
                            estadoRevision: esReconocimiento
                              ? d.estadoRevisionAdmisibilidad
                              : (etapaActivaExp === 'ANALISIS' ? d.estadoRevisionAnalisis : d.estadoRevisionAdmisibilidad),
                          }));
                          // La constancia de presentación (tipo NOTA) la emite la SEP y viene
                          // vinculada a la respuesta, pero NO es un adjunto del partido: va
                          // aparte y sin estado de revisión, para que no se cuente como
                          // pendiente ni se confunda con lo que hay que revisar.
                          const constancias = vinculados.filter(d => d.tipo === 'NOTA');
                          const docsRespuesta = vinculados.filter(d => d.tipo !== 'NOTA');
                          if (vinculados.length === 0) return null;
                          const pendientesCount = docsRespuesta.filter(d => d.estadoRevision === 'PENDIENTE').length;
                          return (
                            <div className="mt-3 space-y-2">
                              {docsRespuesta.length > 0 && (
                              <div className="flex flex-wrap items-center justify-between gap-2">
                                <p className="text-xs text-blue-600 font-medium">Archivos adjuntos:</p>
                                {puedeGestionar && pendientesCount > 0 && (
                                  <Link
                                    to={`/documentos?expedienteId=${expediente.id}&revisarObs=${obs.id}`}
                                    className="inline-flex items-center gap-1 px-3 py-1 bg-orange-600 hover:bg-orange-700 text-white text-xs font-medium rounded-md"
                                    title="Revisar los adjuntos en /documentos: se abren los acordeones y se destacan en amarillo"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                    </svg>
                                    Revisar en Documentos ({pendientesCount} pendiente{pendientesCount === 1 ? '' : 's'})
                                  </Link>
                                )}
                              </div>
                              )}
                              {docsRespuesta.length > 0 && (
                              <div className="space-y-1">
                                {docsRespuesta.map(doc => (
                                  <button key={doc.id} onClick={async () => {
                                    const response = await api.get(`/documentos/${doc.id}/url`);
                                    const url = response.data.data?.url || response.data.url;
                                    if (url) window.open(url, '_blank');
                                  }} className="flex items-center gap-2 text-sm text-blue-700 hover:text-blue-900">
                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 text-[10px] font-semibold uppercase tracking-wide">
                                      {tipoDocLabels[doc.tipo] || doc.tipo}
                                    </span>
                                    <span className="underline">{doc.titulo || doc.nombreOriginal}</span>
                                    {doc.estadoRevision === 'APROBADO' && (
                                      <span className="text-[10px] text-green-700 font-medium">✓ Aprobado</span>
                                    )}
                                    {doc.estadoRevision === 'RECHAZADO' && (
                                      <span className="text-[10px] text-red-700 font-medium">✕ Rechazado</span>
                                    )}
                                  </button>
                                ))}
                              </div>
                              )}
                              {constancias.length > 0 && (
                                <div className="space-y-1 border-t border-blue-200 pt-2">
                                  {constancias.map(doc => (
                                    <button key={doc.id} onClick={async () => {
                                      const response = await api.get(`/documentos/${doc.id}/url`);
                                      const url = response.data.data?.url || response.data.url;
                                      if (url) window.open(url, '_blank');
                                    }} className="flex items-center gap-2 text-sm text-gray-700 hover:text-gray-900">
                                      <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-gray-200 text-gray-700 text-[10px] font-semibold uppercase tracking-wide">
                                        NOTA presentación
                                      </span>
                                      <span className="underline">{doc.titulo || doc.nombreOriginal}</span>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  )}
                  {puedeResponder && (
                    <RespuestaObservacion
                      expedienteId={expediente.id}
                      observacionId={obs.id}
                      esAlianza={esAlianzaExp}
                      partidoId={expediente.partido?.id}
                      tiposPermitidos={tiposRespuestaRecon}
                      labelOverrides={labelsRespuestaRecon}
                      onSuccess={() => {
                        queryClient.invalidateQueries({ queryKey: ['expediente', id] });
                        queryClient.invalidateQueries({ queryKey: ['gestion-expedientes'] });
                        queryClient.invalidateQueries({ queryKey: ['dashboard-alertas'] });
                        queryClient.invalidateQueries({ queryKey: ['dashboard-alertas-internas'] });
                      }}
                      textoInicial={
                        precargarRespuesta && obs.id === primeraPendienteId
                          ? TEXTO_RESPUESTA_PRECARGADO
                          : undefined
                      }
                      corregirHref={(() => {
                        const partidoId = expediente.partido?.id;
                        if (!partidoId) return undefined;
                        if (expediente.tipo === 'RENDICION_ANUAL' && expediente.datosFormulario?.rendicionAnualId) {
                          return `/financiamiento/rendicion-anual/${partidoId}?rendicion=${expediente.datosFormulario.rendicionAnualId}`;
                        }
                        if (expediente.tipo === 'RENDICION_CAMPANA' && expediente.datosFormulario?.rendicionCampanaId) {
                          return `/financiamiento/rendicion-campana/${partidoId}?rendicion=${expediente.datosFormulario.rendicionCampanaId}`;
                        }
                        return undefined;
                      })()}
                    />
                  )}
                  {puedeConsultar && (
                    <div className="flex justify-end">
                      <Link
                        to={`/consultas/nueva?expedienteId=${expediente.id}&observacionId=${obs.id}`}
                        className="px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                      >
                        Hacer una consulta
                      </Link>
                    </div>
                  )}
                  {puedeResponderSEP && (
                    <RespuestaObservacion
                      expedienteId={expediente.id}
                      observacionId={obs.id}
                      esAlianza={esAlianzaExp}
                      partidoId={expediente.partido?.id}
                      tiposPermitidos={tiposRespuestaRecon}
                      labelOverrides={labelsRespuestaRecon}
                      modoSEP
                      nombrePartido={expediente.partido?.denominacion}
                      onSuccess={() => {
                        queryClient.invalidateQueries({ queryKey: ['expediente', id] });
                        queryClient.invalidateQueries({ queryKey: ['gestion-expedientes'] });
                        queryClient.invalidateQueries({ queryKey: ['dashboard-alertas'] });
                        queryClient.invalidateQueries({ queryKey: ['dashboard-alertas-internas'] });
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
        );
      })() : (
        <p className="text-center text-sm text-gray-400 py-8">Sin observaciones registradas</p>
      )}
    </div>
  )}

  {/* TAB: Interacciones - solo internos */}
  {isInterno && activeTab === 'inter' && (
    <div>
      <div className="flex justify-between items-center mb-4">
  <div>
    <h3 className="text-base font-medium text-gray-900">Interacciones con el partido</h3>
    {expediente.partido && (
      <Link
        to={`/interacciones?partidoId=${expediente.partido.id}`}
        className="text-xs text-blue-600 hover:underline"
      >
        Ver todas las interacciones del partido →
      </Link>
    )}
  </div>
  {puedeEscribirInteracciones(user?.rol) && (
  <Link
    to={`/interacciones/nueva?expedienteId=${id}${expediente.partido ? `&partidoId=${expediente.partido.id}` : ''}`}
    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
  >
    + Nueva interacción
  </Link>
  )}
</div>
      {!interacciones || interacciones.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <p className="text-sm">No hay interacciones registradas para este expediente.</p>
          {puedeEscribirInteracciones(user?.rol) && (
          <Link to={`/interacciones/nueva?expedienteId=${id}`} className="mt-2 inline-block text-sm text-blue-600 hover:underline">
            Registrar primera interacción →
          </Link>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {interacciones.map((inter: any) => {
            const tipoColors: Record<string, string> = {
              LLAMADA: 'bg-green-500', WHATSAPP: 'bg-green-700',
              EMAIL: 'bg-yellow-500', PRESENCIAL: 'bg-purple-500',
              VIDEOLLAMADA: 'bg-indigo-500', NOTA_INTERNA: 'bg-gray-400',
            };
            const tipoLabels: Record<string, string> = {
              LLAMADA: 'Llamada', WHATSAPP: 'WhatsApp', EMAIL: 'Email',
              PRESENCIAL: 'Presencial', VIDEOLLAMADA: 'Videollamada', NOTA_INTERNA: 'Nota interna',
            };
            const tipoBadgeColors: Record<string, string> = {
              LLAMADA: 'bg-blue-100 text-blue-800', WHATSAPP: 'bg-green-100 text-green-800',
              EMAIL: 'bg-yellow-100 text-yellow-800', PRESENCIAL: 'bg-purple-100 text-purple-800',
              VIDEOLLAMADA: 'bg-indigo-100 text-indigo-800', NOTA_INTERNA: 'bg-gray-100 text-gray-800',
            };
            return (
              <div key={inter.id} className="flex gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1 ${tipoColors[inter.tipo] || 'bg-gray-400'}`}>
                  <span className="text-white text-xs font-bold">{(tipoLabels[inter.tipo] || inter.tipo)[0]}</span>
                </div>
                <div className="flex-1 border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500 font-medium">
                        {new Date(inter.fechaInteraccion).toLocaleDateString('es-AR')}
                      </span>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded ${tipoBadgeColors[inter.tipo] || 'bg-gray-100 text-gray-700'}`}>
                        {tipoLabels[inter.tipo] || inter.tipo}
                      </span>
                    </div>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${inter.estado === 'RESUELTO' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                      {inter.estado === 'RESUELTO' ? 'Resuelto' : 'Pendiente'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-800 font-medium">{inter.resumen}</p>
                  {inter.contactoNombre && (
                    <p className="text-xs text-gray-400 mt-1">
                      Contacto: {inter.contactoNombre}
                      {inter.registradoPor && ` · Registrado por ${inter.registradoPor.nombre}`}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  )}

  {/* TAB: Datos del Trámite */}
  {activeTab === 'data' && (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {esAlianzaExp && alianzaDetalle && (
        <DatosAlianzaTramite
          alianza={alianzaDetalle.alianza}
          integrantes={integrantesAlianza}
          apoderados={alianzaDetalle.apoderados ?? []}
        />
      )}
      {/* AGRUPACIÓN del expediente. Sólo las alianzas tenían su panel de datos, así
          que en reempadronamiento y reconocimiento esta pestaña quedaba casi vacía:
          mostraba la fecha de presentación y el asignado, y nada del partido. */}
      {!esAlianzaExp && expediente.partido && (
        <div>
          <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest border-b pb-2 mb-4">
            Datos de la Agrupación
          </h4>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between py-1.5 border-b border-gray-100">
              <span className="text-gray-500">Denominación</span>
              <span className="font-medium text-right">{expediente.partido.denominacion}</span>
            </div>
            {expediente.partido.sigla && (
              <div className="flex justify-between py-1.5 border-b border-gray-100">
                <span className="text-gray-500">Sigla</span>
                <span className="font-medium">{expediente.partido.sigla}</span>
              </div>
            )}
            {expediente.partido.numero != null && (
              <div className="flex justify-between py-1.5 border-b border-gray-100">
                <span className="text-gray-500">Número</span>
                <span className="font-medium">{expediente.partido.numero}</span>
              </div>
            )}
            {expediente.partido.tipo && (
              <div className="flex justify-between py-1.5 border-b border-gray-100">
                <span className="text-gray-500">Ámbito</span>
                <span className="font-medium">{ambitoPartidoLabels[expediente.partido.tipo] || expediente.partido.tipo}</span>
              </div>
            )}
            {expediente.partido.localidad && (
              <div className="flex justify-between py-1.5 border-b border-gray-100">
                <span className="text-gray-500">Localidad</span>
                <span className="font-medium">{expediente.partido.localidad}</span>
              </div>
            )}
            <div className="flex justify-between py-1.5">
              <span className="text-gray-500">Ficha del partido</span>
              <Link to={`/partidos/${expediente.partido.id}`} className="text-blue-600 hover:underline font-medium">
                Abrir →
              </Link>
            </div>
          </div>
        </div>
      )}
      <div>
        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest border-b pb-2 mb-4">Información del Expediente</h4>
        <div className="space-y-3 text-sm">
          {/* Número, tipo y estado: estaban sólo en el encabezado de la página, así
              que la pestaña de datos no decía de qué trámite estaba hablando. */}
          <div className="flex justify-between py-1.5 border-b border-gray-100">
            <span className="text-gray-500">Expediente</span>
            <span className="font-medium">{expediente.numero}</span>
          </div>
          <div className="flex justify-between py-1.5 border-b border-gray-100">
            <span className="text-gray-500">Trámite</span>
            <span className="font-medium">{tipoLabels[expediente.tipo] ?? expediente.tipo}</span>
          </div>
          <div className="flex justify-between py-1.5 border-b border-gray-100">
            <span className="text-gray-500">Estado</span>
            <span className="font-medium capitalize">{expediente.estado.replace(/_/g, ' ').toLowerCase()}</span>
          </div>

          {expediente.fechaPresentacion && (
            <div className="flex justify-between py-1.5 border-b border-gray-100">
              <span className="text-gray-500">Presentación</span>
              <span>{formatDateShort(expediente.fechaPresentacion)}</span>
            </div>
          )}
          {isInterno && (
            <div className="flex justify-between py-1.5">
              <span className="text-gray-500">Asignado a</span>
              <span className="font-medium">{expediente.asignado ? `${expediente.asignado.nombre} ${expediente.asignado.apellido}` : 'Sin asignar'}</span>
            </div>
          )}
          {expediente.resolucionNumero && (
            <div className="pt-3 border-t">
              <div className="flex justify-between">
                <span className="text-gray-500">Resolución</span>
                <span className="font-medium">{expediente.resolucionNumero}</span>
              </div>
              {expediente.resolucionTexto && (
                <p className="text-gray-600 mt-1">{expediente.resolucionTexto}</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* APODERADOS de la agrupación. Son la contraparte del trámite: quien firma la
          presentación y quien recibe las intimaciones. Faltaban en esta pestaña, así
          que para saber a quién escribirle había que salir a la ficha del partido.
          Las alianzas ya los traen en DatosAlianzaTramite. */}
      {!esAlianzaExp && expediente.partido && (
        <div className="md:col-span-2">
          <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest border-b pb-2 mb-4">
            Apoderados
          </h4>
          {(() => {
            // Los apoderados con fecha de cese ya no representan al partido: mostrarlos
            // acá haría pensar que se les puede notificar.
            const activos = (apoderadosPartido ?? []).filter((a) => a.activo !== false);
            if (activos.length === 0) {
              return (
                <p className="text-sm text-gray-500">
                  {apoderadosPartido ? 'La agrupación no tiene apoderados registrados.' : 'Cargando…'}
                </p>
              );
            }
            return (
              <div className="space-y-3">
                {activos.map((a) => (
                  <div key={a.id} className="bg-gray-50 rounded-lg p-4 text-sm">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-gray-900">{a.apellido}, {a.nombre}</span>
                      <span className="text-gray-500">DNI {a.dni}</span>
                      {a.esIniciador && (
                        <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs font-medium">Iniciador</span>
                      )}
                      {a.esPromotor && (
                        <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-medium">Promotor</span>
                      )}
                    </div>
                    <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
                      <div className="flex justify-between gap-4">
                        <span className="text-gray-500">Correo declarado</span>
                        <span className="text-right break-all">{a.email || '—'}</span>
                      </div>
                      <div className="flex justify-between gap-4">
                        <span className="text-gray-500">Teléfono</span>
                        <span className="text-right">{a.telefono || '—'}</span>
                      </div>
                      <div className="flex justify-between gap-4 sm:col-span-2">
                        {/* El correo de la CUENTA es el que recibe las notificaciones del
                            sistema. Se muestra aparte del declarado porque pueden diferir. */}
                        <span className="text-gray-500">Correo de la cuenta</span>
                        <span className="text-right break-all">
                          {a.usuario
                            ? <span className="font-medium">{a.usuario.email}</span>
                            : <span className="text-gray-400">Sin cuenta en el sistema</span>}
                          {a.usuario?.estado && a.usuario.estado !== 'ACTIVO' && (
                            <span className="ml-2 px-2 py-0.5 rounded-full bg-gray-200 text-gray-700 text-xs capitalize">
                              {a.usuario.estado.replace(/_/g, ' ').toLowerCase()}
                            </span>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      )}

    </div>
  )}

  {/* TAB: Informes. En reconocimiento se arma por fase; en el resto de los
      trámites `faseRecon` es null y el panel muestra el historial completo. */}
  {activeTab === 'informes' && (
    <PanelInformes documentos={expediente.documentos} fase={faseRecon} esUsuarioInterno={esUsuarioInterno} />
  )}

  {activeTab === 'atributos' && (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {expediente.creador && (
        <div>
          <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest border-b pb-2 mb-4">
            Datos del Iniciador
          </h4>
          <div className="space-y-3 text-sm">
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="flex justify-between py-1.5 border-b border-gray-100">
                <span className="text-gray-500">Nombre</span>
                <span className="font-medium">{expediente.creador.nombre} {expediente.creador.apellido}</span>
              </div>
              {expediente.creador.dni && (
                <div className="flex justify-between py-1.5 border-b border-gray-100">
                  <span className="text-gray-500">DNI</span>
                  <span className="font-medium">{expediente.creador.dni}</span>
                </div>
              )}
              {expediente.creador.email && (
                <div className="flex justify-between py-1.5 border-b border-gray-100">
                  <span className="text-gray-500">Email</span>
                  <span className="font-medium">{expediente.creador.email}</span>
                </div>
              )}
              {expediente.creador.telefono && (
                <div className="flex justify-between py-1.5">
                  <span className="text-gray-500">Teléfono</span>
                  <span className="font-medium">{expediente.creador.telefono}</span>
                </div>
              )}
              {expediente.datosFormulario?.iniciador?.dniFrentePath && (
                <div className="flex justify-between py-1.5 border-t border-gray-100 pt-2">
                  <span className="text-gray-500">DNI frente</span>
                  <button onClick={() => verDniIniciador('frente')} className="text-sm text-blue-600 hover:text-blue-700 font-medium">
                    Ver archivo
                  </button>
                </div>
              )}
              {expediente.datosFormulario?.iniciador?.dniDorsoPath && (
                <div className="flex justify-between py-1.5">
                  <span className="text-gray-500">DNI dorso</span>
                  <button onClick={() => verDniIniciador('dorso')} className="text-sm text-blue-600 hover:text-blue-700 font-medium">
                    Ver archivo
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {expediente.tipo === 'RECONOCIMIENTO' && (
        <div>
          <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest border-b pb-2 mb-4">
            Solicitud de Atributos
          </h4>
          <div className="space-y-3 text-sm">
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="flex justify-between py-1.5 border-b border-gray-100">
                <span className="text-gray-500">Denominación</span>
                <span className="font-medium">{expediente.partido?.denominacion}</span>
              </div>
              {expediente.partido?.sigla && (
                <div className="flex justify-between py-1.5 border-b border-gray-100">
                  <span className="text-gray-500">Sigla</span>
                  <span className="font-medium">{expediente.partido.sigla}</span>
                </div>
              )}
              {expediente.partido?.tipo && (
                <div className="flex justify-between py-1.5 border-b border-gray-100">
                  <span className="text-gray-500">Tipo</span>
                  <span className="font-medium">{expediente.partido.tipo}</span>
                </div>
              )}
              {(expediente.datosFormulario?.atributos?.colores?.length ?? 0) > 0 && (
                <div className="py-1.5 border-b border-gray-100">
                  <span className="text-gray-500 block mb-2">Colores partidarios</span>
                  <div className="flex flex-wrap gap-2">
                    {expediente.datosFormulario.atributos.colores.map((c: string, i: number) => (
                      <div key={i} className="flex items-center gap-1.5 bg-white border rounded px-2 py-1">
                        <div
                          className="w-4 h-4 rounded border border-gray-300"
                          style={{ backgroundColor: c }}
                        />
                        <span className="text-xs font-mono text-gray-700">{c}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {(expediente.datosFormulario?.atributos?.simbolosPaths?.length ?? 0) > 0 && (
                <div className="py-1.5">
                  <span className="text-gray-500 block mb-2">Símbolos</span>
                  <div className="space-y-2">
                    {expediente.datosFormulario.atributos.simbolosPaths.map((_: string, i: number) => (
                      <div key={i} className="flex items-center justify-between bg-white border rounded px-3 py-2">
                        <span className="text-sm">Símbolo N° {i + 1}</span>
                        <button onClick={() => verSimbolo(i)} className="text-sm text-blue-600 hover:text-blue-700 font-medium">
                          Ver archivo
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )}

{activeTab === 'afiliados' && puedeVerAfiliados && (
    <div className="space-y-4">

      {/* Etapa de afiliados del provisorio: plazo de 4 meses y estado de los dos
          documentos del padrón (Excel que procesa el sistema + PDF firmado). */}
      {esEtapaAfiliados(faseRecon) && plazoAfiliados && (
        <div className={`border rounded-xl p-5 ${plazoAfiliados.vencido ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p className={`text-xs font-bold uppercase tracking-wide mb-1 ${plazoAfiliados.vencido ? 'text-red-700' : 'text-amber-700'}`}>
                Plazo para presentar el padrón de afiliados
              </p>
              <p className="text-sm text-gray-800">
                <span className={`text-2xl font-bold ${plazoAfiliados.vencido ? 'text-red-700' : 'text-amber-700'}`}>
                  {textoPlazoAfiliados(plazoAfiliados)}
                </span>
                <span className="ml-2">
                  — vence el {new Date(plazoAfiliados.vence).toLocaleDateString('es-AR')}
                </span>
              </p>
              <p className="text-xs text-gray-600 mt-1">
                4 meses desde la resolución del TEP que otorgó el reconocimiento provisorio
                {plazoAfiliados.acta ? ` (${plazoAfiliados.acta})` : ''}.
              </p>
            </div>
            {isInterno && estadoAfiliadosDocs && (
              <div className="text-sm">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Padrón presentado</p>
                {estadoAfiliadosDocs.completo ? (
                  <p className="text-green-700 font-medium">✓ Excel y PDF firmado verificados</p>
                ) : (
                  <ul className="space-y-1 text-gray-700">
                    {estadoAfiliadosDocs.faltanPresentar.map(d => (
                      <li key={d}>✗ <span className="font-medium">{d}</span> — sin presentar</li>
                    ))}
                    {estadoAfiliadosDocs.faltanVerificar.map(d => (
                      <li key={d}>◻ <span className="font-medium">{d}</span> — presentado, sin verificar</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {expediente.partido ? (
        <>
          {afiliadosResumen ? (
            <>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-bold text-blue-800 mb-3">Resumen de Afiliación</p>
                    <div className="flex gap-8">
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Total</p>
                        <p className="text-2xl font-bold text-gray-900">{afiliadosResumen.total.toLocaleString('es-AR')}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-green-600 uppercase tracking-wide">Válidos</p>
                        <p className="text-2xl font-bold text-green-600">{afiliadosResumen.afiliadosValidos.toLocaleString('es-AR')}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-red-500 uppercase tracking-wide">Observados</p>
                        <p className="text-2xl font-bold text-red-500">{(afiliadosResumen.total - afiliadosResumen.afiliadosValidos).toLocaleString('es-AR')}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Req. 4‰</p>
                        <p className="text-2xl font-bold text-gray-900">{afiliadosResumen.minimoRequerido.toLocaleString('es-AR')}</p>
                      </div>
                    </div>
                  </div>
                  <Link
                    to={`/afiliados?partido=${expediente.partido.id}`}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
                  >
                    Ver padrón completo →
                  </Link>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-8 text-gray-400 text-sm">Cargando resumen...</div>
          )}
        </>
      ) : (
        <p className="text-center text-sm text-gray-400 py-8">Sin partido asociado</p>
      )}
    </div>
  )}

  {activeTab === 'adherentes' && puedeVerAdherentes && (
    <div className="space-y-4">
      {expediente.partido ? (
        <>
          {adherentesResumen ? (
            <>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-bold text-blue-800 mb-3">Resumen de Adherentes</p>
                    <div className="flex gap-8">
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Total</p>
                        <p className="text-2xl font-bold text-gray-900">{adherentesResumen.total.toLocaleString('es-AR')}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-green-600 uppercase tracking-wide">Válidos</p>
                        <p className="text-2xl font-bold text-green-600">{adherentesResumen.adherentesValidos.toLocaleString('es-AR')}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-red-500 uppercase tracking-wide">Observados</p>
                        <p className="text-2xl font-bold text-red-500">{(adherentesResumen.total - adherentesResumen.adherentesValidos).toLocaleString('es-AR')}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Req. mínimo</p>
                        <p className="text-2xl font-bold text-gray-900">{adherentesResumen.minimoRequerido.toLocaleString('es-AR')}</p>
                      </div>
                    </div>
                  </div>
                  <Link
                    to={`/adherentes?partido=${expediente.partido.id}`}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
                  >
                    Ver listado completo →
                  </Link>
                </div>
              </div>
              {!adherentesResumen.alcanzaMinimo && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex gap-3">
                  <span className="text-yellow-500 text-lg">⚠</span>
                  <div>
                    <p className="text-sm font-semibold text-yellow-800">Adherentes insuficientes</p>
                    <p className="text-xs text-yellow-700">
                      El partido tiene {adherentesResumen.adherentesValidos.toLocaleString('es-AR')} adherentes válidos pero necesita {adherentesResumen.minimoRequerido.toLocaleString('es-AR')} (mayor entre 25 y 4‰ del padrón del ámbito). Faltan {(adherentesResumen.minimoRequerido - adherentesResumen.adherentesValidos).toLocaleString('es-AR')} adherentes para cumplir el requisito legal.
                    </p>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-8 text-gray-400 text-sm">Cargando resumen...</div>
          )}
        </>
      ) : (
        <p className="text-center text-sm text-gray-400 py-8">Sin partido asociado</p>
      )}
    </div>
  )}

  {/* TAB: Historial */}
  {activeTab === 'hist' && (
    <div>
      <h3 className="text-base font-medium text-gray-900 mb-4">Historial de movimientos</h3>
      {expediente.movimientos?.length ? (
        <div className="flow-root">
          <ul className="-mb-8">
            {expediente.movimientos.map((mov, idx) => (
              <li key={mov.id}>
                <div className="relative pb-8">
                  {idx !== expediente.movimientos!.length - 1 && (
                    <span className="absolute left-4 top-4 -ml-px h-full w-0.5 bg-gray-200" />
                  )}
                  <div className="relative flex space-x-3">
                    <div>
                      <span className="h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white bg-blue-500">
                        <svg className="h-4 w-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-gray-500">
                        <span className="font-medium text-gray-900">{mov.usuario?.nombre || ''} {mov.usuario?.apellido || 'Sistema'}</span>
                        {' · '}{formatDate(mov.fecha)}
                      </p>
                      <p className="text-sm font-medium mt-1">{mov.accion}</p>
                      {mov.estadoAnterior && mov.estadoNuevo && mov.estadoAnterior !== mov.estadoNuevo && (
                        <p className="text-sm text-gray-600">
                          {estadoConfig[mov.estadoAnterior]?.label || mov.estadoAnterior} → <span className="font-medium">{estadoConfig[mov.estadoNuevo]?.label || mov.estadoNuevo}</span>
                        </p>
                      )}
                      {(() => {
                        const { texto, notificaciones } = parseMovimientoComentario(mov.comentario);
                        return (
                          <>
                            {texto && <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">{texto}</p>}
                            {notificaciones.length > 0 && (
                              <div className="mt-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                                <p className="text-xs font-medium text-gray-500 mb-1">Notificado por correo a:</p>
                                <ul className="space-y-0.5">
                                  {notificaciones.map((n, i) => (
                                    <li key={i} className="flex items-center gap-1.5 text-xs text-gray-700">
                                      <span className={n.ok ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                                        {n.ok ? '✓' : '✗'}
                                      </span>
                                      <span className="break-all">{n.email}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="text-gray-500 text-center text-sm">Sin movimientos</p>
      )}
    </div>
  )}

</div>
</div>

      {/* ============ MODALES ============ */}

      <Modal isOpen={showAsignarModal} onClose={() => setShowAsignarModal(false)} title="Asignar Analista" size="md">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Analista</label>
            <select value={analistaId} onChange={(e) => setAnalistaId(e.target.value)} className="w-full border rounded-lg px-3 py-2">
              <option value="">Seleccione un analista</option>
              {analistas?.map((a: any) => (<option key={a.id} value={a.id}>{a.nombre} {a.apellido}</option>))}
            </select>
          </div>
          <div className="flex justify-end gap-3">
            <button onClick={() => setShowAsignarModal(false)} className="px-4 py-2 border rounded-lg">Cancelar</button>
            <button onClick={() => asignarMutation.mutate()} disabled={!analistaId || asignarMutation.isPending}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg disabled:opacity-50">
              {asignarMutation.isPending ? 'Asignando...' : 'Asignar'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={showObservarModal} onClose={() => setShowObservarModal(false)} title="Registrar Observaciones" size="md">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Observaciones</label>
            <RichTextEditor
              value={observaciones}
              onChange={setObservaciones}
              placeholder="Describa las observaciones..."
              minHeight={150}
            />
          </div>
          <div className="flex justify-end gap-3">
            <button onClick={() => setShowObservarModal(false)} className="px-4 py-2 border rounded-lg">Cancelar</button>
            <button onClick={() => observarMutation.mutate()} disabled={!observaciones || observarMutation.isPending}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg disabled:opacity-50">
              {observarMutation.isPending ? 'Guardando...' : 'Registrar Observación'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showIntimarModal}
        onClose={() => setShowIntimarModal(false)}
        title={expediente?.estado === 'INTIMADO' ? 'Reintimar al partido' : 'Intimar al partido'}
        size="md"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Texto de la intimación</label>
            <RichTextEditor
              value={textoIntimacion}
              onChange={setTextoIntimacion}
              placeholder="Detalle los puntos a subsanar..."
              minHeight={150}
            />
          </div>
          {/* El plazo lo fija la ley y no se elige: la intimación de admisibilidad
              corre por el Art. 10. Estaba a la vista en el cuadro inline que se
              quitó, y al quedar este modal como única vía tiene que decirlo acá — es
              el dato que el partido va a recibir. */}
          <p className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
            Plazo legal para responder: <strong>10 días (Art. 10)</strong>. Se notifica al
            apoderado por correo electrónico.
          </p>
          <div className="flex justify-end gap-3">
            <button onClick={() => setShowIntimarModal(false)} className="px-4 py-2 border rounded-lg">Cancelar</button>
            <button
              onClick={() => intimarMutation.mutate(textoIntimacion)}
              disabled={!textoIntimacion.trim() || intimarMutation.isPending}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg disabled:opacity-50"
            >
              {intimarMutation.isPending ? 'Enviando…' : 'Enviar intimación'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={showAprobarModal} onClose={() => setShowAprobarModal(false)} title="Aprobar Expediente" size="md">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Número de Resolución *</label>
            <input type="text" value={resolucionNumero} onChange={(e) => setResolucionNumero(e.target.value)}
              className="w-full border rounded-lg px-3 py-2" placeholder="Ej: RES-2024-001" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Texto de Resolución *</label>
            <RichTextEditor
              value={resolucionTexto}
              onChange={setResolucionTexto}
              placeholder="Fundamentos de la aprobación..."
              minHeight={150}
            />
          </div>
          <div className="flex justify-end gap-3">
            <button onClick={() => setShowAprobarModal(false)} className="px-4 py-2 border rounded-lg">Cancelar</button>
            <button onClick={() => aprobarMutation.mutate()} disabled={!resolucionNumero || !resolucionTexto || aprobarMutation.isPending}
              className="px-4 py-2 bg-green-600 text-white rounded-lg disabled:opacity-50">
              {aprobarMutation.isPending ? 'Aprobando...' : 'Aprobar'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={showRechazarModal} onClose={() => setShowRechazarModal(false)} title="Rechazar Expediente" size="md">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Número de Resolución *</label>
            <input type="text" value={resolucionNumero} onChange={(e) => setResolucionNumero(e.target.value)}
              className="w-full border rounded-lg px-3 py-2" placeholder="Ej: RES-2024-001" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Motivo del Rechazo *</label>
            <RichTextEditor
              value={motivoRechazo}
              onChange={setMotivoRechazo}
              placeholder="Fundamentos del rechazo..."
              minHeight={150}
            />
          </div>
          <div className="flex justify-end gap-3">
            <button onClick={() => setShowRechazarModal(false)} className="px-4 py-2 border rounded-lg">Cancelar</button>
            <button onClick={() => rechazarMutation.mutate()} disabled={!resolucionNumero || !motivoRechazo || rechazarMutation.isPending}
              className="px-4 py-2 bg-red-600 text-white rounded-lg disabled:opacity-50">
              {rechazarMutation.isPending ? 'Rechazando...' : 'Rechazar'}
            </button>
          </div>
        </div>
      </Modal>

      {/* RECONOCIMIENTO: modal de texto (informe del Secretario / motivo de rechazo del TEP) */}
      <Modal isOpen={showReconRechazarModal} onClose={() => setShowReconRechazarModal(false)} title={reconModalTitulo} size="md">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">{reconModalLabel} *</label>
            <textarea
              value={reconMotivo}
              onChange={(e) => setReconMotivo(e.target.value)}
              rows={4}
              className="w-full border rounded-lg px-3 py-2 text-sm"
              placeholder="Fundamentos del rechazo..."
            />
          </div>
          <div className="flex justify-end gap-3">
            <button onClick={() => setShowReconRechazarModal(false)} className="px-4 py-2 border rounded-lg">Cancelar</button>
            <button
              onClick={() => reconRechazarEndpoint && reconMutation.mutate({ endpoint: reconRechazarEndpoint, body: { motivo: reconMotivo.trim() } })}
              disabled={!reconMotivo.trim() || reconMutation.isPending}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {reconMutation.isPending ? 'Enviando…' : 'Confirmar'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Archivar expediente forzado (acción administrativa interna) */}
      <Modal isOpen={showArchivarForzadoModal} onClose={() => setShowArchivarForzadoModal(false)} title="Archivar expediente" size="md">
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            El expediente pasará a estado <strong>ARCHIVADO</strong> desde cualquier estado actual. Usalo para cerrar un trámite estancado o abandonado.
          </p>
          <div>
            <label className="block text-sm font-medium mb-1">Motivo *</label>
            <textarea
              value={archivarMotivo}
              onChange={(e) => setArchivarMotivo(e.target.value)}
              rows={3}
              className="w-full border rounded-lg px-3 py-2 text-sm"
              placeholder="Motivo del archivo…"
            />
          </div>
          <div className="flex justify-end gap-3">
            <button onClick={() => setShowArchivarForzadoModal(false)} className="px-4 py-2 border rounded-lg">Cancelar</button>
            <button
              onClick={() => archivarForzadoMutation.mutate()}
              disabled={!archivarMotivo.trim() || archivarForzadoMutation.isPending}
              className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 disabled:opacity-50"
            >
              {archivarForzadoMutation.isPending ? 'Archivando…' : 'Archivar'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={showSubsanarModal} onClose={() => setShowSubsanarModal(false)} title="Subsanar Observaciones" size="lg">
        <div className="space-y-4">
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <h4 className="font-medium text-orange-800 mb-2">Observaciones a subsanar:</h4>
            <ul className="space-y-2">
              {expediente?.observaciones?.filter(o => !o.subsanada).map(obs => (
                <li key={obs.id} className="text-sm text-orange-700">• {obs.texto}</li>
              ))}
            </ul>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Respuesta / Explicación</label>
            <RichTextEditor
              value={subsanarTexto}
              onChange={setSubsanarTexto}
              placeholder="Describa las correcciones realizadas..."
              minHeight={150}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Archivos de Subsanación</label>
            {subsanarArchivos.map((archivo, idx) => (
              <div key={idx} className="flex items-center gap-2 mb-2 p-2 bg-gray-50 rounded">
                <span className="flex-1 text-sm truncate">{archivo.file.name}</span>
                <select value={archivo.documentoId} onChange={(e) => { const n = [...subsanarArchivos]; n[idx].documentoId = e.target.value; setSubsanarArchivos(n); }}
                  className="text-sm border rounded px-2 py-1">
                  <option value="">¿Qué documento corrige?</option>
                  {expediente?.documentos?.map(doc => (<option key={doc.id} value={doc.id}>{doc.titulo || doc.nombreOriginal}</option>))}
                </select>
                <button onClick={() => setSubsanarArchivos(subsanarArchivos.filter((_, i) => i !== idx))} className="text-red-500 hover:text-red-700">✕</button>
              </div>
            ))}
            <label className="inline-flex items-center gap-2 px-4 py-2 border border-dashed rounded-lg cursor-pointer hover:bg-gray-50">
              <span className="text-sm text-gray-600">+ Agregar archivo</span>
              <input type="file" className="hidden" onChange={(e) => { if (e.target.files?.[0]) setSubsanarArchivos([...subsanarArchivos, { file: e.target.files[0], documentoId: '' }]); }} />
            </label>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button onClick={() => setShowSubsanarModal(false)} className="px-4 py-2 border rounded-lg">Cancelar</button>
            <button onClick={() => subsanarMutation.mutate()} disabled={!subsanarTexto || subsanarMutation.isPending}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50">
              {subsanarMutation.isPending ? 'Enviando...' : 'Enviar Subsanación'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Pasar a análisis: aprueba la etapa de admisibilidad. */}
      <Modal
        isOpen={pasarAnalisisAbierto}
        onClose={() => setPasarAnalisisAbierto(false)}
        title="Pasar a análisis"
        size="md"
      >
        {(() => {
          const cerrar = () => setPasarAnalisisAbierto(false);
          // Secciones que siguen sin verificar en ADMISIBILIDAD. Se agrupan por
          // etiqueta para que los cuatro tipos de DNI no ocupen cuatro renglones.
          const pendientes = [...new Set(
            (seccionesExp as SeccionInforme[])
              .filter((sec) => sec.admisibilidad.estado === 'PENDIENTE')
              .map((sec) => etiquetaSeccion(sec.tipo)),
          )];
          return (
            <div className="space-y-4">
              {pendientes.length > 0 ? (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <h4 className="font-medium text-amber-900 mb-1">
                    {pendientes.length === 1
                      ? 'Queda 1 sección sin verificar en admisibilidad'
                      : `Quedan ${pendientes.length} secciones sin verificar en admisibilidad`}
                  </h4>
                  <ul className="mt-2 space-y-1">
                    {pendientes.map((etiqueta) => (
                      <li key={etiqueta} className="text-sm text-amber-800 flex gap-2">
                        <span aria-hidden className="text-amber-500">•</span>
                        <span>{etiqueta}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-sm text-amber-800 mt-3">
                    Si continuás, <strong>la etapa de admisibilidad y los documentos pendientes se darán por aprobados</strong>
                  </p>
                </div>
              ) : (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-sm text-green-800">
                    No queda ninguna sección sin verificar en admisibilidad.
                  </p>
                </div>
              )}

              {expediente?.estado === 'INTIMADO' && (
                <p className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
                  Las intimaciones sin resolver <strong>quedan como están</strong>: sin resolver.
                </p>
              )}

              <div className="flex justify-end gap-3">
                <button onClick={cerrar} className="px-4 py-2 border rounded-lg">Cancelar</button>
                <button
                  onClick={() => { aprobarAdmisibilidadMutation.mutate(); cerrar(); }}
                  disabled={aprobarAdmisibilidadMutation.isPending}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg disabled:opacity-50"
                >
                  {aprobarAdmisibilidadMutation.isPending ? 'Procesando…' : 'Pasar a análisis'}
                </button>
              </div>
            </div>
          );
        })()}
      </Modal>

      <ModalInformeSecretario
        advertenciaVerificacion={advertenciaVerificacion}
        nombrePartido={expediente.partido?.denominacion}
        isOpen={showInformeSecretario}
        onClose={() => setShowInformeSecretario(false)}
        expedienteId={id!}
        fechaLimiteOposicion={expediente.datosFormulario?.fechaLimiteOposicion}
        onGuardado={() => {
          queryClient.invalidateQueries({ queryKey: ['expediente', id] });
          queryClient.invalidateQueries({ queryKey: ['gestion-expedientes'] });
        }}
        atributosDisponibles={[
          'DENOMINACION',
          ...(expediente.partido?.sigla ? ['SIGLA'] : []),
          ...((expediente.datosFormulario?.atributos?.colores?.length ?? 0) > 0 ? ['COLOR'] : []),
          ...((expediente.datosFormulario?.atributos?.simbolosPaths?.length ?? 0) > 0 ? ['SIMBOLO'] : []),
        ]}
      />

      <ModalInformeTep
        advertenciaVerificacion={advertenciaVerificacion}
        nombrePartido={expediente.partido?.denominacion}
        isOpen={showInformeTep}
        onClose={() => setShowInformeTep(false)}
        expedienteId={id!}
        atributosDisponibles={[
          'DENOMINACION',
          ...(expediente.partido?.sigla ? ['SIGLA'] : []),
          ...((expediente.datosFormulario?.atributos?.colores?.length ?? 0) > 0 ? ['COLOR'] : []),
          ...((expediente.datosFormulario?.atributos?.simbolosPaths?.length ?? 0) > 0 ? ['SIMBOLO'] : []),
        ]}
        onElevado={() => {
          queryClient.invalidateQueries({ queryKey: ['expediente', id] });
          queryClient.invalidateQueries({ queryKey: ['gestion-expedientes'] });
        }}
      />

      <ModalPublicarAtributos
        isOpen={showPublicarAtributos}
        onClose={() => setShowPublicarAtributos(false)}
        expedienteId={id!}
        textoInicial={textoPublicacionAtributos({
          numero: expediente.numero,
          partido: expediente.partido,
        })}
        onPublicado={() => {
          queryClient.invalidateQueries({ queryKey: ['expediente', id] });
          queryClient.invalidateQueries({ queryKey: ['gestion-expedientes'] });
        }}
      />

      <ModalInformeSimple
        config={informeSimpleConfig}
        expedienteId={id!}
        onClose={() => setInformeSimpleConfig(null)}
        onGuardado={() => {
          queryClient.invalidateQueries({ queryKey: ['expediente', id] });
          queryClient.invalidateQueries({ queryKey: ['gestion-expedientes'] });
          queryClient.invalidateQueries({ queryKey: ['alianza-expediente', id] });
          queryClient.invalidateQueries({ queryKey: ['bandeja-alianzas'] });
          // Los contadores son una query APARTE de la bandeja (otra key), así que
          // no se refrescaban solos: al elevar al TEP, la tarjeta "En el TEP"
          // quedaba en cero hasta recargar la página.
          queryClient.invalidateQueries({ queryKey: ['bandeja-alianzas-contadores'] });
        }}
      />

      {/* Las tres salidas del TEP en reempadronamiento. Se elige el resultado acá
          y recién después se abre el modal de la resolución con su config. */}
      <ModalOpcionesAccion
        abierto={opcionesTepReemp}
        titulo="Registrar la resolución del TEP"
        ayuda="Elegí qué resolvió el Tribunal. En el paso siguiente se cargan el N° de acta, la fecha y los archivos de la resolución."
        opciones={OPCIONES_RESOLUCION_TEP_REEMP}
        onClose={() => setOpcionesTepReemp(false)}
        onElegir={(key) => {
          setOpcionesTepReemp(false);
          handleAccion(key);
        }}
      />

      {/* El mismo chooser para la constitución de alianza. Se monta como una
          instancia SEPARADA y no como una sola con las opciones calculadas por
          tipo: los dos modales nunca están abiertos a la vez —un expediente es de
          un trámite o del otro— y separarlos deja cada lista de opciones al lado
          de su circuito, que es donde se lee cuando hay que cambiarla. */}
      <ModalOpcionesAccion
        abierto={opcionesTepAlianza}
        titulo="Registrar la resolución del TEP"
        ayuda="Elegí qué resolvió el Tribunal sobre la constitución de la alianza. En el paso siguiente se cargan el N° de acta, la fecha y los archivos de la resolución."
        opciones={OPCIONES_RESOLUCION_TEP_ALIANZA}
        onClose={() => setOpcionesTepAlianza(false)}
        onElegir={(key) => {
          setOpcionesTepAlianza(false);
          handleAccion(key);
        }}
      />

      {/* Y el del circuito de "no presentado". Tercera instancia por el mismo
          motivo que la de alianza: nunca hay dos abiertas a la vez y cada lista
          queda al lado del circuito que la usa. */}
      <ModalOpcionesAccion
        abierto={opcionesTepNoPresentado}
        titulo="Registrar la resolución del TEP"
        ayuda="Elegí qué resolvió el Tribunal sobre la solicitud dada por no presentada. En el paso siguiente se cargan el N° de acta, la fecha y los archivos de la resolución."
        opciones={OPCIONES_RESOLUCION_TEP_NO_PRESENTADO}
        onClose={() => setOpcionesTepNoPresentado(false)}
        onElegir={(key) => {
          setOpcionesTepNoPresentado(false);
          handleAccion(key);
        }}
      />

      <ModalResolucionTep
        config={resolucionTepConfig}
        onClose={() => setResolucionTepConfig(null)}
        expedienteId={id!}
        onResuelto={refrescarTrasResolucion}
      />

      <ModalAprobarReempSecretario
        abierto={aprobarReempAbierto}
        expedienteId={id!}
        onClose={() => setAprobarReempAbierto(false)}
        onAprobado={refrescarTrasResolucion}
      />
    </div>
  );
}
